import React from "react";
import { newId } from "./lib/ids";
import * as ReactDOM from "react-dom";
import { EmptyState, Icon, LogoMark, CompanyLogo, PoweredBy, poweredByShown, genToken, findCustomer, notify, confirmDialog, DemoWatermark, JobTimeline, woTimeLogs, actualHours, crewRate, repCommission, useLocalPref, CONTRACT_TERMS, CONTRACT_TERMS_VERSION, CONTRACT_TERMS_CONCRETE, CONTRACT_TERMS_CONCRETE_VERSION } from "./registry";
import { verticalOf, brandOf } from "./lib/verticals";   // the service's brand on the letterhead, the links and the customer document (plan §5.7)
import { ConcreteScopeTable } from "./registry";   // a concrete quote's scope table (concrete.tsx) in place of the fence runs
import { ConcretePlanBlock } from "./concretemap";   // …and the slabs drawn on the satellite view, as a site plan (plan §5.1)
import { CoatingPreviewCard } from "./coatpreview";   // the flake floor on the customer's own photo, saved on the quote (§13 item 14)
import { RendersCard } from "./renders";   // the fence in their photo or in 3D, saved on the quote from the Visualizer (§13 item 27)
import { JobCostActualCard } from "./jobactuals";   // a signed concrete quote: the job as it ran against the estimate (§13 item 28)
import { planOf } from "./lib/slabs";
import { storeCan } from "./lib/roles";
import { downscaleToDataUrl } from "./lib/img";
import { localYmd } from "./lib/dates";
import { cleanDesc, cleanNote, shownCoverNote, firstNameOf, isYmd, signedOf, businessTz, openExpiry, extendValidUntil, fmtDate, expiryPhrase, validDaysOf, renderCoverNote, EXTEND_DAYS, DESC_MAX, NOTE_MAX, VALID_DAYS_DEFAULT } from "./lib/quotedetails";
// computeQuoteView / customerDocOf reach the helpers through the namespace,
// guarded: the pure-function tests (web/scripts/unit.mjs) lift those two out
// of this file without its imports, and they must still run there
import * as QDX from "./lib/quotedetails";
// the concrete quote's head (rate cards → lines), reached through the namespace
// and guarded with typeof like QDX, so the pure-function tests can lift
// computeQuoteView out of this file without its imports
import * as CONCRETE from "./lib/engine.concrete";
import { runGradePct, measuredGrade, parcelHasGrade, runElevSamples, samplesGradePct } from "./lib/grade";
import { GradePointRow, GradePointAddRow } from "./estimator";
import { CrewBuildSheet } from "./crewsheet";
import { FencePlanSheet } from "./fenceplan";
/* ============================================================
   Fence Works — Quote output + Sign & Pay
   ============================================================ */
const Q_money = window.VFP.money, Q_money0 = window.VFP.money0;

/* ---- customer hub link, surfaced anywhere -------------------------------
   Ensures the customer's hub token exists, copies the private URL, and opens
   it — the same link "Sign & pay" shares, now one click from the quote, the
   work order, and the customer profile. */
function customerHubUrl(store: any, customerId: any) {
  const cust = (store.customers || []).find((x: any) => x.id === customerId);
  if (!cust) return null;
  let token = cust.hubToken;
  if (!token) { token = genToken(); store.updateCustomer(cust.id, { hubToken: token }); }
  return location.origin + "/hub/" + token;
}
/* A copied hub link only works once the save carrying the minted token LANDS
   in the cloud — the payments service resolves tokens from the customers
   table, so a token stranded in a tab whose save was dropped (stale-tab
   guard) 404s on sign/pay even though the page renders locally. Poll the
   public endpoint until it answers, nudging a flush between tries, so the
   share modal can say "verified live" instead of the contractor texting a
   link that dies at the signature. */
async function hubLinkLive(token: any) {
  const base = String((window as any).VFP_API_BASE || "").replace(/\/$/, "");
  if (!base || !token) return "local";   // no service configured — nothing to verify against
  for (let i = 0; i < 6; i++) {
    await new Promise(r => setTimeout(r, i ? 1500 : 700));   // first beat lets the save effect pick up the mint
    try { const DB: any = window.VFP_DB; if (DB && DB.flushNow) await DB.flushNow(); } catch (e) {}
    // ?probe=1: this is the owner's link check, not the customer opening it —
    // the service must not flip the quote to viewed or alert the owner
    try { const r = await fetch(base + "/public/hub/" + encodeURIComponent(token) + "?probe=1"); if (r.ok) return "live"; } catch (e) {}
  }
  return "dead";
}

/* "Regenerate link" / "Fix link": ask the service to write this customer's
   row (and token) directly, so the link resolves for the customer without
   waiting on — or being blocked by — the whole-state sync. Falls back to a
   local mint when there's no service (offline / no API base). Returns the
   token now on the customer, or throws with a message the owner can act on. */
async function pushHubLink(store: any, customerId: any, regenerate?: boolean): Promise<{ token: string; pushed: boolean; reissued: boolean }> {
  const cust = (store.customers || []).find((x: any) => x.id === customerId);
  if (!cust) throw new Error("This quote has no customer record to link to — add the customer's name or email first.");
  const DB: any = window.VFP_DB;
  if (!(DB && DB.hubLink && DB.apiBase && DB.apiBase())) {
    const token = regenerate || !cust.hubToken ? genToken() : cust.hubToken;
    if (token !== cust.hubToken) store.updateCustomer(cust.id, { hubToken: token });
    return { token, pushed: false, reissued: token !== cust.hubToken };
  }
  const d = await DB.hubLink(cust, { regenerate: !!regenerate });
  if (d.token !== cust.hubToken) store.updateCustomer(cust.id, { hubToken: d.token });
  return { token: d.token, pushed: true, reissued: !!d.reissued };
}

/* the customer profile's "New link" — reissues the hub token (the old link
   dies), copies the new one and confirms it's live on the server */
function HubRegenButton({ store, customerId }: any) {
  const [state, setState] = React.useState<any>(null);
  const cust = (store.customers || []).find((x: any) => x.id === customerId);
  if (!cust) return null;
  return (
    <button className="btn btn-ghost btn-sm" disabled={state === "working"} title="Issue a fresh hub link for this customer — the old link stops working, the new one is written to the cloud and copied"
      onClick={async () => {
        const ok = await confirmDialog({ title: "Issue a new hub link?", body: "The link you sent before will stop working. The new one is copied to your clipboard and checked live on the server.", confirmLabel: "New link" });
        if (!ok) return;
        setState("working");
        try {
          const r = await pushHubLink(store, cust.id, true);
          const url = location.origin + "/hub/" + r.token;
          if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
          const live = await hubLinkLive(r.token);
          setState(live === "dead" ? "dead" : "ok");
          notify(live === "dead" ? "New link saved, but the server can't see it yet — check the sync dot and try again." : "New hub link copied — it's live.", { kind: live === "dead" ? "error" : "success" });
        } catch (e: any) { setState("dead"); notify((e && e.message) || "Couldn't issue a new link", { kind: "error" }); }
        setTimeout(() => setState(null), 3000);
      }}>
      <Icon name={state === "ok" ? "check" : "undo"} size={13} />{state === "working" ? "Issuing…" : state === "ok" ? "New link copied ✓" : "New link"}
    </button>
  );
}

/* beforeOpen: an optional async check (the quote screen passes its margin-floor
   confirm) — copying the hub link is sending the quote, so it asks first too */
function HubLinkButton({ store, customerId, label = "Customer hub", beforeOpen }: any) {
  const [copied, setCopied] = React.useState<any>(false);
  const cust = (store.customers || []).find((x: any) => x.id === customerId);
  if (!cust) return null;
  return (
    <button className="btn btn-ghost btn-sm" title="Copy & open this customer's private hub — quote, payments, install tracker & blueprint"
      onClick={async () => {
        const url = customerHubUrl(store, customerId);
        if (!url) return;
        if (beforeOpen && !(await beforeOpen())) return;
        if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
        // the owner's own look at the page is not the customer opening it:
        // ?owner=1 keeps the service from flipping the quote to viewed or
        // emailing "they're looking at it right now" about ourselves
        window.open(url + "?owner=1", "_blank", "noopener");
        setCopied(true); setTimeout(() => setCopied(false), 2500);
      }}>
      <Icon name={copied ? "check" : "map"} size={13} />{copied ? "Link copied ✓" : label}
    </button>
  );
}

/* ---- good / better / best options ----------------------------------------
   Same fence geometry, re-priced live per product tier (q.options stores only
   {label, productId} — totals always compute fresh from the engine). The doc
   prints the tier table; the hub shows it with an "I want this one" button;
   Apply switches the whole quote to that tier in one click.
   One tier can be marked Recommended — stored as q.recommendedProductId, the
   product it names, so it follows the product through an Apply (the quoted
   tier then carries the badge). It's a label on the offer and nothing else:
   no total, document or signature reads it. */
function quoteTiers(q: any) {
  return [{ id: "cur", label: "Quoted", productId: q && q.productId, current: true }, ...(q && Array.isArray(q.options) ? q.options : [])];
}
/* the tier to badge "Recommended" — the first one carrying the recommended
   product — or null */
function recommendedTierId(q: any) {
  const rec = q && q.recommendedProductId;
  if (!rec) return null;
  const t = quoteTiers(q).find((x: any) => x && x.productId === rec);
  return t ? t.id : null;
}
function QuoteOptions({ q, store, settings, force, fullyPaid }: any) {
  const opts = q.options || [];
  const products = store.allProducts || window.VFP.PRODUCTS || [];
  if (!q.inputs) return null;
  const priceFor = (productId: any) => {
    const p = products.find((x: any) => x.id === productId);
    if (!p) return null;
    try { return { p, total: window.VFP.computeQuote({ ...q.inputs, productId }, p, settings).customerTotal }; } catch (e) { return null; }
  };
  // rec: the product to recommend now (undefined = leave it). A recommendation
  // whose product is no longer offered is dropped rather than left dangling.
  const setOpts = (next: any[], rec?: any) => {
    const r = rec !== undefined ? rec : q.recommendedProductId;
    const offered = !!r && (r === q.productId || next.some((o: any) => o.productId === r));
    const patch: any = { options: next.length ? next : undefined };
    if (rec !== undefined || (q.recommendedProductId && !offered)) patch.recommendedProductId = offered ? r : undefined;
    store.updateQuote(q.id, patch); force();
  };
  const recId = recommendedTierId(q);
  const locked = fullyPaid || q.status === "signed" || q.status === "paid";
  const recommend = (t: any) => {
    const on = recId !== t.id;
    store.updateQuote(q.id, { recommendedProductId: on ? t.productId : undefined });
    force();
  };
  const addOpt = () => {
    const used = new Set([q.productId, ...opts.map((o: any) => o.productId)]);
    const cand = products.find((p: any) => !used.has(p.id)) || products[0];
    setOpts([...opts, { id: "opt-" + Date.now(), label: ["Better", "Best", "Premium"][opts.length % 3], productId: cand.id }]);
  };
  const apply = (o: any, priced: any) => {
    // a description typed for the old product's line doesn't describe the new
    // one: that line goes back to the new product's price-book description
    const descs = q.lineDescs && typeof q.lineDescs === "object" && Object.prototype.hasOwnProperty.call(q.lineDescs, "base")
      ? (() => { const m = { ...q.lineDescs }; delete m.base; return m; })() : q.lineDescs;
    store.updateQuote(q.id, { productId: o.productId, productName: priced.p.name, total: priced.total, inputs: { ...q.inputs, productId: o.productId }, ...(descs !== q.lineDescs ? { lineDescs: descs } : {}) });
    store.addActivity(`${q.customer || "Quote"} switched to the ${o.label} option — ${priced.p.name} (${Q_money0(priced.total)})`);
    force();
  };
  if (!opts.length) {
    if (fullyPaid || q.status === "signed" || q.status === "paid") return null;
    return (
      <div className="no-print" style={{ margin: "18px 0 0" }}>
        <button className="btn btn-ghost btn-sm" onClick={addOpt} title="Offer this fence in 2–3 product tiers — same layout, priced per tier. Customers pick in their hub.">
          <Icon name="plus" size={13} />Offer good / better / best options
        </button>
      </div>
    );
  }
  const tiers = quoteTiers(q);
  return (
    <div style={{ margin: "22px 0 0" }}>
      <div className="row between" style={{ marginBottom: 10 }}>
        <div className="kicker" style={{ margin: 0 }}>Options — same fence, your choice of product</div>
        {opts.length < 2 && <button className="qedit-btn no-print" onClick={addOpt}><Icon name="plus" size={11} />Add tier</button>}
      </div>
      {/* side by side on a laptop and on paper; stacked on a phone, where three
          columns left no room for the price, the badges and the controls */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px 10px" }}>
        {tiers.map((t: any) => {
          const priced = priceFor(t.productId);
          if (!priced) return null;
          return (
            <div key={t.id} className="deposit-box" style={{ borderColor: recId === t.id ? "var(--green)" : t.current ? "var(--teal-tint-2)" : "var(--line)", position: "relative" }}>
              {recId === t.id && <span className="chip green" style={{ position: "absolute", top: -9, left: 10, fontSize: 9.5 }}><Icon name="check" size={10} />Recommended</span>}
              {t.current && <span className="chip teal no-print" style={{ position: "absolute", top: -9, right: 10, fontSize: 9.5 }}>this quote</span>}
              <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>{t.label}</div>
              <div style={{ fontWeight: 700, fontSize: 13, margin: "3px 0 1px" }}>{priced.p.name}</div>
              <div className="muted" style={{ fontSize: 11 }}>{priced.p.sub || ""}</div>
              <div className="display tnum" style={{ fontSize: 20, marginTop: 6, color: t.current ? "var(--teal)" : "var(--fg)" }}>{Q_money(priced.total)}</div>
              {!t.current && (
                <div className="row gap-6 no-print" style={{ marginTop: 8, flexWrap: "wrap" }}>
                  <select className="select" style={{ flex: 1, minWidth: 0, padding: "4px 6px", fontSize: 11.5 }} value={t.productId}
                    onChange={e => setOpts(opts.map((o: any) => o.id === t.id ? { ...o, productId: e.target.value } : o), recId === t.id ? e.target.value : undefined)}>
                    {products.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button className="btn btn-outline btn-sm" style={{ padding: "4px 9px", fontSize: 11.5 }} onClick={() => apply(t, priced)} title="Switch the whole quote to this tier">Apply</button>
                  <button className="pe-del" title="Remove tier" onClick={() => setOpts(opts.filter((o: any) => o.id !== t.id))}><Icon name="x" size={11} /></button>
                </div>
              )}
              {!locked && (
                <button className="qedit-btn no-print" style={{ marginTop: 8, color: "var(--fg-3)", background: "transparent", borderColor: "var(--line-2)" }}
                  aria-pressed={recId === t.id} onClick={() => recommend(t)}
                  title={recId === t.id ? "Take the Recommended badge off this option" : "Badge this option Recommended on the quote and the customer's hub"}>
                  {recId === t.id ? <><Icon name="x" size={10} />Remove the badge</> : <><Icon name="check" size={10} />Recommend this one</>}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="faint no-print" style={{ fontSize: 11, margin: "8px 0 0" }}>Tiers show in the customer's hub with an "I want this one" button — their pick lands in Messages, then Apply switches the quote.{recId ? " The Recommended badge shows there too." : ""}</p>
    </div>
  );
}

/* ---- balance chase: one send, logged --------------------------------------
   FW-024: this fired a `balance.reminder` event that nothing listened for, and
   dispatchEvent drops any automation whose toggle is off in any case — so a
   contractor with no email on the quote pressed "Send reminder" and literally
   nothing happened, while the activity log said it was sent. The service sends
   it now and we record what actually went out; the prefilled mailto: stays as
   the fallback for a workspace with no live service. */
async function sendBalanceReminder(store: any, q: any) {
  const bal = Math.max(0, (Number(q.total) || 0) - quotePayments(q).paid);
  if (!(bal > 0.5)) return;
  const url = q.customerId ? customerHubUrl(store, q.customerId) : null;
  const company = brandOf(store, verticalOf(q)).name || "our team";
  const DB: any = (window as any).VFP_DB;
  const stamp = (what: string) => {
    store.updateQuote(q.id, { reminders: [...(q.reminders || []), { at: Date.now(), kind: "balance" }] });
    store.addActivity(`${what} — ${q.customer || "customer"} · ${Q_money0(bal)}`);
  };
  if (DB && DB.messageSendNow && DB.apiBase && DB.apiBase() && q.customerId) {
    try {
      const r = await DB.messageSendNow({ kind: "balance_reminder", customerId: q.customerId, balance: bal, vertical: verticalOf(q) });   // the quote's service picks the words and the brand (router.js MANUAL_SENDS)
      if (r && r.deferred) notify("Queued — the reminder goes out when quiet hours lift.");
      else notify(`Balance reminder sent to ${q.customer || "the customer"}.`);
      stamp(r && r.deferred ? "Balance reminder queued" : "Balance reminder sent");
      return;
    } catch (e: any) {
      notify(`Couldn't send it for you (${String((e && e.message) || e)}) — opening your email instead.`, { kind: "error" });
    }
  }
  if (q.email) {
    const isCx = verticalOf(q) === "concrete";   // a concrete customer never reads "fence" (plan §5.6)
    const subj = encodeURIComponent(`Balance due on your ${isCx ? "concrete work" : "fence"} — ${Q_money0(bal)}`);
    const body = encodeURIComponent(`Hi ${q.customer || "there"},\n\n${isCx ? "Your concrete work is done — we hope it looks great!" : "Your new fence is complete — we hope you love it!"} A friendly reminder that the remaining balance is ${Q_money0(bal)}.\n\n${url ? "Pay securely online: " + url + "\n\n" : ""}Questions? Just reply to this email.\n\n— ${company}`);
    window.open(`mailto:${q.email}?subject=${subj}&body=${body}`, "_blank");
    stamp("Balance reminder drafted");
    return;
  }
  // no service, no email: say so rather than logging a send that never was
  notify("No email on this quote and the service isn't reachable — add an email or text them from your phone.", { kind: "error" });
}

/* ---- clean 2D site-plan drawing generated from the saved geometry ---- */
function SitePlan({ inputs }: any) {
  const parcel = inputs && inputs.parcel;
  const poly = parcel ? parcel.polygon : (inputs && inputs.mVerts) || [];
  if (!poly || poly.length < 2) return null;
  const closed = parcel ? parcel.closed !== false : false;
  const sides = parcel ? parcel.sides : poly.slice(1).map((b, i) => ({ i, a: poly[i], b, selected: true, lf: null }));
  const gates = (parcel && parcel.gates) || [];
  const k = Math.cos(poly[0][0] * Math.PI / 180);
  const pj = poly.map(([la, ln]) => [ln * k * 111320, -la * 110540]);
  const xs = pj.map(p => p[0]), ys = pj.map(p => p[1]);
  const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
  const w = (maxx - minx) || 1, h = (maxy - miny) || 1, pad = 0.14 * Math.max(w, h);
  const VW = 620, sc = VW / (w + 2 * pad), VH = Math.round((h + 2 * pad) * sc);
  const TX = (x) => (x - minx + pad) * sc, TY = (y) => (y - miny + pad) * sc;
  const sp = pj.map(p => [TX(p[0]), TY(p[1])]);
  const segs = sides.map(s => {
    const a = sp[s.i], b = sp[(s.i + 1) % sp.length]; return { a, b, sel: s.selected, lf: s.lf };
  });
  const lenFt = (s) => s.lf;
  return (
    <div className="siteplan">
      <svg viewBox={`0 0 ${VW} ${VH}`} width="100%" style={{ display: "block" }}>
        {closed && <polygon points={sp.map(p => p.join(",")).join(" ")} fill="rgba(45,212,191,0.06)" stroke="none" />}
        {segs.map((s, i) => (
          <line key={i} x1={s.a[0]} y1={s.a[1]} x2={s.b[0]} y2={s.b[1]} stroke={s.sel ? "#0fb39a" : "#c2ccd8"} strokeWidth={s.sel ? 3 : 1.5} strokeDasharray={s.sel ? "" : "4 4"} strokeLinecap="round" />
        ))}
        {segs.map((s, i) => s.sel && lenFt(s) != null && (
          <g key={"l" + i}>
            <rect x={(s.a[0] + s.b[0]) / 2 - 22} y={(s.a[1] + s.b[1]) / 2 - 9} width="44" height="18" rx="4" fill="#0b2b27" />
            <text x={(s.a[0] + s.b[0]) / 2} y={(s.a[1] + s.b[1]) / 2 + 4} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="#2dd4bf">{Math.round(lenFt(s))}′</text>
          </g>
        ))}
        {gates.map((g, i) => {
          const a = sp[g.side], b = sp[(g.side + 1) % sp.length]; if (!a || !b) return null;
          const cx = a[0] + (b[0] - a[0]) * g.t, cy = a[1] + (b[1] - a[1]) * g.t;
          const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy) || 1;
          const side = sides.find(s => s.i === g.side), lf = side && side.lf ? side.lf : 1;
          const half = ((g.width || 4) / lf) * len / 2;
          const ux = dx / len, uy = dy / len, px = -uy, py = ux, r = half * 2;
          const sgn = (g.swing & 2) ? 1 : -1, hx = cx - ux * half, hy = cy - uy * half;
          return (
            <g key={"g" + i}>
              <line x1={cx - ux * half} y1={cy - uy * half} x2={cx + ux * half} y2={cy + uy * half} stroke="#f5c451" strokeWidth="4" />
              <path d={`M ${hx} ${hy} L ${hx + ux * r} ${hy + uy * r} A ${r} ${r} 0 0 ${sgn > 0 ? 1 : 0} ${hx + px * sgn * r} ${hy + py * sgn * r}`} fill="none" stroke="#f5c451" strokeWidth="1.5" strokeDasharray="3 3" />
            </g>
          );
        })}
        {/* measured grade: a tick at each surveyed drop/rise point plus a small
            elevation strip + Δ label outside the run (display only). The strip
            follows lib/grade runElevSamples — the same corner-elevation chain +
            profile bumps the 3D viewer builds — so 2D and 3D can never drift. */}
        {sides.map((s: any, i) => {
          if (!s || !s.selected) return null;
          const prof = (Array.isArray(s.profile) && s.profile.length) ? s.profile : null;
          const samples = (parcel && parcelHasGrade(parcel)) ? runElevSamples(parcel, s.i) : null;
          if (!samples && !prof) return null;
          const a = sp[s.i], b = sp[(s.i + 1) % sp.length]; if (!a || !b) return null;
          const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy) || 1;
          const ux = dx / len, uy = dy / len;
          let px = -uy, py = ux;
          // keep the strip on the outside of the drawing
          const cx = sp.reduce((t, p) => t + p[0], 0) / sp.length, cy = sp.reduce((t, p) => t + p[1], 0) / sp.length;
          const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
          if (Math.hypot(mx + px * 12 - cx, my + py * 12 - cy) < Math.hypot(mx - px * 12 - cx, my - py * 12 - cy)) { px = -px; py = -py; }
          let pts;
          if (samples) pts = samples.map((p: any) => ({ t: +p.t || 0, e: +p.elevIn || 0 }));
          else { let cum = 0; const cums = prof.map((p: any) => { cum += (+p.deltaIn || 0); return { t: +p.t || 0, e: cum }; }); pts = [{ t: 0, e: 0 }, ...cums, { t: 1, e: cums[cums.length - 1].e }]; }
          const mn = Math.min(...pts.map(p => p.e)), mxE = Math.max(...pts.map(p => p.e)), spanE = (mxE - mn) || 1;
          if (!prof && mxE - mn < 0.25) return null;   // measured flat, no shots → nothing to flag
          const AMP = 7, OFF = 10;
          const P = (t, e) => { const d = OFF + ((mxE - e) / spanE) * AMP; return `${a[0] + ux * len * t + px * d},${a[1] + uy * len * t + py * d}`; };
          const net = pts[pts.length - 1].e - pts[0].e;
          const pct = samples ? samplesGradePct(samples, s.lf || 0) : runGradePct(prof, s.lf || 0);
          const lx = mx + px * (OFF + AMP + 9), ly = my + py * (OFF + AMP + 9);
          return (
            <g key={"gp" + i}>
              {(prof || []).map((p: any, k) => {
                const tx = a[0] + ux * len * (+p.t || 0), ty = a[1] + uy * len * (+p.t || 0);
                return <line key={k} x1={tx - px * 3.5} y1={ty - py * 3.5} x2={tx + px * 3.5} y2={ty + py * 3.5} stroke="#0fb39a" strokeWidth="2" strokeLinecap="round" />;
              })}
              <polyline points={pts.map(p => P(p.t, p.e)).join(" ")} fill="none" stroke="#7c879a" strokeWidth="1.3" strokeDasharray="2 2" />
              <text x={lx} y={ly + 3} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="#7c879a">{`Δ ${net < 0 ? "−" : "+"}${+Math.abs(net).toFixed(1)}″ over ${Math.round(s.lf || 0)}′${pct ? ` · ${+pct.toFixed(1)}%` : ""}`}</text>
            </g>
          );
        })}
        {/* absolute corner elevations (parcel.cornerElev): badge each set corner */}
        {parcel && parcel.cornerElev && Object.keys(parcel.cornerElev).map((k) => {
          const idx = +k, v = +parcel.cornerElev[k];
          if (!isFinite(v) || !sp[idx]) return null;
          const cx = sp.reduce((t, p) => t + p[0], 0) / sp.length, cy = sp.reduce((t, p) => t + p[1], 0) / sp.length;
          let dx = sp[idx][0] - cx, dy = sp[idx][1] - cy;
          const dl = Math.hypot(dx, dy) || 1; dx /= dl; dy /= dl;
          return (
            <g key={"ce" + k}>
              <circle cx={sp[idx][0]} cy={sp[idx][1]} r="3" fill={v === 0 ? "#0fb39a" : "#f5c451"} />
              <text x={sp[idx][0] + dx * 17} y={sp[idx][1] + dy * 17 + 3} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="#7c879a">{v === 0 ? "BASE 0" : (v < 0 ? "−" : "+") + (+Math.abs(v).toFixed(1)) + "″"}</text>
            </g>
          );
        })}
        {/* north arrow */}
        <g transform={`translate(${VW - 34} 30)`}>
          <path d="M0 -14 L6 8 L0 3 L-6 8 Z" fill="#7c879a" />
          <text x="0" y="22" textAnchor="middle" fontSize="10" fontFamily="monospace" fill="#7c879a">N</text>
        </g>
      </svg>
    </div>
  );
}

/* shared grade labels for the runs table (app quote + customer hub doc) */
const GRADE_LABELS: any = { level: "Level", slope_to: "Sloped — toward house", slope_away: "Sloped — away from house", step_to: "Stepped — toward house", step_away: "Stepped — away from house" };

/* computeQuoteView — everything the CUSTOMER-facing document needs, computed
   identically wherever it renders (contractor Quote page, its Print view, and
   the customer hub's Quote & Invoice). `products` overrides the catalog for
   public-route rendering (built-ins + the workspace's custom products). */
function computeQuoteView(q, settings, products?: any[]) {
  const cat = (products && products.length ? products : window.VFP.PRODUCTS);
  // a CONCRETE quote (lib/engine.concrete.ts) builds its head — product, inputs,
  // quote, lines — from the rate cards; the tail below (descriptions, per-line
  // overrides, discount, change orders, the signed revision) is shared
  const CE: any = typeof CONCRETE !== "undefined" ? CONCRETE : null;
  const concreteHead = q && q.vertical === "concrete" && CE ? CE.concreteViewHead(q, settings) : null;
  const product = concreteHead ? concreteHead.product : (cat.find(p => p.id === q.productId) || cat[0]);
  const inputs = concreteHead ? concreteHead.inputs : Object.assign({}, q.inputs, { linearFeet: (q.inputs && q.inputs.linearFeet != null) ? q.inputs.linearFeet : q.lf });
  const fenceList = concreteHead ? null : ((q.inputs && q.inputs.fences && q.inputs.fences.length > 1) ? q.inputs.fences : null);
  const fenceQuotes = fenceList
    ? fenceList.map(f => ({ name: f.name, prod: cat.find(p => p.id === f.snapshot.productId) || product, q: window.VFP.computeQuote(f.snapshot, cat.find(p => p.id === f.snapshot.productId) || product, settings), snap: f.snapshot }))
    : null;
  const quote = concreteHead ? concreteHead.quote : fenceQuotes
    ? fenceQuotes.reduce((acc, f) => {
        acc.customerTotal += f.q.customerTotal; acc.LF += f.q.LF; acc.panels += f.q.panels; acc.posts += f.q.posts;
        acc.subtotal += (f.q.subtotal != null ? f.q.subtotal : f.q.customerTotal); acc.tax += (f.q.tax || 0);
        acc.gates += f.q.gates; acc.materialTotal += f.q.materialTotal; acc.laborCost += f.q.laborCost; acc.laborHours += f.q.laborHours;
        acc.operatorCost += f.q.operatorCost; acc.passThrough += f.q.passThrough; acc.grossProfit += f.q.grossProfit;
        acc.taxRate = f.q.taxRate || acc.taxRate; acc.taxLabel = f.q.taxLabel || acc.taxLabel;
        acc.mat = acc.mat.concat(f.q.mat); acc.bomExtras = acc.bomExtras.concat(f.q.bomExtras || []); f.q.notes.forEach(n => { if (!acc.notes.includes(n)) acc.notes.push(n); });
        return acc;
      }, { customerTotal: 0, subtotal: 0, tax: 0, taxRate: 0, taxLabel: "Sales tax", LF: 0, panels: 0, posts: 0, mat: [], bomExtras: [], addons: [], notes: [], gates: 0, materialTotal: 0, laborCost: 0, laborHours: 0, operatorCost: 0, passThrough: 0, grossProfit: 0, marginPct: 0, crew: { name: "—" }, crewRate: 0, markup: 2, markupBase: "total", base: 0, slopeAmount: 0 })
    : window.VFP.computeQuote(inputs, product, settings);
  if (fenceQuotes) quote.marginPct = quote.subtotal > 0 ? (quote.grossProfit / quote.subtotal) * 100 : 0;

  const rowsOf = (snap, prod) => {
    const ss = snap && snap.parcel && snap.parcel.sides ? snap.parcel.sides.filter(s => s.selected) : null;
    return ss && ss.length
      ? ss.map(s => ({ name: `Side ${s.i + 1} (${s.name}${s.suffix ? " — " + s.suffix : ""})`, length: s.lf, grade: s.grade || "level", type: prod.name }))
      : [{ name: "Fence line", length: snap ? (snap.linearFeet || 0) : quote.LF, grade: "level", type: prod.name }];
  };
  const runGroups = concreteHead ? [] : fenceQuotes
    ? fenceQuotes.map(f => ({ name: f.name, prod: f.prod, q: f.q, rows: rowsOf(f.snap, f.prod) }))
    : [{ name: null, prod: product, q: quote, rows: rowsOf(q.inputs, product) }];

  /* ---- customer pricing breakdown — EVERY line carries a STABLE `key` ----
     `key` is the override handle: the base fence line is "base", each add-on
     reuses the engine add-on key (removal / disposal / clearing / jchannel /
     corners / gate-<tier> / permit / travel / grade), the slope surcharge is
     "slope", and each fence on a multi-fence quote is "fence-<i>". `computed`
     is the engine's original amount; `amount` is what's shown (override wins).
     Edit-estimate (quote.tsx) lets the contractor type a hard dollar value per
     line — stored on the quote as q.lineOverrides[key] — that re-foots the
     whole document below. */
  const lines = concreteHead ? concreteHead.lines : fenceQuotes
    ? fenceQuotes.map((f, fi) => ({ key: "fence-" + fi, label: `${f.name} — ${f.prod.name}`, sub: `${Math.round(f.q.LF)} ft installed · ${f.q.panels} panels · ${f.q.posts} posts`, amount: (f.q.subtotal != null ? f.q.subtotal : f.q.customerTotal) }))
    : [
        { key: "base", label: `${product.name} — installed`, sub: `${Math.round(quote.LF)} linear ft · ${quote.panels} panels · ${quote.posts} posts`, amount: quote.base },
        // gates now ride here as their own "Gate Installed" add-on line(s) (one per size tier)
        ...quote.addons.filter(a => a.customerFacing).map(a => ({ key: a.key, label: a.label, sub: a.note, amount: a.amount })),
      ];
  if (!fenceQuotes && !concreteHead) {
    if (quote.slopeAmount > 0) lines.push({ key: "slope", label: "Slope / grade surcharge", sub: quote.slopeSub || `+${Math.round((quote.slopePct || 0.15) * 100)}%`, amount: quote.slopeAmount });
  }

  /* ---- one-line descriptions (lib/quotedetails.ts) ---- what's included, the
     grade of material. q.lineDescs[key] wins, and "" hides one on this quote;
     otherwise a price-book product's own `description` fills its line (the
     base fence line, or each fence on a multi-fence quote). Words only: no
     amount reads them. A signed quote replays signedDoc's lines below, with
     the descriptions it was signed with. */
  const qdx: any = typeof QDX !== "undefined" ? QDX : null;
  const descs = (q && q.lineDescs && typeof q.lineDescs === "object") ? q.lineDescs : null;
  if (qdx) lines.forEach((l: any) => {
    const prod = l.key === "base" ? product : (fenceQuotes && /^fence-\d+$/.test(String(l.key)) ? (fenceQuotes[+String(l.key).slice(6)] || {}).prod : null);
    const def = prod && typeof prod.description === "string" ? qdx.cleanDesc(prod.description) : (concreteHead && typeof l.defaultDesc === "string" ? qdx.cleanDesc(l.defaultDesc) : "");
    const own = descs && l.key != null && Object.prototype.hasOwnProperty.call(descs, l.key) ? qdx.cleanDesc(descs[l.key]) : null;
    const d = own != null ? own : def;
    if (def) l.defaultDesc = def;
    if (d) l.desc = d;
  });

  /* ---- per-line price overrides (display/quote-model layer; the engine math
     above is untouched) ----  A finite number in q.lineOverrides keyed by a
     line's stable key REPLACES that line's computed amount. Each line keeps its
     `computed` value (for the "edited" marker + reset) and gets `overridden`.
     When ANY override applies we re-foot subtotal → tax → total from the
     EFFECTIVE line amounts, using the same formula the engine uses, so the
     printed quote and the customer hub always sum correctly. With no overrides
     the engine's own subtotal/tax/total stand untouched (byte-for-byte
     backward-compatible). Overrides move the engine total only; once a quote is
     a signed-contract ledger (signedTotal + change orders, below) that contract
     total is frozen and these overrides no longer drive customerTotal. */
  const ov = (q && q.lineOverrides && typeof q.lineOverrides === "object") ? q.lineOverrides : null;
  let anyOverride = false;
  lines.forEach((l: any) => {
    l.computed = l.amount;
    const o = ov && l.key != null ? ov[l.key] : undefined;
    if (o != null && o !== "" && isFinite(+o)) {
      l.amount = Math.max(0, Math.round(+o * 100) / 100);
      l.overridden = true;
      anyOverride = true;
    } else {
      l.overridden = false;
    }
  });
  if (anyOverride) {
    const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
    const subtotal = round2(lines.reduce((s: number, l: any) => s + (Number(l.amount) || 0), 0));
    const taxRate = Math.max(0, Number(quote.taxRate) || 0);
    const tax = round2(subtotal * taxRate);
    quote.subtotal = subtotal;
    quote.tax = tax;
    quote.customerTotal = round2(subtotal + tax);
  }

  /* ---- named discount (display/model layer, like the overrides above): a
     % of the subtotal or a flat $, applied BEFORE tax (it reduces the taxable
     base) and rendered as its own line on the doc + the hub. Once the quote
     is a signed ledger the frozen contract total wins, same as overrides. */
  const disc = (q && q.discount && (Number(q.discount.pct) > 0 || Number(q.discount.amt) > 0)) ? q.discount : null;
  if (disc) {
    const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
    const sub0 = Number(quote.subtotal) || 0;
    const dAmt = round2(Math.max(0, Math.min(sub0, Number(disc.pct) > 0 ? sub0 * Number(disc.pct) / 100 : Number(disc.amt) || 0)));
    if (dAmt > 0) {
      quote.discountAmt = dAmt;
      quote.discountLabel = ((disc.label || "").trim() || "Discount") + (Number(disc.pct) > 0 ? ` (${disc.pct}%)` : "");
      const taxRate = Math.max(0, Number(quote.taxRate) || 0);
      quote.tax = round2(Math.max(0, sub0 - dAmt) * taxRate);
      quote.customerTotal = round2(Math.max(0, sub0 - dAmt) + quote.tax);
    }
  }

  /* ---- change orders: post-signature scope changes, kept as a ledger ----
     The signed contract total stays frozen (q.signedTotal); every change after
     signing is a dated line item. "remeasure" entries document an inputs
     change the engine already re-priced; "custom" entries (equipment rental,
     unforeseen work, credits) ADD to the engine total. Revised total =
     engine total + custom change orders. */
  const changeOrders = Array.isArray(q.changeOrders) ? q.changeOrders : [];
  // approval gate: a CO only moves money once the customer approved it (hub
  // e-sign or an in-person approval the contractor recorded). Legacy COs with
  // no status predate approvals and stay grandfathered in; pending/declined
  // ones still RENDER on the ledger (badged) but never change the total.
  const coApplied = (c: any) => c && (!c.status || c.status === "approved");
  const appliedCOs = changeOrders.filter(coApplied);
  const coSum = appliedCOs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const signedTotal = q.signedTotal != null && isFinite(Number(q.signedTotal)) ? Number(q.signedTotal) : null;
  // FW-003: pure ledger once signed — customer price = signed total + Σ approved
  // COs, WHETHER OR NOT any change order exists. A catalog/settings edit after
  // signing can never move a contract price. The signed revision (q.signedDoc:
  // lines, subtotal, tax, discount as they were at signing) replaces the
  // recomputed lines so the document agrees with its frozen grand total. A
  // CO's amount can still be tweaked independently of the engine's repricing —
  // materials & schedule follow the re-measured inputs.
  //
  // quote.baseTotal is the contract BEFORE change orders, and it is the only
  // number anything may freeze as q.signedTotal: the ledger adds the approved
  // COs back on top, so freezing customerTotal (which already carries them on
  // an unsigned quote) counted each one twice. Same rule as baseTotalOf in
  // server/src/contract.js.
  quote.baseTotal = signedTotal != null ? signedTotal : quote.customerTotal;
  if (signedTotal != null) {
    const sd = q.signedDoc;
    if (sd && Array.isArray(sd.lines) && sd.lines.length) {
      lines.splice(0, lines.length, ...sd.lines.map((l: any) => ({ ...l })));
      if (sd.subtotal != null) quote.subtotal = Number(sd.subtotal) || 0;
      if (sd.tax != null) quote.tax = Number(sd.tax) || 0;
      if (sd.taxRate != null) quote.taxRate = Number(sd.taxRate) || 0;
      if (sd.taxLabel) quote.taxLabel = sd.taxLabel;
      quote.discountAmt = Number(sd.discountAmt) || 0;
      quote.discountLabel = sd.discountLabel || quote.discountLabel;
      quote.frozen = true;
    }
    quote.customerTotal = Math.max(0, signedTotal + coSum);
  } else if (coSum) quote.customerTotal = Math.max(0, quote.customerTotal + coSum);
  return { product, inputs, fenceQuotes, quote, runGroups, lines, changeOrders, signedTotal };
}

/* ---- the customer document (FW-002 / FW-003) ----
   customerDocOf: everything the customer-facing screens need, precomputed
   from the engine on the contractor's side and stamped on the quote as
   q.customerDoc. The public hub renders from THIS, so the server never has to
   send pricing inputs (markup, labor rate, product costs) to a customer.
   signedDocOf: the frozen revision captured at signing (lines/subtotal/tax/
   discount) that computeQuoteView replays for a signed quote. */
const round2 = (n: any) => Math.round((Number(n) || 0) * 100) / 100;
function signedDocOf(view: any) {
  const qq = view.quote;
  return {
    at: Date.now(),
    // each line with its one-line description when it has one (left off
    // otherwise, so a document without them is the shape it always was)
    lines: (view.lines || []).map((l: any) => ({ key: l.key, label: l.label, sub: l.sub, amount: round2(l.amount), ...(l.desc ? { desc: l.desc } : {}) })),
    subtotal: round2(qq.subtotal != null ? qq.subtotal : qq.customerTotal), tax: round2(qq.tax), taxRate: Number(qq.taxRate) || 0, taxLabel: qq.taxLabel || "Sales tax",
    discountAmt: round2(qq.discountAmt), discountLabel: qq.discountLabel || null,
    // what these lines add up to — the base, not the change orders on top
    customerTotal: round2(qq.baseTotal != null ? qq.baseTotal : qq.customerTotal),
  };
}
/* A stamped document's total BEFORE change orders — the base a signature
   freezes. A document this build stamped records it (quote.baseTotal); an
   older one's customerTotal already carries the approved change orders, which
   come back off. Mirrors baseTotalOf in server/src/contract.js. */
function docBaseTotal(doc: any, changeOrders: any) {
  const dq = doc && doc.quote;
  if (!dq) return null;
  const b = dq.baseTotal != null ? Number(dq.baseTotal) : NaN;
  if (isFinite(b) && b > 0) return round2(b);
  const t = Number(dq.customerTotal);
  if (!(isFinite(t) && t > 0)) return null;
  const co = (Array.isArray(changeOrders) ? changeOrders : []).filter((c: any) => c && (!c.status || c.status === "approved")).reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0);
  return round2(t - co);
}
function customerDocOf(q: any, settings: any, products?: any[]) {
  const view = computeQuoteView(q, settings, products);
  const cat = (products && products.length ? products : window.VFP.PRODUCTS);
  const calcOf = (qq: any, prod: any) => ({ LF: qq.LF, panels: qq.panels, posts: qq.posts, gates: qq.gates, panelWidth: qq.panelWidth != null ? qq.panelWidth : (prod && prod.panelWidth), subtotal: round2(qq.subtotal != null ? qq.subtotal : qq.customerTotal), customerTotal: round2(qq.customerTotal) });
  const bom: any[] = (view.quote.mat || []).filter((m: any) => m && m.qty > 0).map((m: any) => ({ item: m.label, qty: Math.round(m.qty) }));
  (Array.isArray(view.quote.bomExtras) ? view.quote.bomExtras : []).forEach((x: any) => { if (x && x.item && +x.qty > 0) bom.push({ item: x.item, qty: Math.round(+x.qty) }); });
  const groups = view.fenceQuotes
    ? view.fenceQuotes.map((f: any) => ({ name: f.name, productId: f.prod.id, productName: f.prod.name, inputs: f.snap, calc: calcOf(f.q, f.prod) }))
    : [{ name: null, productId: view.product.id, productName: view.product.name, inputs: null, calc: calcOf(view.quote, view.product) }];
  // good/better/best option totals, so the hub can show them without the engine
  const optionTotals: any = {};
  (Array.isArray(q.options) ? q.options : []).forEach((o: any) => {
    const p = o && cat.find((x: any) => x.id === o.productId);
    if (!p || !q.inputs) return;
    try { optionTotals[o.productId] = round2(window.VFP.computeQuote({ ...q.inputs, productId: o.productId }, p, settings).customerTotal); } catch (e) {}
  });
  const qq = view.quote;
  // the cover note filled in for this customer (frozen once signed), and the
  // valid-until date — the hub link gets these, never the template
  const qdx: any = typeof QDX !== "undefined" ? QDX : null;
  // the service's brand, from the settings mirrors (lib/verticals.ts brandOf):
  // the name in the cover note, and — on a concrete quote — a frozen brand block
  // so the hub's letterhead and the contract read the same name after any later
  // change. A fence quote's document is byte-identical to before.
  // (typeof-guarded: scripts/unit-quotes2.mjs lifts this function into a vm
  // without the module's imports, the way computeQuoteView guards CONCRETE)
  const vOf = typeof verticalOf === "function" ? verticalOf : (r: any) => (r && r.vertical === "concrete" ? "concrete" : "fence");
  const brand: any = typeof brandOf === "function" ? brandOf({ settings }, vOf(q)) : { name: (settings && settings.companyName) || "", isCompany: true };
  const note = qdx ? qdx.liveCoverNote(q, { firstName: qdx.firstNameOf(q.customer), company: brand.name || (settings && settings.companyName) || "" }) : "";
  const sd = qdx && qdx.signedOf(q) && q.signedDoc && typeof q.signedDoc === "object" ? q.signedDoc : null;
  const vu = sd && Object.prototype.hasOwnProperty.call(sd, "validUntil") ? sd.validUntil : q.validUntil;
  return {
    v: 1, at: Date.now(),
    ...(note ? { coverNote: note } : {}),
    ...(qdx && qdx.isYmd(vu) ? { validUntil: vu } : {}),
    ...(vOf(q) !== "fence" && brand.name ? { brand: { name: brand.name, phone: brand.phone, email: brand.email, website: brand.website, legalForm: brand.legalForm } } : {}),
    // a line's warranty (the concrete rate card's wording) is part of what they read
    lines: (view.lines || []).map((l: any) => ({ key: l.key, label: l.label, sub: l.sub, amount: round2(l.amount), ...(l.desc ? { desc: l.desc } : {}), ...(l.warranty ? { warranty: l.warranty } : {}) })),
    runGroups: (view.runGroups || []).map((g: any) => ({ name: g.name, prodName: g.prod && g.prod.name, LF: g.q && g.q.LF, rows: (g.rows || []).map((r: any) => ({ name: r.name, length: r.length, grade: r.grade, type: r.type })) })),
    quote: { customerTotal: round2(qq.customerTotal), baseTotal: round2(qq.baseTotal != null ? qq.baseTotal : qq.customerTotal), subtotal: round2(qq.subtotal != null ? qq.subtotal : qq.customerTotal), tax: round2(qq.tax), taxRate: Number(qq.taxRate) || 0, taxLabel: qq.taxLabel || "Sales tax",
      discountAmt: round2(qq.discountAmt), discountLabel: qq.discountLabel || null, LF: qq.LF, panels: qq.panels, posts: qq.posts, gates: qq.gates, frozen: !!qq.frozen,
      // a concrete quote's scope — what's being done, where, how much — for the hub's table (no rates, no costs)
      ...(qq.concrete ? { concrete: { unit: qq.concrete.unit, sqft: round2(qq.concrete.sqft), blocks: (qq.concrete.blocks || []).map((b: any) => ({ key: b.key, jobType: b.jobType, label: b.label, where: b.where || null, size: round2(b.size), unit: b.unit, amount: round2(b.amount), ...(b.time ? { time: { walkHours: b.time.walkHours ?? null, parkHours: b.time.parkHours ?? null, driveHours: b.time.driveHours ?? null } } : {}) })),
        // the slabs drawn on the satellite view, as geometry and a label — the customer's site plan (lib/slabs.ts planOf; typeof-guarded for the vm test)
        ...((typeof planOf === "function" ? planOf(q && q.inputs && q.inputs.concrete) : null) ? { plan: planOf(q.inputs.concrete) } : {}) } } : {}) },
    bom, groups, optionTotals,
    signedTotal: view.signedTotal,
  };
}
/* the view the customer hub renders: the stamped document when there is one,
   the engine only when pricing inputs are locally present (contractor's own
   device / sandbox), else a minimal view from the stored total. */
function hubQuoteView(q: any, state: any) {
  const settings = (state && state.settings) || {};
  const products = [...((state && state.customProducts) || []), ...window.VFP.PRODUCTS];
  const doc = q && q.customerDoc;
  if (doc && doc.quote) {
    const prodOf = (id: any, name: any) => products.find((p: any) => p.id === id) || { id, name: name || (q.productName || (q.vertical === "concrete" ? "Concrete work" : "Fence")), panelWidth: 8 };
    const product = prodOf(q.productId, q.productName);
    const groups = Array.isArray(doc.groups) ? doc.groups : [];
    const fenceQuotes = groups.length > 1 ? groups.map((g: any) => ({ name: g.name, prod: prodOf(g.productId, g.productName), q: { ...g.calc }, snap: g.inputs })) : null;
    const changeOrders = Array.isArray(q.changeOrders) ? q.changeOrders : [];
    const single = groups[0] && groups[0].calc ? groups[0].calc : {};
    const quote = { ...single, ...doc.quote, mat: [], bomExtras: (doc.bom || []).map((b: any) => ({ item: b.item, qty: b.qty })), notes: [] };
    // the ledger is live even when the doc is a moment stale: base + approved
    // COs. The base is the signed total, or — unsigned — the document's own
    // base when it records one, so a CO approved on the server after the doc
    // was stamped still counts, and counts once (contract.js contractTotalOf).
    const coSum = changeOrders.filter((c: any) => c && (!c.status || c.status === "approved")).reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0);
    if (q.signedTotal != null) quote.customerTotal = Math.max(0, Number(q.signedTotal) + coSum);
    else if (doc.quote.baseTotal != null && isFinite(Number(doc.quote.baseTotal))) quote.customerTotal = Math.max(0, Number(doc.quote.baseTotal) + coSum);
    return { product, inputs: q.inputs, fenceQuotes, quote, runGroups: (doc.runGroups || []).map((g: any) => ({ name: g.name, prod: { name: g.prodName }, q: { LF: g.LF }, rows: g.rows || [] })), lines: (doc.lines || []).map((l: any) => ({ ...l })), changeOrders, signedTotal: q.signedTotal != null ? Number(q.signedTotal) : null, doc };
  }
  if (settings.markup != null || settings.laborRate != null) return computeQuoteView(q, settings, products);
  // no document and no engine inputs (a legacy quote through the public hub): the stored total, one line
  const isConc = q.vertical === "concrete";
  const product = products.find((p: any) => p.id === q.productId) || { id: q.productId, name: q.productName || (isConc ? "Concrete work" : "Fence"), panelWidth: 8 };
  const total = round2(q.total);
  const changeOrders = Array.isArray(q.changeOrders) ? q.changeOrders : [];
  const mq = isConc ? Math.round(Number(q.measure && q.measure.qty) || 0) : 0;
  return { product, inputs: q.inputs, fenceQuotes: null,
    quote: { customerTotal: total, subtotal: total, tax: 0, taxRate: 0, taxLabel: "Sales tax", LF: Number(q.lf) || 0, panels: 0, posts: 0, gates: 0, mat: [], bomExtras: [], notes: [] },
    runGroups: isConc ? [] : [{ name: null, prod: product, q: { LF: Number(q.lf) || 0 }, rows: [{ name: "Fence line", length: Number(q.lf) || 0, grade: "level", type: product.name }] }],
    lines: [isConc ? { key: "base", label: product.name, sub: `${mq} ${(q.measure && q.measure.unit) || "sq ft"}`, amount: total } : { key: "base", label: `${product.name} — installed`, sub: `${Math.round(Number(q.lf) || 0)} linear ft`, amount: total }],
    changeOrders, signedTotal: q.signedTotal != null ? Number(q.signedTotal) : null, doc: null };
}

/* quoteBom — bill of materials (supplies + quantities, NO prices) straight
   from the engine's itemized list. Shared by the quote document, the 3D
   viewer's step counts, and the crew build sheet so they can never drift. */
function quoteBom(quote: any, product: any) {
  const rows = quote.mat
    .filter(m => m.qty > 0)
    .map(m => ({ item: m.label, qty: Math.round(m.qty) }));
  // gates and other add-on supplies come from bomExtras now (the engine emits a
  // per-size-tier gate row — "Walk Gate", "10 ft Drive Gate", …); never priced
  // into materialTotal, the cost lives in the "Gate Installed" / add-on line.
  (Array.isArray(quote.bomExtras) ? quote.bomExtras : []).forEach((x: any) => {
    if (x && x.item && +x.qty > 0) rows.push({ item: x.item, qty: Math.round(+x.qty) });
  });
  // merge dup labels
  const merged: any = {};
  rows.forEach(r => { merged[r.item] = (merged[r.item] || 0) + r.qty; });
  return Object.entries(merged).map(([item, qty]) => ({ item, qty: qty as number })).filter(r => r.qty > 0);
}

/* quotePayments — ONE payment ledger for a quote: online payments collected
   through Stripe accumulate in `depositPaid` (webhook), manual payments the
   contractor records (cash / check / outside card) live in `q.payments`.
   Every paid/due figure (quote doc, print, customer hub) derives from this. */
function quotePayments(q) {
  const list = Array.isArray(q && q.payments) ? q.payments : [];
  const manual = list.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const paid = (Number(q && q.depositPaid) || 0) + manual;
  return { list, manual, paid };
}

/* quoteMarginOf — what this quote makes, as the customer will actually be
   charged: revenue is the PRE-TAX subtotal less any discount (the engine's
   own grossProfit/marginPct are figured before a discount or a typed price
   override, so a 15% discount never showed up in them); cost is the engine's
   operator cost. The quote screen's margin line, its internal breakdown, the
   margin floor and the Jobs Sheet's quoted side all read this one. */
function quoteMarginOf(quote: any) {
  const revenue = Math.max(0, (Number(quote && quote.subtotal) || 0) - (Number(quote && quote.discountAmt) || 0));
  const cost = Number(quote && quote.operatorCost) || 0;
  const profit = revenue - cost;
  return { revenue, cost, profit, pct: revenue > 0 ? (profit / revenue) * 100 : null };
}
/* the margin floor at send (Settings → Markup rules & labor): 25% unless set,
   0 = off. A warning before a quote goes out — it never blocks one. */
const MARGIN_FLOOR_DEFAULT = 25;
function marginFloorOf(settings: any) {
  const v = settings && settings.marginFloorPct;
  const n = v == null || v === "" ? MARGIN_FLOOR_DEFAULT : Number(v);
  return isFinite(n) ? Math.max(0, Math.min(90, n)) : MARGIN_FLOOR_DEFAULT;
}
/* { margin, floor } when this margin is under the floor, else null (no
   floor, or nothing priced yet to judge) */
function marginBelowFloor(m: any, settings: any) {
  const floor = marginFloorOf(settings);
  if (!(floor > 0) || !m || m.pct == null || !(m.revenue > 0)) return null;
  return m.pct < floor ? { margin: m.pct, floor } : null;
}

/* Settings → Markup rules & labor: the floor itself. Saves as it's typed, like
   the other quoting controls on that tab; an emptied box means off. */
function MarginFloorSetting({ store, onSaved }: any) {
  const saved = marginFloorOf(store.settings);
  const [txt, setTxt] = React.useState<any>(String(saved));
  // follow a change made elsewhere (another tab, a sync) without fighting the typing
  React.useEffect(() => { if ((Number(txt) || 0) !== saved) setTxt(String(saved)); }, [saved]);
  function change(v: any) {
    setTxt(v);
    const n = String(v).trim() === "" ? 0 : Number(v);
    if (!isFinite(n)) return;
    store.setSettings({ marginFloorPct: Math.max(0, Math.min(90, Math.round(n))) });
    if (onSaved) onSaved();
  }
  return (
    <div>
      <label className="field-label" htmlFor="set-margin-floor">Margin floor at send</label>
      <div className="row gap-10" style={{ alignItems: "center", flexWrap: "wrap" }}>
        <div className="pe-money-input" style={{ maxWidth: 110 }}>
          <input id="set-margin-floor" className="tnum" type="number" inputMode="numeric" min="0" max="90" step="1" value={txt} onChange={e => change(e.target.value)} />
          <span className="muted" style={{ fontWeight: 400, fontSize: 12.5 }}>%</span>
        </div>
        <span className="muted" style={{ fontSize: 12.5 }}>gross margin · 0 = off</span>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: "8px 0 0" }}>
        {saved > 0
          ? <>Sending or sharing a quote under <b style={{ color: "var(--fg-2)" }}>{saved}%</b> asks you first. It never stops a send, and the customer never sees cost or margin. The quote screen shows each quote's margin in an internal line you can hide.</>
          : <>Off — quotes go out without a margin check. The quote screen still shows each quote's margin in an internal line you can hide.</>}
      </p>
    </div>
  );
}

/* Settings → Markup rules & labor → Quoting: what a NEW quote starts with —
   how long it's good for, and the personal note at the top. Saves as it's
   typed, like the margin floor beside it. Quotes already made keep what they
   say (lib/quotedetails.ts withQuoteDefaults stamps a quote once, when it's
   created). */
function QuoteDefaultsSetting({ store, onSaved }: any) {
  const s = (store && store.settings) || {};
  const days = validDaysOf(s);
  const [txt, setTxt] = React.useState<any>(String(days));
  // follow a change made elsewhere (another tab, a sync) without fighting the typing
  React.useEffect(() => { if (String(txt).trim() !== "" && Number(txt) !== days) setTxt(String(days)); }, [days]);
  const [note, setNote] = React.useState<any>(typeof s.quoteCoverNote === "string" ? s.quoteCoverNote : "");
  function changeDays(v: any) {
    setTxt(v);
    const t = String(v).trim();
    if (t === "" || !isFinite(Number(t))) return;
    store.setSettings({ quoteValidDays: Math.max(0, Math.min(365, Math.round(Number(t)))) });
    if (onSaved) onSaved();
  }
  function changeNote(v: any) {
    setNote(v);
    store.setSettings({ quoteCoverNote: String(v).slice(0, NOTE_MAX) });
    if (onSaved) onSaved();
  }
  const co = (store.company && store.company.name) || s.companyName || "";
  return (
    <div data-quote-defaults>
      <label className="field-label" htmlFor="set-quote-valid">Quotes are good for</label>
      <div className="row gap-10" style={{ alignItems: "center", flexWrap: "wrap" }}>
        <div className="pe-money-input" style={{ maxWidth: 120 }}>
          <input id="set-quote-valid" className="tnum" type="number" inputMode="numeric" min="0" max="365" step="1" value={txt} onChange={e => changeDays(e.target.value)} />
          <span className="muted" style={{ fontWeight: 400, fontSize: 12.5 }}>days</span>
        </div>
        <span className="muted" style={{ fontSize: 12.5 }}>0 = no expiry date · {VALID_DAYS_DEFAULT} unless you change it</span>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: "8px 0 0" }}>
        {days > 0
          ? <>New quotes say <b style={{ color: "var(--fg-2)" }}>Valid until</b> {days} day{days === 1 ? "" : "s"} after they're made. Change or clear it on any quote. Once it passes, the customer can't sign until you extend it — one tap on the quote.</>
          : <>Off — new quotes carry no expiry date. You can still give any quote one.</>}
      </p>
      <label className="field-label" htmlFor="set-quote-note" style={{ marginTop: 16 }}>Default cover note <span className="faint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— optional</span></label>
      <textarea id="set-quote-note" className="textarea" rows={3} maxLength={NOTE_MAX} value={note}
        placeholder={"Hi {first_name}, thanks for having {company} out to look at your yard. Here's the fence we talked about — any questions, just reply."}
        onChange={e => changeNote(e.target.value)} />
      <p className="muted" style={{ fontSize: 12.5, margin: "6px 0 0" }}>
        <code>{"{first_name}"}</code> and <code>{"{company}"}</code> fill in. It goes at the top of each new quote, the customer's link and the quote email, and you can edit it on any quote. Plain text — line breaks are kept.
      </p>
      {String(note).trim() && (
        <div className="quote-cover-preview" style={{ marginTop: 10 }}>
          <span className="kicker" style={{ fontSize: 9.5 }}>A customer named Dana reads</span>
          <p className="quote-cover-text">{renderCoverNote(note, { firstName: "Dana", company: co })}</p>
        </div>
      )}
    </div>
  );
}

/* the canonical lost-reason set — shared by the mark-lost modal, the Estimates
   list chip, and the Reports "Why we lose" panel. Ids are stored on the quote. */
const LOST_REASONS = [
  { id: "price",       label: "Price too high" },
  { id: "timing",      label: "Bad timing / postponed" },
  { id: "competitor",  label: "Went with a competitor" },
  { id: "no_response", label: "Went dark / no response" },
  { id: "diy",         label: "Doing it themselves" },
  { id: "other",       label: "Other" },
];
const lostReasonLabel = (id: any) => (LOST_REASONS.find(x => x.id === id) || {} as any).label || (id ? String(id) : "No reason logged");

/* quoteDeposit — the deposit DUE on a quote, resolved from (in priority order):
     1. a per-quote override the contractor set on THIS quote — `q.depositAmt`
        (a flat $) or `q.depositPct` (a percent of the total);
     2. the workspace deposit policy (`settings.deposit`, the same one the
        customer hub honours): full_only → 100%, custom → customPct, else the
        first offered option;
     3. a 50% default.
   Backward-compatible: a quote with no override and no policy = 50% deposit,
   exactly as the hub and the in-person pad defaulted before. Returns the
   resolved percent, the dollar amount (clamped to the total), the remaining
   balance, and where the figure came from (for the UI copy). */
function quoteDeposit(q: any, settings: any, total: number) {
  const T = Math.max(0, Number(total) || 0);
  const clamp = (a: number) => Math.max(0, Math.min(T, Math.round((Number(a) || 0) * 100) / 100));
  // 1) per-quote override
  if (q && q.depositAmt != null && q.depositAmt !== "" && isFinite(+q.depositAmt)) {
    const amount = clamp(+q.depositAmt);
    return { pct: T > 0 ? (amount / T) * 100 : 0, amount, balance: Math.max(0, T - amount), source: "quote", mode: "amount" };
  }
  if (q && q.depositPct != null && q.depositPct !== "" && isFinite(+q.depositPct)) {
    const pct = Math.max(0, Math.min(100, +q.depositPct));
    const amount = clamp(T * (pct / 100));
    return { pct, amount, balance: Math.max(0, T - amount), source: "quote", mode: "pct" };
  }
  // 1b) a deposit already collected IS the deposit — the customer picked 50%
  //     on the hub, so the terms box says 50%, not the policy's first option
  const sp = Array.isArray(q && q.stripePayments) ? q.stripePayments : [];
  const paidDep = sp.find((p: any) => p && p.kind === "deposit" && Number(p.amount) > 0) || (sp.length === 0 && Number(q && q.depositPaid) > 0 && Number(q.depositPaid) < T - 0.005 ? { amount: Number(q.depositPaid) } : null);
  if (paidDep && T > 0) {
    const amount = clamp(+paidDep.amount);
    return { pct: Math.round((amount / T) * 1000) / 10, amount, balance: Math.max(0, T - amount), source: "paid", mode: "pct" };
  }
  // 2) workspace deposit policy (mirrors hub.tsx / integrations.tsx)
  const dep = (settings && settings.deposit) || {};
  let pct = 50;
  if (dep.mode === "full_only") pct = 100;
  else if (dep.mode === "custom") pct = Math.max(1, Math.min(99, Number(dep.customPct) || 50));
  else {
    const opts = (Array.isArray(dep.options) ? dep.options.map(Number) : []).filter(n => n >= 1 && n <= 99).sort((a, b) => a - b);
    if (opts.length) pct = opts[0];
  }
  const amount = clamp(T * (pct / 100));
  return { pct, amount, balance: Math.max(0, T - amount), source: dep.mode ? "policy" : "default", mode: "pct" };
}

/* quoteSchedule — OPTIONAL progress / milestone billing. `q.paymentSchedule`
   is an ordered list of stages, each a percent (`pct`) OR a flat dollar
   (`amt`) of the total: e.g. Deposit 50% → On start 25% → On completion 25%.
   Stages are resolved to dollar amounts (the LAST stage absorbs any rounding
   remainder so the schedule always foots to the total), then the money already
   collected (`paid`) is allocated across them in order to mark each stage
   paid / partially paid / due. Returns null when no schedule is set — a quote
   without one bills exactly as before (single deposit + balance). */
function quoteSchedule(q: any, total: number, paid: number) {
  const raw = Array.isArray(q && q.paymentSchedule) ? q.paymentSchedule : null;
  if (!raw || !raw.length) return null;
  const T = Math.max(0, Number(total) || 0);
  const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
  // resolve each stage to a dollar amount
  const amts = raw.map((s: any) => s && s.amt != null && s.amt !== "" && isFinite(+s.amt)
    ? Math.max(0, round2(+s.amt))
    : Math.max(0, round2(T * ((Number(s && s.pct) || 0) / 100))));
  // last stage soaks up the rounding gap so Σstages === total
  const sumExceptLast = amts.slice(0, -1).reduce((a, b) => a + b, 0);
  if (amts.length) amts[amts.length - 1] = Math.max(0, round2(T - sumExceptLast));
  // greedily allocate collected money across the stages, in order
  let left = Math.max(0, Number(paid) || 0);
  const stages = raw.map((s: any, i: number) => {
    const amount = amts[i];
    const applied = Math.min(amount, left);
    left = round2(left - applied);
    return {
      id: s.id || ("stage" + i),
      label: s.label || (i === 0 ? "Deposit" : "Stage " + (i + 1)),
      pct: (s.amt != null && s.amt !== "" && isFinite(+s.amt)) ? (T > 0 ? round2((amount / T) * 100) : 0) : (Number(s.pct) || 0),
      amount,
      paid: round2(applied),
      due: round2(Math.max(0, amount - applied)),
      settled: applied >= amount - 0.005 && amount > 0,
    };
  });
  const scheduled = round2(amts.reduce((a, b) => a + b, 0));
  return { stages, scheduled, overflow: round2(left) };
}

/* a fresh default schedule the UI offers when a contractor turns milestone
   billing on — deposit (from policy) up front, the rest split start/finish. */
function defaultSchedule(depositPct: number) {
  const d = Math.max(0, Math.min(100, Math.round(depositPct || 50)));
  const rest = Math.max(0, 100 - d);
  const onStart = Math.round(rest / 2);
  const onDone = Math.max(0, rest - onStart);
  return [
    { id: "dep", label: "Deposit", pct: d },
    { id: "start", label: "On start", pct: onStart },
    { id: "done", label: "On completion", pct: onDone },
  ];
}

/* financing is LIVE only when the contractor's Wisetack account is actually
   connected — the display toggle alone never puts a dead "finance monthly"
   button in front of a customer. */
function financingLive(settings) {
  const fin = (settings && settings.financing) || (window.VFP.DEFAULT_SETTINGS || {}).financing || {};
  const wt = settings && settings.integrations && settings.integrations.wisetack;
  return !!(fin.enabled && wt && wt.connected);
}

/* ---- Fence Construction 3D (quote card + customer hub) ----
   three.js stays out of the main bundle: fencebuild is ONLY ever imported
   lazily, and only mounted after the user asks for it (teaser button) so
   nobody pays the WebGL cost on page load. */
const FenceBuild3DLazy: any = React.lazy(() => import("./fencebuild").then((m: any) => ({ default: m.FenceBuild3D })));

/* product/quote → FenceBuild3D spec. Custom products carry the editor blob
   (style/color/height); built-ins encode it in name/sub — fall back to
   keyword sniffing so even a bare productName renders something sensible. */
function fenceSpecFrom(product: any, name?: any) {
  const p = product || {};
  const ed = p._editor || {};
  const sub = String(p.sub || "");
  const subParts = sub.includes(" · ") ? sub.split(" · ") : ["", ""];
  const label = String(p.name || name || "");
  const txt = (label + " " + sub).toLowerCase();
  const category = p.category || (txt.includes("vinyl") ? "Vinyl" : txt.includes("chain") ? "Chain Link"
    : (txt.includes("alum") || txt.includes("iron") || txt.includes("ornament")) ? "Iron/Aluminum"
    : (txt.includes("wood") || txt.includes("cedar") || txt.includes("pine")) ? "Wood" : "Vinyl");
  const hm = label.match(/(\d+(?:\.\d+)?)\s*(?:ft|foot|feet|['′])/i) || label.match(/(\d+(?:\.\d+)?)\s*[×x]/);
  return {
    category,
    style: ed.style || subParts[1] || "",
    color: ed.color || subParts[0] || "",
    heightFt: +ed.height || (hm ? +hm[1] : 6) || 6,
    panelFt: +p.panelWidth || +ed.panelLength || 8,
    // how the posts go in and how deep — set per product in the Product Editor;
    // the 3D build and its callouts read these instead of guessing a depth
    postSet: ed.postSet || undefined,
    embedIn: ed.embedIn != null && ed.embedIn !== "" ? +ed.embedIn : undefined,
    pipeFt: ed.pipeFt != null && ed.pipeFt !== "" ? +ed.pipeFt : undefined,
  };
}

/* teaser-then-mount: the canvas (and the three.js chunk) only loads on tap.
   layout (inputs.parcel) + quote let the viewer build the customer's REAL
   fence line and default the slope simulation to their detected grade. */
function Fence3DLauncher({ spec, sub, layout, quote, bom, counts }: any) {
  const [open, setOpen] = React.useState<any>(false);
  if (!open) {
    return (
      <button className="fb3d-teaser" onClick={() => setOpen(true)}>
        <span className="ic">▶</span>
        <span style={{ minWidth: 0 }}>
          <b style={{ display: "block", fontSize: 14.5 }}>See it built in 3D</b>
          <span className="muted" style={{ fontSize: 12.5 }}>{sub || "Watch it go together step by step — posts, rails, boards & caps."}</span>
        </span>
      </button>
    );
  }
  return (
    <React.Suspense fallback={<div className="muted" style={{ textAlign: "center", padding: "48px 0", fontSize: 13 }}>Loading 3D…</div>}>
      <FenceBuild3DLazy spec={spec} layout={layout} quote={quote} bom={bom} counts={counts} compact />
    </React.Suspense>
  );
}

const PAY_LABELS: any = { cash: "Cash", check: "Check", card: "Card (manual)", other: "Other payment" };
function payMethodLabel(p: any) {
  if (!p) return "Payment";
  if (p.method === "stripe") return "Paid online — Stripe" + (p.kind && p.kind !== "deposit" ? " (" + p.kind + ")" : "");
  if (p.method === "check") return "Check" + (p.checkNo ? " #" + p.checkNo : "");
  return (PAY_LABELS[p.method] || "Payment") + (p.ref ? " · " + p.ref : "");
}

/* Payment detail — everything about one ledger entry: method, refs, note,
   who recorded it & when, the photo, and (for manual entries) Remove. */
function PaymentDetailModal({ p, onClose, onDelete, onPhoto }: any) {
  const isStripe = p.method === "stripe";
  const Row = ({ label, children }: any) => (
    <div className="row between" style={{ padding: "7px 0", borderBottom: "1px solid var(--line)", gap: 14 }}>
      <span className="muted" style={{ fontSize: 12.5, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right", minWidth: 0 }}>{children}</span>
    </div>
  );
  return ReactDOM.createPortal(
    <div className="hub-preview-overlay" data-dialog onClick={onClose}>
      <div className="hub-quote-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="hub-preview-bar">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="dollar" size={14} className="muted" />Payment details</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <div className="hub-quote-body" style={{ display: "grid", gap: 12 }}>
          <div style={{ textAlign: "center", padding: "6px 0 2px" }}>
            <div className="display tnum" style={{ fontSize: 32, color: "var(--green)" }}>{Q_money(p.amount)}</div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{payMethodLabel(p)}</div>
          </div>
          <div>
            {isStripe
              ? <>
                  <Row label="Source">Online — Stripe{p.source ? " (" + p.source + ")" : ""}</Row>
                  {p.kind ? <Row label="For">{p.kind === "balance" ? "Balance" : p.kind === "full" ? "Paid in full" : "Deposit"}</Row> : null}
                  {p.at ? <Row label="Paid on">{new Date(p.at).toLocaleString()}</Row> : <Row label="Paid">Recorded (date not captured)</Row>}
                  <Row label="Receipt">In your Stripe dashboard{p.sessionId ? " · " + String(p.sessionId).slice(0, 14) + "…" : ""}</Row>
                </>
              : <>
                  {p.checkNo && <Row label="Check #">{p.checkNo}</Row>}
                  {p.ref && <Row label="Reference">{p.ref}</Row>}
                  {p.at ? <Row label="Date received">{new Date(p.at).toLocaleDateString()}</Row> : null}
                  {p.by && <Row label="Recorded by">{p.by}</Row>}
                  {p.createdAt ? <Row label="Recorded">{new Date(p.createdAt).toLocaleString()}</Row> : null}
                  {p.note && <Row label="Note">{p.note}</Row>}
                </>}
          </div>
          {p.photo && (
            <img src={p.photo} alt="" onClick={() => onPhoto && onPhoto(p.photo)}
              style={{ width: "100%", maxHeight: 260, objectFit: "contain", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, cursor: "zoom-in" }} />
          )}
          {!isStripe && p.id && (
            <button className="btn btn-sm btn-block" style={{ background: "var(--red-tint)", color: "var(--red)", border: "1px solid var(--red-tint)" }} onClick={() => onDelete && onDelete(p)}>
              <Icon name="trash" size={13} />Remove this payment
            </button>
          )}
        </div>
      </div>
    </div>, document.body);
}

/* Record a payment received outside the platform — cash, paper check, a card
   run on the contractor's own terminal. App modal (never a browser popup). */
function RecordPaymentModal({ due, total, onSave, onClose, seedAmount, seedLabel }: any) {
  const [amount, setAmount] = React.useState<any>(() => +((seedAmount != null && isFinite(+seedAmount)) ? +seedAmount : (due > 0 ? due : total)).toFixed(2));
  const [method, setMethod] = React.useState<any>("cash");
  const [checkNo, setCheckNo] = React.useState<any>("");
  const [ref, setRef] = React.useState<any>("");
  const [date, setDate] = React.useState<any>(() => localYmd());
  const [note, setNote] = React.useState<any>("");
  const [photo, setPhoto] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const fileRef = React.useRef<any>(null);
  const amt = Number(amount) || 0;
  return ReactDOM.createPortal(
    <div className="hub-preview-overlay" data-dialog onClick={onClose}>
      <div className="hub-quote-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="hub-preview-bar">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="dollar" size={14} className="muted" />Record a payment</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <div className="hub-quote-body" style={{ display: "grid", gap: 12 }}>
          <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>For money collected outside Fence Works — cash, a paper check, or a card you charged on your own machine. It counts toward this invoice immediately.</p>
          {seedLabel && <div className="pe-callout info" style={{ margin: 0 }}><p className="muted" style={{ margin: 0, fontSize: 12.5 }}>Recording the <b style={{ color: "var(--fg)" }}>{seedLabel}</b> stage — amount pre-filled. Adjust it for a partial payment.</p></div>}
          <div className="row gap-10" style={{ flexWrap: "wrap" }}>
            <div style={{ width: 150 }}><label className="field-label">Amount</label><input className="input tnum" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} /></div>
            <div style={{ flex: 1, minWidth: 150 }}><label className="field-label">Date received</label><input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
          </div>
          {due > 0.005 && Math.abs(amt - due) > 0.005 && (
            <button className="filter-chip" style={{ justifySelf: "start" }} onClick={() => setAmount(+due.toFixed(2))}>Use remaining balance — {Q_money(due)}</button>
          )}
          <div>
            <label className="field-label">Paid by</label>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              {[["cash", "Cash"], ["check", "Check"], ["card", "Card (outside)"], ["other", "Other"]].map(([v, lb]) => (
                <button key={v} className={"filter-chip" + (method === v ? " on" : "")} onClick={() => setMethod(v)}>{lb}</button>
              ))}
            </div>
          </div>
          {method === "check" && (
            <div style={{ width: 180 }}><label className="field-label">Check #</label><input className="input" value={checkNo} onChange={e => setCheckNo(e.target.value)} placeholder="1042" /></div>
          )}
          {(method === "card" || method === "other") && (
            <div><label className="field-label">Reference <span className="faint">optional — last 4, receipt #…</span></label><input className="input" value={ref} onChange={e => setRef(e.target.value)} /></div>
          )}
          <div><label className="field-label">Note <span className="faint">optional</span></label><input className="input" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. picked up on site" /></div>
          <div>
            <label className="field-label">Photo <span className="faint">optional — the check, a receipt…</span></label>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={async e => {
              const f = e.target.files && e.target.files[0];
              if (f) { setBusy(true); try { setPhoto(await downscaleToDataUrl(f)); } catch (err) {} finally { setBusy(false); } }
              if (fileRef.current) fileRef.current.value = "";
            }} />
            {photo
              ? <div className="row gap-10" style={{ alignItems: "center" }}>
                  <img src={photo} alt="" style={{ width: 74, height: 74, objectFit: "cover", borderRadius: 10, border: "1px solid var(--line)" }} />
                  <button className="btn btn-ghost btn-sm" onClick={() => setPhoto("")}><Icon name="x" size={13} />Remove</button>
                </div>
              : <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}><Icon name="image" size={14} />{busy ? "Processing…" : "Attach photo"}</button>}
          </div>
          <button className="btn btn-primary btn-block" disabled={amt <= 0} onClick={() => onSave({
            id: "pay" + Date.now().toString(36),
            amount: +amt.toFixed(2), method, checkNo: checkNo.trim(), ref: ref.trim(), note: note.trim(),
            at: (date ? new Date(date + "T12:00:00") : new Date()).getTime(), photo,
          })}>
            <Icon name="check" size={15} />Record {Q_money(amt)} payment
          </button>
        </div>
      </div>
    </div>, document.body);
}

function SignaturePad({ onSign, signed }: any) {
  const ref = React.useRef(null);
  const drawing = React.useRef(false);
  const dirty = React.useRef(false);
  React.useEffect(() => {
    const c = ref.current; if (!c) return; const ctx = c.getContext("2d");
    // (Re)size the backing bitmap to the element's laid-out size. Reading the
    // size once on mount misaligned the drawn line whenever the pad was revealed
    // after a reflow or the phone was rotated (offsetWidth was stale/zero), so
    // re-run this whenever the element actually changes size.
    let w = 0, h = 0;
    const setup = () => {
      const nw = c.offsetWidth, nh = c.offsetHeight;
      if (!nw || !nh || (nw === w && nh === h)) return;   // not laid out yet / no change
      w = nw; h = nh;
      const dpr = window.devicePixelRatio || 1;
      c.width = nw * dpr; c.height = nh * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.scale(dpr, dpr);
      ctx.strokeStyle = "#eef2f8"; ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.lineJoin = "round";
      dirty.current = false;
    };
    setup();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => { if (!drawing.current) setup(); }) : null;
    if (ro) ro.observe(c);
    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return [t.clientX - r.left, t.clientY - r.top]; };
    const down = (e) => { e.preventDefault(); drawing.current = true; const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); dirty.current = true; };
    const up = () => { drawing.current = false; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    c.addEventListener("touchstart", down, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", up);
    return () => { window.removeEventListener("mouseup", up); window.removeEventListener("touchend", up); if (ro) ro.disconnect(); };
  }, []);
  return (
    <div>
      <div className="sig-wrap">
        <canvas ref={ref} className="sig-canvas" style={{ pointerEvents: signed ? "none" : "auto" }} />
        {signed && <div className="sig-done"><Icon name="check" size={16} />Signed</div>}
        <div className="sig-line">Sign above</div>
      </div>
      {!signed &&
        <div className="row between" style={{ marginTop: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; }}>Clear</button>
          <button className="btn btn-outline btn-sm" onClick={() => { if (dirty.current) onSign(ref.current ? (ref.current as any).toDataURL("image/png") : null); }}>Accept &amp; sign</button>
        </div>}
    </div>
  );
}

function Quote({ store, go, goBack, params, settings }: any) {
  // sandbox: customer-facing docs carry a SAMPLE watermark until branded
  const demoSample = !!(window.VFP_DEMO && window.VFP_DEMO.active && !window.VFP_DEMO.branded(store));
  const q = store.quotes.find(x => x.id === (params && params.quoteId)) || store.quotes[store.quotes.length - 1];
  const [showInternal, setShowInternal] = React.useState<any>(true);   // open by default — the operator's numbers are the point of this page
  // "Hide cost & margin": every cost figure off this screen so it can be
  // turned toward the customer. Remembered on this device until shown again.
  const [hideCostPref, setHideCostPref] = useLocalPref("fw_quote_hide_cost", "0");
  const costHidden = hideCostPref === "1";
  const [share, setShare] = React.useState<any>(null);          // sign & pay link modal
  const [share3d, setShare3d] = React.useState<any>(null);      // public 3D viewer link modal
  const [sendState, setSendState] = React.useState<any>(null);  // null | sending | sent | error msg
  const [deposit, setDeposit] = React.useState<any>(null);        // in-person pad: deposit FRACTION (0..1); null = use resolved
  const [payMode, setPayMode] = React.useState<any>("deposit");   // 'deposit' | 'finance'
  const [editCust, setEditCust] = React.useState<any>(false);
  const [editDep, setEditDep] = React.useState<any>(false);       // deposit / payment-terms editor
  const [recordPay, setRecordPay] = React.useState<any>(null);    // record-a-payment modal: null | true | { stageId, label, amount }
  const [inPerson, setInPerson] = React.useState<any>(false);     // reveal the in-person sign pad
  const [ipName, setIpName] = React.useState<any>("");            // in-person pad: the name THEY type
  const [ipAgree, setIpAgree] = React.useState<any>(false);       // …their consent box
  const [ipBusy, setIpBusy] = React.useState<any>(false);         // …signature on its way to the server
  const [ipErr, setIpErr] = React.useState<any>("");
  const [coSign, setCoSign] = React.useState<any>(null);          // in-person change-order approval: { coId, name, agree, busy?, err? }
  // a name typed and a box ticked for one customer never carry over to the next
  // quote opened in this screen — consent is per signer, per document
  React.useEffect(() => { setIpName(""); setIpAgree(false); setIpErr(""); setCoSign(null); }, [q && q.id]);
  const [qboState, setQboState] = React.useState<any>("");        // QuickBooks invoice send state
  const [qboPayFail, setQboPayFail] = React.useState<any>(false);  // last payment sync to QuickBooks failed
  const [photoView, setPhotoView] = React.useState<any>("");      // payment photo lightbox
  const [payView, setPayView] = React.useState<any>(null);        // payment detail modal
  const [crewSheet, setCrewSheet] = React.useState<any>(false);   // printable crew build sheet
  const [planSheet, setPlanSheet] = React.useState<any>(false);   // printable blueprint fence plan
  // opened with ?openSheet=crew|packet (the walkthrough, a work-order link)
  React.useEffect(() => {
    const o = params && params.openSheet;
    if (o === "crew") setCrewSheet(true); else if (o === "packet") setPlanSheet(true);
  }, [params && params.openSheet, q && q.id]);
  React.useEffect(() => {
    const d: any = (window as any).VFP_DEMO;
    if (!(d && d.active)) return;
    d.quotePage = { crewSheet: () => setCrewSheet(true), jobPacket: () => setPlanSheet(true) };
    return () => { d.quotePage = null; };
  }, []);
  const [editEst, setEditEst] = React.useState<any>(null);        // null | working copy of the estimate inputs
  const [priceEdits, setPriceEdits] = React.useState<any>({});    // edit-mode: in-progress per-line override text, keyed by line key
  const [noteEdit, setNoteEdit] = React.useState<any>(null);      // cover note editor: null | the template being typed
  const [descEdit, setDescEdit] = React.useState<any>(false);     // line descriptions editable in the Pricing table
  const [validEdit, setValidEdit] = React.useState<any>(false);   // the valid-until date picker
  React.useEffect(() => { setNoteEdit(null); setDescEdit(false); setValidEdit(false); }, [q && q.id]);
  const [, force] = React.useReducer(x => x + 1, 0);

  if (!q) {
    return <div className="page" style={{ textAlign: "center", paddingTop: 80 }}>
      <EmptyState icon="send" title="No quote selected" sub="Build and send a quote from the Estimator." />
      <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => go("estimator")}>Go to Estimator</button>
    </div>;
  }

  const { product, inputs, fenceQuotes, quote, runGroups: _rg, lines: _ln, changeOrders: qChangeOrders } = computeQuoteView(q, settings);
  // a concrete quote (lib/verticals.ts): the fence-only pieces of this page —
  // the runs table, site plan, 3D build, crew sheet, job packet, the re-measure
  // editor — step aside; everything about money, signing and paying is shared
  const isConcrete = q.vertical === "concrete";
  const thing = isConcrete ? "concrete" : "fence";
  // the service's brand (plan §5.7): letterhead, links, the legal form on the printed acceptance line
  const brand = brandOf(store, verticalOf(q));
  // keep the STORED total in step with the live engine total — the estimates
  // list, hub landing card, dashboards and reports all read q.total, and it
  // goes stale the moment pricing settings or the estimate change. Unsigned,
  // unpaid quotes only: a signed ledger's contract total must never drift.
  React.useEffect(() => {
    const frozen = q.status === "signed" || q.status === "paid" || (q.signature && q.signature.at) || quotePayments(q).paid > 0;
    if (frozen) return;
    const t = Math.round((Number(quote.customerTotal) || 0) * 100) / 100;
    if (t > 0 && Math.abs(t - (Number(q.total) || 0)) > 0.005) store.updateQuote(q.id, { total: t });
  }, [quote.customerTotal, q.status]);
  const committedQ = q.status === "signed" || q.status === "paid";
  /* ---- quote details (lib/quotedetails.ts): the valid-until date, the cover
     note and line descriptions. Before signing they're edited right here;
     once it's signed they are part of the contract — the server froze them in
     signedDoc — so the editors go, the same way the price's do. ---- */
  const tz = businessTz(settings);
  const ex = openExpiry(q, settings);                  // "none" for a signed, lost or archived quote
  const detailsLocked = committedQ || signedOf(q);
  const coName = brand.name || "";
  const firstName = firstNameOf(q.customer);
  const noteShown = shownCoverNote(q, { firstName, company: coName });
  const defaultNote = cleanNote(settings && settings.quoteCoverNote);
  const noteHasFields = noteEdit != null && /\{\s*(first[ _]?name|company)\s*\}/i.test(String(noteEdit));
  function setValidUntil(v: any) { store.updateQuote(q.id, { validUntil: isYmd(v) ? v : null }); force(); }
  function extendQuote(days: number = EXTEND_DAYS) {
    const v = extendValidUntil(q.validUntil, days, { tz });
    store.updateQuote(q.id, { validUntil: v });
    store.addActivity(`Extended ${q.customer || "the customer"}'s quote — good until ${fmtDate(v)}`);
    notify(`Good until ${fmtDate(v)} — ${firstName || "the customer"} can sign it again.`);
    setValidEdit(false); force();
  }
  function saveNote(text: any) { store.updateQuote(q.id, { coverNote: cleanNote(text) }); setNoteEdit(null); force(); }
  // one line under a priced line: typed text wins, "" hides the price-book
  // default on this quote, null goes back to it
  function setLineDesc(key: string, value: any) {
    if (!key) return;
    const next: any = { ...((q.lineDescs && typeof q.lineDescs === "object") ? q.lineDescs : {}) };
    if (value == null) delete next[key]; else next[key] = String(value).replace(/[\r\n]+/g, " ").slice(0, DESC_MAX);
    store.updateQuote(q.id, { lineDescs: next }); force();
  }
  /* sending an expired quote sends the customer to a page they can't sign on —
     offer the extension first (never a silent send) */
  async function expiredOkToSend() {
    if (!ex.expired) return true;
    const ok = await confirmDialog({
      title: "This quote has expired",
      body: `It was good until ${fmtDate(ex.validUntil)}, so ${firstName || "the customer"} can't sign it. Extend it ${EXTEND_DAYS} days and send?`,
      confirmLabel: `Extend ${EXTEND_DAYS} days & send`, cancelLabel: "Not now",
    });
    if (ok) extendQuote();
    return ok;
  }
  // cost & margin (operator only — roles.ts "money.cost"): the line above the
  // document, the breakdown below it, and the floor checked at send
  const canCost = storeCan(store, "money.cost");
  const qm = quoteMarginOf(quote);
  const qComm = repCommission(null, q, settings);
  const floorHit = committedQ ? null : marginBelowFloor(qm, settings);
  /* Margin floor at send: a quote going out under the floor asks first — in
     the app's own dialog, never a block. Nobody past the owner's roles sees
     the numbers, and with cost hidden (the screen's turned to the customer)
     the dialog doesn't show them either. A signed quote has nothing to send. */
  async function marginOkToSend() {
    const hit = committedQ ? null : marginBelowFloor(quoteMarginOf(quote), settings);
    if (!hit) return true;
    const numbers = canCost && !costHidden;
    return confirmDialog({
      title: "Below your margin floor",
      body: numbers
        ? `This quote is at ${Math.round(hit.margin)}% margin — below your ${Math.round(hit.floor)}% floor. Send anyway?`
        : "This quote is priced below the margin floor set for your business. Send anyway?",
      confirmLabel: "Send anyway", cancelLabel: "Not yet",
    });
  }
  // payment ledger: Stripe deposits + manually recorded payments → paid / due
  const pay = quotePayments(q);
  const paidTotal = pay.paid;
  const amountDue = Math.max(0, quote.customerTotal - paidTotal);
  const fullyPaid = paidTotal > 0 && amountDue <= 0.005;
  // deposit due (per-quote override → workspace policy → 50%) and the optional
  // progress-billing schedule. Both purely derive the customer-facing numbers.
  const depInfo = quoteDeposit(q, settings, quote.customerTotal);
  const sched = quoteSchedule(q, quote.customerTotal, paidTotal);
  // in-person pad amount: the contractor can flip 25/50/Full there (`deposit`
  // is a 0..1 fraction); until they touch it, follow the resolved deposit.
  const depFrac = deposit != null ? deposit : (quote.customerTotal > 0 ? depInfo.amount / quote.customerTotal : 0.5);
  const dep = quote.customerTotal * depFrac;
  // online payments: prefer the per-payment Stripe records (they carry the date
  // + source); fall back to one synthetic entry for quotes paid before we
  // started recording them.
  const stripeEntries = Array.isArray(q.stripePayments) && q.stripePayments.length
    ? q.stripePayments
    : (Number(q.depositPaid) > 0 ? [{ amount: Number(q.depositPaid), method: "stripe", at: null }] : []);
  const ledger = [
    ...stripeEntries,
    ...pay.list,
  ];
  const fin = (settings && settings.financing) || window.VFP.DEFAULT_SETTINGS.financing;
  const finLive = financingLive(settings);   // enabled AND Wisetack connected
  const finMonthly = window.VFP.monthlyPayment(quote.customerTotal, fin.apr, fin.termMonths);
  function applyFinancing() {
    store.addActivity(`${q.customer} opened financing (${fin.partner})`);
    store.updateQuote(q.id, { financingClicked: true });
    const DB: any = window.VFP_DB;
    if (DB && DB.financingLink && DB.apiBase && DB.apiBase()) {
      DB.financingLink(q.id)
        .then((r: any) => { if (r && r.url) window.open(r.url, "_blank", "noopener"); else if (fin.applyUrl) window.open(fin.applyUrl, "_blank", "noopener"); })
        .catch((e: any) => {
          if (e && e.code === "not_connected") notify("Connect your Wisetack account in Integrations to offer monthly financing.");
          else if (fin.applyUrl) window.open(fin.applyUrl, "_blank", "noopener");
          else notify("Couldn't start financing: " + ((e && e.message) || e));
        });
    } else if (fin.applyUrl) { window.open(fin.applyUrl, "_blank", "noopener"); }
    force();
  }

  // per-run detail + customer pricing lines come from computeQuoteView
  const runGroups = _rg;

  /* ---- manual estimate editing (field-verified lengths, gates, corners) ----
     Everything on this page — pricing, BOM, site-plan labels, and the work
     order's supplies list — derives from q.inputs through the same engine, so
     editing the inputs re-flows all of it. Multi-fence quotes are edited in
     the Estimator (per-fence snapshots). */
  const multiFence = !!(q.inputs && q.inputs.fences && q.inputs.fences.length > 1);
  const estParcel = q.inputs && q.inputs.parcel;
  function startEditEst() {
    if (multiFence) { go("estimator", { loadProject: q.projectId }); return; }
    setPriceEdits({});   // fresh price-override edit session (buffers reload from q.lineOverrides)
    setEditEst({
      sides: estParcel && Array.isArray(estParcel.sides) ? estParcel.sides.map(s => ({ ...s })) : null,
      linearFeet: Math.round((q.inputs && q.inputs.linearFeet) || q.lf || 0),
      gates: (q.inputs && q.inputs.gates) | 0,
      corners: (q.inputs && q.inputs.corners) | 0,
    });
  }
  function applyEditEst() {
    const w = editEst; if (!w) return;
    let inputs2: any, lf: number;
    if (w.sides) {
      const sides = w.sides.map(s => ({ ...s, lf: Math.max(0, Math.round(+s.lf || 0)) }));
      const sel = sides.filter(s => s.selected && s.lf > 0);
      if (!sel.length) { notify("Keep at least one fence run selected (with a length)."); return; }
      const runs = sel.map(s => s.lf);
      lf = runs.reduce((a, b) => a + b, 0);
      // same closed-loop rule the estimator uses: every side selected on a closed parcel
      const closed = estParcel.closed !== false && sides.every(s => s.selected);
      inputs2 = { ...q.inputs, parcel: { ...estParcel, sides }, runs, closed, linearFeet: lf, gates: w.gates | 0, corners: w.corners | 0 };
    } else {
      lf = Math.max(0, Math.round(+w.linearFeet || 0));
      if (lf <= 0) { notify("Enter the fence length."); return; }
      // no per-run geometry on a manual quote → the engine prices from total LF + corners
      inputs2 = { ...q.inputs, runs: null, linearFeet: lf, gates: w.gates | 0, corners: w.corners | 0 };
    }
    const q2 = window.VFP.computeQuote(inputs2, product, settings);
    if (committedQ) {
      // signed/paid → never silently rewrite the contract: review the change as
      // a CHANGE ORDER in our own modal (amount tweakable — discount it, no-charge
      // it, or set a manual price). Materials/schedule still follow the inputs.
      const engineBefore = window.VFP.computeQuote(q.inputs, product, settings).customerTotal;
      const delta = Math.round((q2.customerTotal - engineBefore) * 100) / 100;
      const oldLF = Math.round((q.inputs && q.inputs.linearFeet) || q.lf || 0);
      const og = (q.inputs && q.inputs.gates) | 0, ng = w.gates | 0;
      const label = `Re-measured — ${oldLF} ft → ${Math.round(lf)} ft` + (og !== ng ? `, gates ${og} → ${ng}` : "");
      setCoReview({ kind: "remeasure", label, amount: String(delta), engineDelta: delta, meta: { inputs2, lf } });
      return;   // recorded (or cancelled) from the modal
    }
    const revised = q2.customerTotal;
    store.updateQuote(q.id, { inputs: inputs2, lf, total: revised });
    if (q.projectId && store.updateProject) store.updateProject(q.projectId, { lf, total: revised });
    store.addActivity(`Estimate updated for ${q.customer || "customer"} — ${Math.round(lf)} ft · ${Q_money0(revised)}`);
    setEditEst(null); force();
  }

  /* change-order review modal — both flavors land here: a re-measure (carries
     the new inputs) and a custom cost/credit (equipment rental, land clearing). */
  const [coReview, setCoReview] = React.useState<any>(null);
  const [moreOpen, setMoreOpen] = React.useState<any>(false);
  const moreRef = React.useRef<any>(null);
  React.useEffect(() => {
    if (!moreOpen) return;
    const off = (e: any) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const esc = (e: any) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("mousedown", off); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", off); document.removeEventListener("keydown", esc); };
  }, [moreOpen]);
  const coAppliedSum = (list: any[]) => (list || []).filter((c: any) => c && (!c.status || c.status === "approved")).reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0);
  /* mode: "pending" (default — the customer approves it on their hub with the
     same e-sign flow as the quote) or "approved" (they're standing right here:
     it's recorded pending and the in-person approval card opens, where they
     sign for it on this device). Only approved COs move the total — a pending
     one sits on the ledger badged "awaiting approval". */
  function recordChangeOrder(mode: any) {
    const r = coReview; if (!r) return;
    const label = String(r.label || "").trim();
    const amt = Math.round((+r.amount || 0) * 100) / 100;
    if (!label) { notify("Describe the change."); return; }
    if (r.kind === "custom" && !amt) { notify("Enter a non-zero amount (negative for a credit)."); return; }
    const cosPrev = Array.isArray(q.changeOrders) ? q.changeOrders : [];
    // the base, never quote.customerTotal: before a signed total exists that
    // already carries the approved COs, and the re-foot below adds them again
    const signedTotal = quote.baseTotal;
    const co: any = { id: "co" + Date.now(), at: Date.now(), kind: r.kind, label, amount: amt, status: "pending" };
    if (r.kind === "remeasure" && Math.abs((r.engineDelta || 0) - amt) > 0.005) co.engineDelta = r.engineDelta;  // documented: contractor adjusted the price
    if (r.photo) co.photo = r.photo;   // the "why": a shot of what was found on site
    const nextCos = [...cosPrev, co];
    const revised = Math.max(0, signedTotal + coAppliedSum(nextCos));
    const patch: any = { changeOrders: nextCos, signedTotal, total: revised };
    if (r.meta) { patch.inputs = r.meta.inputs2; patch.lf = r.meta.lf; }
    store.updateQuote(q.id, patch);
    if (q.projectId && store.updateProject) store.updateProject(q.projectId, { total: revised, ...(r.meta ? { lf: r.meta.lf } : {}) });
    store.addActivity(`Change order — ${q.customer || "customer"}: ${label} (${amt >= 0 ? "+" : "−"}${Q_money(Math.abs(amt))})${mode === "approved" ? " · approving in person" : " · awaiting customer approval"}`);
    setCoReview(null); setEditEst(null);
    if (mode === "approved") setCoSign({ coId: co.id, name: "", agree: false });
    force();
  }
  /* contractor-side actions on a PENDING change order */
  function coSetStatus(coId: any, status: any, extra?: any) {
    const cos = Array.isArray(q.changeOrders) ? q.changeOrders : [];
    const nextCos = status === "withdrawn"
      ? cos.filter((c: any) => c.id !== coId)
      : cos.map((c: any) => c.id === coId ? { ...c, status, ...(extra || {}) } : c);
    const revised = Math.max(0, quote.baseTotal + coAppliedSum(nextCos));
    store.updateQuote(q.id, { changeOrders: nextCos, total: revised });
    if (q.projectId && store.updateProject) store.updateProject(q.projectId, { total: revised });
    force();
  }
  /* In person: the customer approves a change order on THIS device — their
     typed name, the consent box and a drawn signature, the same as the
     approval card on their hub. Online it goes to the server, which records it
     with the hub's audit trail plus who presented the device. The one-tap
     stamp this replaces was written by the browser, under the quote's customer
     name, with no signature at all. */
  async function approveCoInPerson(image: any) {
    const s = coSign; if (!s || s.busy) return;
    const cos = Array.isArray(q.changeOrders) ? q.changeOrders : [];
    const c = cos.find((x: any) => x && x.id === s.coId);
    if (!c) { setCoSign(null); return; }
    const name = String(s.name || "").trim();
    if (!s.agree) { setCoSign({ ...s, err: "Have them tick the agreement box first." }); return; }
    if (name.length < 2) { setCoSign({ ...s, err: "Have them type their full legal name first." }); return; }
    const amount = round2(c.amount);
    const after = round2(Math.max(0, quote.baseTotal + coAppliedSum(cos.map((x: any) => x.id === c.id ? { ...x, status: "approved" } : x))));
    const DB = window.VFP_DB;
    if (DB && DB.apiBase && DB.apiBase() && DB.approveChangeOrderInPerson) {
      setCoSign({ ...s, busy: true, err: "" });
      try {
        const r = await DB.approveChangeOrderInPerson({ quoteId: q.id, coId: c.id, name, image, agree: true, expectedAmount: amount, expectedTotal: after });
        if (r.quote) store.adoptQuote(r.quote);
        const total = r.revisedTotal != null ? r.revisedTotal : after;
        if (!r.already) {
          if (q.projectId && store.updateProject) store.updateProject(q.projectId, { total });
          store.addActivity(`${name} approved a change order in person — ${c.label} · new total ${Q_money0(total)}`);
        }
        setCoSign(null);
        notify(r.already ? "That change order was already approved — nothing changed." : `Approved by ${name} — the new total is ${Q_money0(total)}.`);
      } catch (e: any) {
        setCoSign((cur: any) => cur && { ...cur, busy: false, err: (e && e.message) || "Couldn't record the approval — try again." });
      }
      force();
      return;
    }
    // sandbox / no service configured: nothing to record it on but this device
    const sandbox = !!(window.VFP_DEMO && window.VFP_DEMO.active);
    coSetStatus(c.id, "approved", { approval: { name, image: typeof image === "string" ? image.slice(0, 200000) : null, at: new Date().toISOString(), method: "in_person", ...(sandbox ? { sandbox: true } : {}) } });
    store.addActivity(`${name} approved a change order in person — ${c.label}`);
    setCoSign(null);
  }
  const setSideEdit = (i, patch) => setEditEst(w => ({ ...w, sides: w.sides.map(s => s.i === i ? { ...s, ...patch } : s) }));
  const [gpEditOpen, setGpEditOpen] = React.useState<any>({});   // which side's grade-points editor is expanded in the Edit-estimate panel
  // normalize a profile the SAME way the Estimator's setSideProfile does (filter
  // invalid, clamp t to 0..1, sort by t, null when empty) so points captured here
  // and on the map are byte-for-byte interchangeable.
  const normProfile = (prof: any) => { const n = (prof || []).filter((pt: any) => isFinite(+pt.t) && isFinite(+pt.deltaIn)).map((pt: any) => ({ t: Math.max(0, Math.min(1, +pt.t)), deltaIn: +pt.deltaIn })).sort((a: any, b: any) => a.t - b.t); return n.length ? n : null; };
  const editLF = editEst && (editEst.sides
    ? editEst.sides.filter(s => s.selected).reduce((t, s) => t + Math.max(0, Math.round(+s.lf || 0)), 0)
    : Math.max(0, Math.round(+editEst.linearFeet || 0)));

  // ---- bill of materials (supplies + quantities, NO prices) — straight from the engine's itemized list ----
  const bom = quoteBom(quote, product);

  const lines = _ln;

  /* ---- per-line price overrides (Edit-estimate) ----
     A hard dollar value the contractor types over ANY computed pricing line,
     stored on the quote as q.lineOverrides[key] (keyed by the line's stable
     key). computeQuoteView applies them + re-foots subtotal/tax/total, so the
     contractor page, the print view and the customer hub all show the
     overridden numbers. setLineOverride writes the WHOLE map back (updateQuote
     shallow-merges); an empty string / null entry clears that line. */
  function lineOverridesMap() {
    return (q.lineOverrides && typeof q.lineOverrides === "object") ? { ...q.lineOverrides } : {};
  }
  function setLineOverride(key: string, value: any) {
    if (!key) return;
    // keep the raw text the contractor is typing (so "12.", leading zeros, an
    // empty field… all behave) while committing a clean rounded number to the
    // quote so the re-footed totals update live.
    setPriceEdits((m: any) => ({ ...m, [key]: value }));
    const next = lineOverridesMap();
    if (value === "" || value == null || !isFinite(+value)) { delete next[key]; }
    else { next[key] = Math.max(0, Math.round((+value) * 100) / 100); }
    store.updateQuote(q.id, { lineOverrides: next });
    force();
  }
  function clearLineOverride(key: string) {
    setPriceEdits((m: any) => { const n = { ...m }; delete n[key]; return n; });
    const next = lineOverridesMap();
    if (!(key in next)) return;
    delete next[key];
    store.updateQuote(q.id, { lineOverrides: next });
    force();
  }

  /* In person: the customer signs on THIS device. It used to be a browser
     write of status + signedTotal (+ the signedDoc stampQuoteDoc froze), and
     quotes_guard_money strips signature, signedTotal, signedDoc and contract
     from every browser save — so what reached the cloud was a "signed" quote
     with no signature, no contract and nothing frozen. Online it now goes to
     the server, which signs it exactly as the hub does (same contract, same
     hash, same consent), records that it happened in person and who presented
     the device, and hands back the quote as it now stands for this tab to
     adopt. The pad sends the total it showed; the server refuses a figure it
     doesn't compute itself. */
  async function signInPerson(image: any) {
    setIpErr("");
    if (ipBusy) return;
    const name = String(ipName || "").trim();
    if (!ipAgree) { setIpErr("Have them read the Terms & Conditions and tick the agreement box first."); return; }
    if (name.length < 2) { setIpErr("Have them type their full legal name first."); return; }
    const total = round2(quote.customerTotal);
    const DB = window.VFP_DB;
    if (DB && DB.apiBase && DB.apiBase() && DB.signInPerson) {
      setIpBusy(true);
      try {
        const r = await DB.signInPerson({ quoteId: q.id, name, image, agree: true, termsVersion: isConcrete ? CONTRACT_TERMS_CONCRETE_VERSION : CONTRACT_TERMS_VERSION, expectedTotal: total });
        if (r.quote) store.adoptQuote(r.quote);
        if (!r.already) store.addActivity(`${name} signed in person — ${Q_money0(total)}`);
        notify(r.already ? "This estimate was already signed — nothing changed." : "Signed. The signature and its audit trail are on the estimate.");
      } catch (e: any) {
        setIpErr((e && e.message) || "Couldn't record the signature — try again.");
      } finally { setIpBusy(false); force(); }
      return;
    }
    // sandbox / no service configured: nothing to sign against but this device
    const sandbox = !!(window.VFP_DEMO && window.VFP_DEMO.active);
    store.updateQuote(q.id, {
      status: q.status === "paid" ? "paid" : "signed",
      signedTotal: quote.baseTotal,
      signature: { name, image: typeof image === "string" ? image.slice(0, 200000) : null, at: new Date().toISOString(), terms: isConcrete ? CONTRACT_TERMS_CONCRETE_VERSION : CONTRACT_TERMS_VERSION, method: "in_person", ...(sandbox ? { sandbox: true } : {}) },
    });
    store.addActivity(`${name} signed in person — ${Q_money0(total)}`);
    force();
  }
  /* discount editor — % or flat $, named so the invoice line reads like a
     promotion ("Spring special"), not a price mistake. Pre-sign only: once
     signed the contract ledger is frozen (post-sign price moves are COs). */
  const [discModal, setDiscModal] = React.useState<any>(null);   // { label, mode: "pct"|"amt", value }
  function applyDiscount(next: any) {
    // recompute the stored total through the same view math the doc renders
    const v2 = computeQuoteView({ ...q, discount: next }, settings);
    store.updateQuote(q.id, { discount: next, total: v2.quote.customerTotal });
    if (next) store.addActivity(`Discount on ${q.customer || "quote"}: ${(next.label || "Discount")} (−${Number(next.pct) > 0 ? next.pct + "%" : Q_money0(Number(next.amt) || 0)})`);
    else store.addActivity(`Discount removed from ${q.customer || "quote"}'s estimate`);
    setDiscModal(null); force();
  }

  /* mark-lost: one tap + a reason chip. The reason is the whole point — it
     feeds the "Why we lose" report; a lost quote drops out of every open-
     pipeline list (they all filter on pending/viewed) but stays on the books. */
  const [lostModal, setLostModal] = React.useState<any>(null);   // { reason, note }
  function markLost() {
    const r = lostModal || {};
    if (!r.reason) { notify("Pick a reason — that's the data that makes this worth tracking."); return; }
    store.updateQuote(q.id, { status: "lost", lostReason: r.reason, lostNote: (r.note || "").trim() || undefined, lostAt: Date.now() });
    store.addActivity(`Marked lost — ${q.customer || "customer"}: ${(LOST_REASONS.find(x => x.id === r.reason) || {}).label || r.reason}`);
    setLostModal(null); force();
  }
  function reopenLost() {
    const log = [...(Array.isArray(q.lostLog) ? q.lostLog : []), { reason: q.lostReason, note: q.lostNote, at: q.lostAt, reopenedAt: Date.now() }];
    store.updateQuote(q.id, { status: "pending", lostReason: undefined, lostNote: undefined, lostAt: undefined, lostLog: log });
    store.addActivity(`Reopened the estimate for ${q.customer || "customer"} — back in the pipeline`);
    force();
  }
  async function markPaid() {
    /* LIVE: take the deposit for real. This button said "Pay $X deposit ·
       Secured by Stripe" and only wrote status "paid" + depositPaid into the
       quote — no charge. The server strips a browser-written depositPaid, but
       "paid" can never be walked back, so the hub then read "Deposit paid ✓"
       with $0 received. It now opens the customer's own pay page (Stripe
       Checkout, the same one their link uses) for them to pay on this device.
       Cash or check is "Record it manually" below. */
    const DB: any = window.VFP_DB;
    if (DB && DB.hubLink && DB.apiBase && DB.apiBase() && q.customerId) {
      try {
        const { token } = await pushHubLink(store, q.customerId);
        const base = (window.location && window.location.origin) || "";
        window.open(`${base}/hub/${token}`, "_blank", "noopener");
        notify("Opened the customer's pay page — hand them the device to pay by card or bank. The quote updates when Stripe confirms.");
      } catch (e: any) { notify((e && e.message) || "Couldn't open the pay page."); }
      return;
    }
    // demo / offline sandbox only: simulate the deposit so the flow can be walked
    // the resolved deposit (per-quote override → policy → default), in cents
    const depAmt = Math.round(((depInfo && isFinite(depInfo.amount) && depInfo.amount > 0) ? depInfo.amount : dep) * 100) / 100;
    store.updateQuote(q.id, { status: "paid", depositPaid: depAmt, signedTotal: quote.baseTotal });
    store.addActivity(`${q.customer} paid a deposit — ${Q_money0(depAmt)}`);
    store.addProjectFromQuote(q, quote);
    force();
  }
  async function recordPayment(p) {
    // attribution: which signed-in user logged this payment
    try {
      const a: any = window.VFP_DB && window.VFP_DB.auth;
      if (a && a.enabled) { const u = await a.getUser(); if (u) p.by = u.name || u.email || ""; }
    } catch (e) {}
    p.createdAt = Date.now();
    const firstPay = paidTotal <= 0.005 && q.status !== "paid";   // nothing collected yet → this payment books the job
    const dueAfter = Math.max(0, quote.customerTotal - (paidTotal + p.amount));
    const patch: any = { payments: pay.list.concat([p]) };
    // "paid" means paid in full; a partial payment leaves the quote "signed"
    // (the balance keeps showing everywhere) — never step a paid quote back.
    if (dueAfter <= 0.005) patch.status = "paid";
    else if (q.status !== "paid") patch.status = "signed";
    store.updateQuote(q.id, patch);
    if (firstPay) store.addProjectFromQuote(q, quote);   // same kickoff a Stripe deposit triggers
    // job.paid on the manual payment that clears the balance (the Stripe path
    // emits server-side in quotePaidFromSession, so these never double-fire).
    if (paidTotal < quote.customerTotal - 0.005 && dueAfter <= 0.005) {
      try {
        const DB: any = window.VFP_DB;
        if (DB && DB.emitEvent) DB.emitEvent("job.paid", {
          customer: { id: (q as any).customerId || null, name: q.customer, phone: q.phone, email: q.email },
          job: { id: q.id, amount: quote.customerTotal, bom: (q as any).bom || [], vertical: verticalOf(q) },
        }).catch(() => {});
      } catch (e) {}
    }
    store.addActivity(`${q.customer} paid ${Q_money0(p.amount)} — ${payMethodLabel(p)}${dueAfter <= 0.005 ? " · paid in full" : ""}`);
    // FW → QuickBooks: reflect this manual payment on the QBO invoice (if synced)
    if ((q as any).qboInvoiceId) resyncQboPayments();
    setRecordPay(false); force();
  }
  // FW → QuickBooks payments: a failed sync is surfaced (chip + toast) with a
  // one-tap retry instead of vanishing into an empty catch.
  async function resyncQboPayments() {
    try {
      const DB: any = window.VFP_DB;
      if (!(DB && DB.qboSyncPayments)) return;
      if (DB.flushNow) await DB.flushNow();
      await DB.qboSyncPayments(q.id);
      setQboPayFail(false);
    } catch (e) {
      setQboPayFail(true);
      notify("QuickBooks didn't update — tap Re-sync on the quote to try again.");
    }
  }
  async function deletePayment(p) {
    if (!p || !p.id) return;
    if (!(await confirmDialog({ title: `Remove this ${Q_money0(p.amount)} payment?`, body: "The amount due goes back up.", confirmLabel: "Remove payment", danger: true }))) return;
    store.updateQuote(q.id, { payments: pay.list.filter((x: any) => x.id !== p.id) });
    store.addActivity(`Removed a recorded ${Q_money0(p.amount)} payment (${payMethodLabel(p)})`);
    setPayView(null); force();
  }
  // ---- payment terms: configurable deposit + optional milestone schedule ----
  // set a flat-$ deposit override on the quote (clears the % override)
  function setDepositAmt(v: any) {
    const n = v === "" || v == null ? null : Math.max(0, Number(v) || 0);
    store.updateQuote(q.id, { depositAmt: n, depositPct: null }); force();
  }
  // set a %-of-total deposit override (clears the $ override)
  function setDepositPct(v: any) {
    const n = v === "" || v == null ? null : Math.max(0, Math.min(100, Number(v) || 0));
    store.updateQuote(q.id, { depositPct: n, depositAmt: null }); force();
  }
  // back to the workspace default (no per-quote override)
  function clearDepositOverride() { store.updateQuote(q.id, { depositPct: null, depositAmt: null }); force(); }
  // milestone schedule helpers — the schedule is an ordered list of % stages
  function setSchedule(stages: any) { store.updateQuote(q.id, { paymentSchedule: stages && stages.length ? stages : null }); force(); }
  function enableSchedule() { setSchedule(defaultSchedule(Math.round(depInfo.pct))); }
  function updateStage(i: number, patch: any) {
    const cur = Array.isArray(q.paymentSchedule) ? q.paymentSchedule.slice() : [];
    cur[i] = Object.assign({}, cur[i], patch); setSchedule(cur);
  }
  function addStage() {
    const cur = Array.isArray(q.paymentSchedule) ? q.paymentSchedule.slice() : [];
    setSchedule(cur.concat([{ id: "s" + Date.now().toString(36), label: "Stage " + (cur.length + 1), pct: 0 }]));
  }
  function removeStage(i: number) {
    const cur = Array.isArray(q.paymentSchedule) ? q.paymentSchedule.slice() : [];
    cur.splice(i, 1); setSchedule(cur);
  }
  /* the customer whose hub this quote shares to, with a minted token — NEVER a
     dead "/hub/preview" link (the server 404s that token). Resolution order:
     the linked customerId → adopt an existing customer matching the quote's
     name/email → create one from the quote's contact fields. Whatever matched
     gets written back onto the quote so the link stays stable. Returns null
     only for a truly anonymous quote (no name AND no email to link on). */
  function ensureShareToken() {
    let cust = (store.customers || []).find(x => x.id === q.customerId);
    if (!cust) cust = findCustomer(store.customers || [], q.customer, q.email);
    if (!cust) {
      if (!(q.customer || "").trim() && !(q.email || "").trim()) return null;
      cust = { id: newId("cust-"), name: q.customer || q.email, phone: q.phone || "", email: q.email || "", address: q.address || "", hubToken: genToken() };
      store.addCustomer(cust);
    }
    if (cust.id !== q.customerId) store.updateQuote(q.id, { customerId: cust.id });
    let token = cust.hubToken;
    if (!token) { token = genToken(); store.updateCustomer(cust.id, { hubToken: token }); }
    return token;
  }
  // build + open the customer's secure sign & pay link (the normal flow — the
  // customer e-signs and pays from their own device on the hub)
  async function openShareLink() {
    if (!(await marginOkToSend())) return;
    if (!(await expiredOkToSend())) return;
    const token = ensureShareToken();
    const base = location.origin;
    const url = token ? base + "/hub/" + token : base + "/hub/preview";
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    store.updateQuote(q.id, { status: q.status === "pending" ? "viewed" : q.status });
    setShare({ url, copied: true, preview: !token, live: token ? "checking" : null });
    if (token) hubLinkLive(token).then((res: any) => setShare((s: any) => s && s.url === url ? { ...s, live: res } : s));
  }
  // "Fix link" (push this customer + token to the cloud now) and "Regenerate
  // link" (issue a fresh token, the old link dies). Both write the customer
  // row through the service, then re-check the link is live.
  async function repairShareLink(regenerate: boolean) {
    const token0 = ensureShareToken();
    if (!token0) return;
    setShare((s: any) => ({ ...(s || {}), live: "fixing", error: null }));
    try {
      const r = await pushHubLink(store, q.customerId || ((store.customers || []).find((x: any) => x.hubToken === token0) || {}).id, regenerate);
      const url = location.origin + "/hub/" + r.token;
      if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
      setShare({ url, copied: true, preview: false, live: "checking", reissued: r.reissued });
      const live = await hubLinkLive(r.token);
      setShare((s: any) => s && s.url === url ? { ...s, live } : s);
    } catch (e: any) {
      setShare((s: any) => s ? { ...s, live: "dead", error: (e && e.message) || "Couldn't reach the service" } : s);
    }
  }
  // public 3D-only link — the SAME hub token on /build3d/<token>: just the
  // customer's fence in the construction viewer. No sign-in, no hub chrome.
  function openShare3D() {
    const token = ensureShareToken();
    const base = location.origin;
    const url = token ? base + "/build3d/" + token : base + "/build3d/preview";
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    setShare3d({ url, copied: true });
  }

  return (
    <div className="page fade-up" style={{ maxWidth: 1100 }}>
      {/* actions bar */}
      <div className="row between no-print quote-toolbar" style={{ marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => goBack ? goBack() : go("dashboard")}><Icon name="chevright" size={14} style={{ transform: "rotate(180deg)" }} />Back</button>
        <div className="row gap-8 quote-actions">
          {(() => {
            const wo = (store.workOrders || []).find((w: any) => w.quoteId === q.id);
            return wo ? <button className="btn btn-outline btn-sm" onClick={() => go("workorders", { openWorkOrder: wo.id })}><Icon name="clipboard" size={14} />View work order</button> : null;
          })()}
          {(q.inputs && (q.inputs.parcel || (q.inputs.mVerts && q.inputs.mVerts.length > 1))) && (
            <button className="btn btn-ghost btn-sm" data-tour="open-on-map" title="Reload this estimate's drawing in the Estimator — move corners, add gates, re-measure — then regenerate the quote"
              onClick={() => go("estimator", { restoreSnapshot: { ...q.inputs, productId: q.inputs.productId || q.productId }, quoteId: q.id, prefillCustomer: q.customerId || undefined })}>
              <Icon name="map" size={14} />Open on map</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => window.print()}><Icon name="printer" size={14} />Print / PDF</button>
          {q.customerId && <HubLinkButton store={store} customerId={q.customerId} beforeOpen={async () => (await marginOkToSend()) && (await expiredOkToSend())} />}
          <button className="btn btn-primary btn-sm" data-tour="signpay" onClick={openShareLink}><Icon name="lock" size={13} />Sign &amp; pay link</button>
          {/* the less-frequent actions live under More — the toolbar was
              wrapping to two rows on a laptop */}
          <div className="qmore no-print" ref={moreRef}>
          <button className="btn btn-outline btn-sm" aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((v: boolean) => !v)}><Icon name="sliders" size={13} />More ▾</button>
          {moreOpen && <div className="qmore-menu" role="menu" onClick={() => setMoreOpen(false)}>
          {<button className="btn btn-ghost btn-sm" onClick={() => setCrewSheet(true)}
            title="Printable build sheet for the install crew — specs, per-run grades, materials & the ordered build sequence">
            <Icon name="clipboard" size={14} />Crew sheet</button>}
          {!isConcrete && <button className="btn btn-ghost btn-sm" onClick={() => setPlanSheet(true)}
            title="The customer's job packet — blueprint fence plan, install specs & pre-job sheet, plus your own uploaded documents. Print / save as PDF from the preview.">
            <Icon name="book" size={14} />Job packet</button>}
          {(() => {
            const qbo = store.settings && store.settings.integrations && store.settings.integrations.quickbooks;
            const DB: any = window.VFP_DB;
            if (!qbo || !qbo.connected || !DB || !DB.qboCreateInvoice || !DB.apiBase || !DB.apiBase()) return null;
            const qboPayload = { id: q.id, customer: q.customer, email: q.email, phone: q.phone, address: q.address,
              total: quote.customerTotal, subtotal: quote.subtotal, tax: quote.tax, taxRate: quote.taxRate,
              lines: (lines || []).map((l: any) => ({ label: l.label, sub: l.sub, amount: l.amount })),
              productName: q.productName || product.name, lf: quote.LF };
            // the quote changed after it was pushed → the QBO invoice is stale
            const outOfSync = !!q.qboInvoiceId && (q as any).qboSyncedTotal != null && Math.abs(quote.customerTotal - (q as any).qboSyncedTotal) > 0.005;
            return <button className="btn btn-outline btn-sm" disabled={qboState === "sending"}
              title={outOfSync ? "This estimate changed — update the QuickBooks invoice" : q.qboInvoiceId ? "Re-sync payments to QuickBooks" : "Create the invoice in QuickBooks"}
              style={outOfSync ? { color: "var(--amber)", borderColor: "var(--amber)" } : undefined}
              onClick={() => {
                setQboState("sending");
                const op = outOfSync ? DB.qboResyncInvoice(qboPayload) : DB.qboCreateInvoice(qboPayload);
                op.then((r: any) => {
                  store.updateQuote(q.id, { qboInvoiceId: r.invoiceId, qboDocNumber: r.docNumber, qboSyncedTotal: quote.customerTotal });
                  const inv = r.docNumber ? " #" + r.docNumber : "";
                  store.addActivity(outOfSync ? `Updated QuickBooks invoice${inv} to ${Q_money0(quote.customerTotal)}`
                    : !r.already ? `Created QuickBooks invoice${inv} for ${q.customer || "customer"}`
                    : (r.paymentSynced > 0 ? `Synced ${Q_money0(r.paymentSynced)} to QuickBooks invoice${inv}` : `QuickBooks invoice${inv} is up to date`));
                  setQboState("done");
                }).catch((e: any) => { setQboState(""); notify("QuickBooks: " + ((e && e.message) || e)); });
              }}><Icon name="clipboard" size={14} />{qboState === "sending" ? "Sending…" : outOfSync ? "Update QuickBooks" : q.qboInvoiceId ? "In QuickBooks ✓" : qboState === "done" ? "Sent ✓" : "Send to QuickBooks"}</button>;
          })()}
          {qboPayFail && <button className="chip" style={{ color: "var(--amber)", borderColor: "var(--amber)", cursor: "pointer" }} onClick={resyncQboPayments} title="The last payment didn't reach QuickBooks"><Icon name="undo" size={11} />QuickBooks didn't update — Re-sync</button>}
          <button className="btn btn-outline btn-sm" onClick={async () => { if (!(await marginOkToSend())) return; if (!(await expiredOkToSend())) return; store.updateQuote(q.id, { status: "viewed" }); force(); }}>Mark sent</button>
          <button className="btn btn-outline btn-sm" title="Public link that opens just their fence in the 3D construction viewer" onClick={openShare3D}><Icon name="box" size={13} />Share 3D</button>
          <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)", border: "1px solid var(--red-tint)" }}
            onClick={async () => { if (await confirmDialog({ title: `Delete this quote for ${q.customer || "customer"}?`, body: "This can't be undone.", confirmLabel: "Delete quote", danger: true })) { store.deleteQuote(q.id); (goBack ? goBack() : go("dashboard")); } }}>
            <Icon name="trash" size={14} />Delete
          </button>
          </div>}
          </div>
        </div>
      </div>

      {/* no sales-tax rate configured → this quote charges 0 tax. Nag (gently)
          until a rate is set OR the contractor confirms they don't charge tax
          (settings.taxNone) — plenty of states don't tax fence installs. */}
      {!(Number(settings.taxRate) > 0) && !settings.taxNone && (
        <div className="pe-callout warn no-print" style={{ marginBottom: 16, padding: "12px 16px" }}>
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
            <p style={{ margin: 0, fontSize: 12.5, flex: "1 1 280px", color: "var(--fg-2)" }}>
              <b style={{ color: "var(--amber)" }}>Sales tax isn't set</b> — this quote charges no tax. If your state taxes fence work, set your rate before sending.
            </p>
            <button className="btn btn-outline btn-sm" onClick={() => go("settings", { section: "tax" })}><Icon name="dollar" size={13} />Set sales tax</button>
            <button className="btn btn-ghost btn-sm" title="Hide this warning everywhere — you can turn it back on under Settings → Sales Tax & Fees" onClick={() => { store.setSettings({ taxNone: true }); force(); }}>We don't charge tax</button>
          </div>
        </div>
      )}

      {/* past its valid-until date and not signed: the customer's link shows
          "expired — ask for an updated quote" and the server won't sign it */}
      {ex.expired && (
        <div className="pe-callout warn no-print q-expired-banner" data-quote-expired style={{ marginBottom: 16, padding: "12px 16px" }}>
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
            <p style={{ margin: 0, fontSize: 12.5, flex: "1 1 280px", color: "var(--fg-2)" }}>
              <b style={{ color: "var(--amber)" }}>This quote expired on {fmtDate(ex.validUntil, ex.today)}</b> — {firstName || "the customer"} can't sign it until you extend it. Their link asks them to request an updated quote.
            </p>
            <button className="btn btn-primary btn-sm" onClick={() => extendQuote()}><Icon name="calendar" size={13} />Extend {EXTEND_DAYS} days</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setValidEdit(true)}>Pick a date</button>
          </div>
        </div>
      )}

      {(() => {
        const pend = (Array.isArray(q.changeOrders) ? q.changeOrders : []).filter((c: any) => c && c.status === "pending");
        if (!pend.length) return null;
        const wo = (store.workOrders || []).find((w: any) => w.quoteId === q.id);
        const sum = pend.reduce((a: number, c: any) => a + (Number(c.amount) || 0), 0);
        return (
          <div className="co-hold no-print" data-tour="co-hold" style={{ marginBottom: 14 }}>
            <Icon name="edit" size={15} style={{ color: "var(--amber)", marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>On hold — {pend.length} change order{pend.length > 1 ? "s" : ""} awaiting {(q.customer || "the customer").split(" ")[0]}'s approval ({sum >= 0 ? "+" : "−"}{Q_money0(Math.abs(sum))})</div>
              <div className="muted" style={{ fontSize: 12 }}>The invoice stays at the signed contract and production {wo && wo.coHoldOverride ? "was confirmed to proceed anyway" : "shouldn't be scheduled"} until it's signed on the portal. {wo ? "The work order and the schedule carry the same warning." : ""}</div>
            </div>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <button className="btn btn-outline btn-sm" onClick={openShareLink}><Icon name="send" size={12} />Send approval link</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setCoSign({ coId: pend[0].id, name: "", agree: false })} title="They're here — they sign for it on this device"><Icon name="check" size={12} />Approve in person</button>
              {wo && <button className="btn btn-ghost btn-sm" onClick={() => go("workorders", { openWorkOrder: wo.id })}><Icon name="clipboard" size={12} />Work order</button>}
            </div>
          </div>
        );
      })()}
      {/* cost & gross margin — internal, one line, above the customer's
          document. "Hide cost & margin" takes this AND the breakdown below off
          the screen, so the owner can turn it toward the customer. */}
      {canCost && (costHidden
        ? <div className="no-print" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setHideCostPref("0")} title="Bring back the cost and margin figures"><Icon name="lock" size={13} />Show cost &amp; margin</button>
          </div>
        : <div className="no-print" data-tour="margin-line" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 16px", padding: "9px 12px 9px 14px", marginBottom: 14, borderRadius: 12,
            border: "1px solid " + (floorHit ? "var(--amber)" : "var(--line)"), background: floorHit ? "var(--amber-tint)" : "var(--surface-2)" }}>
            <span className="row gap-6 kicker" style={{ margin: 0 }}><Icon name="lock" size={12} />Internal</span>
            <span style={{ fontSize: 13 }}><span className="muted">Cost</span> <b className="tnum">{Q_money0(qm.cost)}</b></span>
            <span style={{ fontSize: 13 }}><span className="muted">Gross margin</span> <b className="tnum" style={{ color: qm.profit < 0 || floorHit ? "var(--amber)" : "var(--green)" }}>{Q_money0(qm.profit)}{qm.pct != null ? " · " + Math.round(qm.pct) + "%" : ""}</b></span>
            {qComm.amount > 0 && qm.revenue > 0 && <span className="muted" style={{ fontSize: 12 }}>{Math.round(((qm.profit - qComm.amount) / qm.revenue) * 100)}% after {qComm.rep}'s {qComm.pct}% commission</span>}
            {floorHit && <span className="chip" style={{ fontSize: 11, color: "var(--amber)", borderColor: "var(--amber)" }}>Under your {Math.round(floorHit.floor)}% floor</span>}
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => setHideCostPref("1")} title="Take every cost and margin figure off this screen — to show it to the customer"><Icon name="x" size={12} />Hide cost &amp; margin</button>
          </div>)}
      <div className={"quote-doc" + (demoSample ? " demo-doc" : "")}>
        {demoSample && <DemoWatermark />}
        {/* header */}
        <div className="quote-head">
          <div className="row gap-12">
            <CompanyLogo company={brand} size={44} />
            <div>
              <div className="display" style={{ fontSize: 22 }}>{brand.name || "Your Company"}</div>
              {(() => {
                const contact = [brand.address, brand.phone].filter(Boolean).join(" · ");
                const named = !!brand.name;
                // placeholder header → one tap to where the logo/name/contact
                // actually get set, instead of making the user hunt Settings
                return (named && contact)
                  ? <div className="muted mono" style={{ fontSize: 12 }}>{contact}</div>
                  : <button className="btn btn-ghost btn-sm no-print" style={{ padding: 0, color: "var(--teal)", fontSize: 12 }}
                      title="Your logo, company name, and contact info go on every estimate"
                      onClick={() => go("settings", { section: "company" })}>
                      <Icon name="building" size={12} />Add your logo &amp; company info in Settings<Icon name="chevright" size={12} />
                    </button>;
              })()}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            {/* the same document moves through its life: ESTIMATE while it's a
                proposal, INVOICE once it's signed or money is on it, PAID when
                nothing is owed. Number and date stay; the invoice date is the
                day it was signed / booked. */}
            <div className="kicker" data-tour="doc-kind">{fullyPaid ? "Invoice · paid" : (committedQ || paidTotal > 0) ? "Invoice" : "Estimate"}</div>
            <div className="mono" style={{ fontSize: 13, marginTop: 4 }}>#{q.id.slice(-6).toUpperCase()}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{(committedQ || paidTotal > 0) && (q.wonAt || (q.signature && q.signature.at))
              ? <>Invoiced {new Date(q.wonAt || q.signature.at).toLocaleDateString()} <span className="faint">· estimate {new Date(q.createdAt).toLocaleDateString()}</span></>
              : new Date(q.createdAt).toLocaleDateString()}</div>
            {/* valid until — printed on the estimate; the controls are screen-only.
                Gone once it's signed: it was accepted in time. */}
            {!detailsLocked && paidTotal <= 0 && (
              <div className="q-valid" data-quote-valid>
                {validEdit ? (
                  <span className="q-valid-edit no-print">
                    <input className="input" type="date" aria-label="Valid until" value={isYmd(q.validUntil) ? q.validUntil : ""} onChange={e => setValidUntil(e.target.value)} />
                    <button className="btn btn-ghost btn-sm" onClick={() => extendQuote()} title={`Good for ${EXTEND_DAYS} more days`}>+{EXTEND_DAYS} days</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setValidUntil(null); setValidEdit(false); }} title="This quote doesn't expire">No expiry</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setValidEdit(false)}><Icon name="check" size={12} />Done</button>
                  </span>
                ) : ex.state === "none" ? (
                  <button className="qedit-btn no-print" onClick={() => setValidEdit(true)} title="Give this quote a valid-until date"><Icon name="calendar" size={11} />No expiry · set a date</button>
                ) : (
                  <>
                    <span className={"q-valid-date" + (ex.expired ? " expired" : ex.soon ? " soon" : "")}>{ex.expired ? "Expired " : "Valid until "}{fmtDate(ex.validUntil, ex.today)}</span>
                    {!ex.expired && <span className="muted no-print" style={{ fontSize: 11.5 }}> · {expiryPhrase(ex)}</span>}
                    <button className="qedit-btn no-print" style={{ marginLeft: 6 }} onClick={() => setValidEdit(true)}><Icon name="edit" size={11} />Change</button>
                  </>
                )}
              </div>
            )}
            {(committedQ || paidTotal > 0) && !fullyPaid && <div className="tnum" style={{ fontSize: 12.5, marginTop: 4 }}><span className="muted">Amount due</span> <b style={{ color: "var(--amber)" }}>{Q_money(amountDue)}</b></div>}
            <span className={"chip " + ({ pending: "", viewed: "", signed: "violet", paid: "green" }[q.status])} style={{ marginTop: 8, textTransform: "capitalize" }}>{fullyPaid ? "Paid in full" : q.status === "viewed" ? "Sent" : q.status === "paid" ? "Deposit paid" : q.status}</span>
            {/* who sold it — editable any time; feeds the leaderboard, Sales Team
                & jobs sheet. no-print keeps the customer's printed copy clean. */}
            <div className="row gap-8 no-print" style={{ marginTop: 8, justifyContent: "flex-end", alignItems: "center" }}>
              <span className="kicker" style={{ fontSize: 9.5 }}>Sold by</span>
              <select className="select" style={{ width: 150, padding: "5px 8px", fontSize: 12.5 }} value={q.rep || ""}
                onChange={e => {
                  const rep = e.target.value || undefined;
                  store.updateQuote(q.id, { rep });
                  if (q.projectId) store.updateProject(q.projectId, { rep });   // keep the won job credited to the same rep
                  force();
                }}>
                <option value="">Unassigned</option>
                {(() => {
                  const names = new Set<string>();
                  ((store.settings && store.settings.salesReps) || []).forEach((r: any) => { if (r.name) names.add(r.name); });
                  (store.quotes || []).forEach((x: any) => { if (x.rep) names.add(x.rep); });
                  if (q.rep) names.add(q.rep);
                  return [...names].sort().map(n => <option key={n} value={n}>{n}</option>);
                })()}
              </select>
            </div>
          </div>
        </div>

        {/* prepared for */}
        <div className="quote-for">
          <div style={{ flex: 1 }}><div className="row gap-8"><span className="kicker">Prepared for</span>
              <button className="qedit-btn no-print" onClick={() => setEditCust(e => !e)}><Icon name={editCust ? "check" : "edit"} size={11} />{editCust ? "Done" : "Edit"}</button></div>
            {editCust
              ? <div style={{ display: "grid", gap: 6, marginTop: 6, maxWidth: 300 }}>
                  <input className="input" value={q.customer || ""} placeholder="Customer name" onChange={e => { store.updateQuote(q.id, { customer: e.target.value }); if (q.customerId) store.updateCustomer(q.customerId, { name: e.target.value }); force(); }} />
                  <input className="input" value={q.address || ""} placeholder="Property address" onChange={e => { store.updateQuote(q.id, { address: e.target.value }); force(); }} />
                  <div className="row gap-6">
                    <input className="input" value={q.email || ""} placeholder="Email" onChange={e => { store.updateQuote(q.id, { email: e.target.value }); force(); }} />
                    <input className="input" value={q.phone || ""} placeholder="Phone" onChange={e => { store.updateQuote(q.id, { phone: e.target.value }); force(); }} />
                  </div>
                </div>
              : <><div style={{ fontWeight: 700, marginTop: 5 }}>{q.customer || "Customer"}</div>
                  <div className="muted" style={{ fontSize: 13 }}>{q.address}</div>
                  {q.email && <div className="muted" style={{ fontSize: 13 }}>{q.email}{q.phone ? " · " + q.phone : ""}</div>}</>}</div>
          <div style={{ textAlign: "right" }}><div className="kicker">Project total</div>
            <div className="display tnum" style={{ fontSize: 32, color: "var(--teal)", marginTop: 3 }}>{Q_money(quote.customerTotal)}</div>
            {paidTotal > 0 && !fullyPaid && <div style={{ marginTop: 10 }}>
              <div className="kicker" style={{ color: "var(--red)" }}>Amount due</div>
              <div className="display tnum" style={{ fontSize: 32, color: "var(--red)", marginTop: 3 }}>{Q_money(amountDue)}</div>
              <div className="muted tnum" style={{ fontSize: 12, marginTop: 2 }}>{Q_money0(paidTotal)} of {Q_money0(quote.customerTotal)} paid</div>
            </div>}
            {fullyPaid && <span className="chip green" style={{ marginTop: 10 }}><Icon name="check" size={11} />Paid in full</span>}
            {q.inputs && q.inputs.siteVisitRequired && <span className="chip amber" style={{ marginTop: 8, fontSize: 11 }}><Icon name="mappin" size={11} />Preliminary · final after site visit</span>}</div>
        </div>

        {/* the contractor's personal note — the first thing the customer reads
            after their name, here, on their link, in the email and on paper.
            Plain text with its line breaks; {first_name} / {company} fill in.
            Frozen with the contract once it's signed. */}
        {noteEdit != null && !detailsLocked ? (
          <div className="quote-cover-note editing no-print" data-cover-note-editor>
            <label className="field-label" htmlFor="q-cover-note">Note to {firstName || "the customer"}</label>
            <textarea id="q-cover-note" className="textarea" rows={4} maxLength={NOTE_MAX} autoFocus value={noteEdit}
              placeholder={"Hi {first_name}, thanks for having us out to look at your yard…"} onChange={e => setNoteEdit(e.target.value)} />
            <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>
              <code>{"{first_name}"}</code> and <code>{"{company}"}</code> fill in. Plain text — your line breaks are kept.
            </p>
            {noteHasFields && (
              <div className="quote-cover-preview"><span className="kicker" style={{ fontSize: 9.5 }}>They'll read</span>
                <p className="quote-cover-text">{renderCoverNote(noteEdit, { firstName, company: coName })}</p></div>
            )}
            <div className="row gap-8" style={{ marginTop: 10, flexWrap: "wrap" }}>
              <button className="btn btn-primary btn-sm" onClick={() => saveNote(noteEdit)}><Icon name="check" size={13} />Save note</button>
              {defaultNote && cleanNote(noteEdit) !== defaultNote && <button className="btn btn-ghost btn-sm" onClick={() => setNoteEdit(defaultNote)}>Use my default</button>}
              {!!noteShown && <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} onClick={() => saveNote("")}>Remove note</button>}
              <button className="btn btn-ghost btn-sm" onClick={() => setNoteEdit(null)}>Cancel</button>
            </div>
          </div>
        ) : noteShown ? (
          <div className="quote-cover-note" data-cover-note>
            <p className="quote-cover-text">{noteShown}</p>
            {coName && <div className="quote-cover-sign">— {coName}</div>}
            {!detailsLocked && <button className="qedit-btn no-print quote-cover-edit" onClick={() => setNoteEdit(typeof q.coverNote === "string" ? q.coverNote : noteShown)}><Icon name="edit" size={11} />Edit note</button>}
          </div>
        ) : !detailsLocked ? (
          <button className="btn btn-ghost btn-sm no-print quote-add-note" onClick={() => setNoteEdit(defaultNote || "")}
            title="A short personal message at the top of the quote, their link and the email"><Icon name="plus" size={12} />Add a personal note</button>
        ) : null}

        {/* change orders awaiting the customer's e-sign */}
        {(() => {
          const pend = (Array.isArray(q.changeOrders) ? q.changeOrders : []).filter((c: any) => c && c.status === "pending");
          if (!pend.length) return null;
          return (
            <div className="pe-callout no-print" style={{ marginBottom: 14, borderColor: "var(--amber-tint)", background: "var(--amber-tint)" }}>
              <div className="row gap-8" style={{ marginBottom: 8 }}>
                <Icon name="edit" size={14} style={{ color: "var(--amber)", marginTop: 2 }} />
                <div style={{ fontWeight: 700, fontSize: 13 }}>{pend.length} change order{pend.length > 1 ? "s" : ""} awaiting the customer's approval — the total stays at the signed contract until they e-sign on their hub.</div>
              </div>
              {pend.map((c: any) => (
                <div key={c.id} className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--line)" }}>
                  {c.photo && <a className="co-photo" href={c.photo} target="_blank" rel="noopener" style={{ backgroundImage: `url(${c.photo})` }} title="Open photo" />}
                  <span style={{ flex: "1 1 220px", fontSize: 12.5, minWidth: 0 }}>{c.label} <b className="tnum">({(Number(c.amount) || 0) >= 0 ? "+" : "−"}{Q_money(Math.abs(Number(c.amount) || 0))})</b></span>
                  <button className="btn btn-ghost btn-sm" onClick={openShareLink} title="Send them their hub link — the approval card with the signature pad is waiting on it"><Icon name="send" size={12} />Send approval link</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setCoSign({ coId: c.id, name: "", agree: false })} title="They're here — they type their name and sign for it on this device, and the total updates"><Icon name="check" size={12} />Approve in person</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} onClick={async () => { if (await confirmDialog({ title: "Withdraw this change order?", body: "It comes off the ledger entirely.", confirmLabel: "Withdraw", danger: true })) coSetStatus(c.id, "withdrawn"); }}><Icon name="x" size={12} />Withdraw</button>
                </div>
              ))}
            </div>
          );
        })()}

        {/* fence runs detail */}
        <div className="row between" data-tour="pricing" style={{ margin: "4px 0 10px", flexWrap: "wrap", gap: 8 }}>
          <div className="kicker" style={{ margin: 0 }}>Fence Runs</div>
          <div className="row gap-8 no-print">
            {!editEst && !committedQ && (
              <button className="btn btn-ghost btn-sm" onClick={() => setDiscModal({ label: (q.discount && q.discount.label) || "", mode: q.discount && Number(q.discount.amt) > 0 ? "amt" : "pct", value: q.discount ? String(Number(q.discount.pct) > 0 ? q.discount.pct : (q.discount.amt || "")) : "" })}
                title="A named discount — % or flat $ off, shown as its own line on the invoice and the customer hub">
                <Icon name="dollar" size={13} />{quote.discountAmt > 0 ? "Discount · −" + Q_money0(quote.discountAmt) : "Add discount"}</button>
            )}
            {!editEst && committedQ && <button className="btn btn-ghost btn-sm" onClick={() => setCoReview({ kind: "custom", label: "", amount: "" })}
              title="Document an unforeseen cost or credit on the signed contract — equipment rental, extra clearing, removed footage…">
              <Icon name="plus" size={13} />Change order</button>}
            {!editEst && <button className="btn btn-ghost btn-sm" onClick={isConcrete ? () => go("estimator", { quoteId: q.id }) : startEditEst}
              title={isConcrete ? (committedQ ? "This scope is signed — record extra work as a change order" : "Change what's being done, the areas and the rates") : multiFence ? "Multi-fence quotes are edited per fence in the Estimator" : committedQ ? "Re-measure the runs — recorded as a change order on the signed contract" : "Adjust field-verified lengths, gates & corners — pricing, materials and the site plan update"}>
              <Icon name="edit" size={13} />{isConcrete ? "Edit estimate" : multiFence ? "Edit in Estimator" : committedQ ? "Re-measure" : "Edit estimate"}</button>}
          </div>
        </div>
        {coReview && (() => {
          const cosPrev = Array.isArray(q.changeOrders) ? q.changeOrders : [];
          const baseSigned = quote.baseTotal;   // before change orders — prevSum adds them
          const prevSum = coAppliedSum(cosPrev);
          const amt = Math.round((+coReview.amount || 0) * 100) / 100;
          const revised = Math.max(0, baseSigned + prevSum + amt);
          const discounted = coReview.kind === "remeasure" && Math.abs((coReview.engineDelta || 0) - amt) > 0.005;
          return ReactDOM.createPortal(
            <div className="modal-scrim no-print" onClick={() => setCoReview(null)}>
              <div className="modal" style={{ width: "min(560px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
                <div className="row between" style={{ marginBottom: 4 }}>
                  <h3 className="display" style={{ fontSize: 18, margin: 0 }}>Change order</h3>
                  <span className="chip amber" style={{ fontSize: 11 }}>Quote is {q.status}</span>
                </div>
                <p className="muted" style={{ fontSize: 13, margin: "0 0 16px" }}>Documented on the signed contract — the original total stays on the document; this is itemized on top.</p>
                {coReview.kind === "remeasure" && (
                  <div className="pe-callout info" style={{ marginBottom: 14 }}>
                    <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
                      Re-measure repriced the job by <b style={{ color: "var(--fg)" }}>{(coReview.engineDelta || 0) >= 0 ? "+" : "−"}{Q_money(Math.abs(coReview.engineDelta || 0))}</b>.
                      Adjust the amount below to discount or no-charge it — materials, supplies &amp; the site plan still follow the new measurements either way.
                    </p>
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <label className="field-label">What changed</label>
                  <input className="input" autoFocus={coReview.kind === "custom"} placeholder="e.g. Skid steer rental — land clearing (not in original scope)" value={coReview.label}
                    onChange={(e: any) => setCoReview((r: any) => ({ ...r, label: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label className="field-label">Amount — negative for a credit</label>
                  <div className="row gap-8">
                    <span className="muted" style={{ fontSize: 14 }}>$</span>
                    <input className="input tnum" type="number" step="0.01" placeholder="850 or -300" value={coReview.amount} style={{ width: 150 }}
                      onChange={(e: any) => setCoReview((r: any) => ({ ...r, amount: e.target.value }))} />
                    {discounted && <span className="chip" style={{ fontSize: 11 }}>adjusted from {(coReview.engineDelta || 0) >= 0 ? "+" : "−"}{Q_money(Math.abs(coReview.engineDelta || 0))}</span>}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label className="field-label">Photo of the reason (optional)</label>
                  <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                    {coReview.photo && <button className="co-photo" style={{ backgroundImage: `url(${coReview.photo})` }} title="Remove photo" onClick={() => setCoReview((r: any) => ({ ...r, photo: null }))} />}
                    <button className="btn btn-outline btn-sm" type="button" onClick={() => {
                      const inp = document.createElement("input"); inp.type = "file"; inp.accept = "image/*"; (inp as any).capture = "environment";
                      inp.onchange = async () => { const f = inp.files && inp.files[0]; if (!f) return; try { const url = await downscaleToDataUrl(f, 900, 0.72); setCoReview((r: any) => ({ ...r, photo: url })); } catch (e) { notify("Couldn't read that photo."); } };
                      inp.click();
                    }}><Icon name="image" size={13} />{coReview.photo ? "Replace photo" : "Add photo"}</button>
                    <span className="faint" style={{ fontSize: 11.5 }}>Goes on the approval card the customer signs, and on the job timeline.</span>
                  </div>
                </div>
                <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
                  <div className="row between" style={{ fontSize: 12.5 }}><span className="muted">Original contract (as signed)</span><span className="tnum">{Q_money(baseSigned)}</span></div>
                  {prevSum !== 0 && <div className="row between" style={{ fontSize: 12.5 }}><span className="muted">Approved change orders</span><span className="tnum">{prevSum >= 0 ? "+" : "−"}{Q_money(Math.abs(prevSum))}</span></div>}
                  <div className="row between" style={{ fontSize: 12.5 }}><span className="muted">This change</span><span className="tnum">{amt >= 0 ? "+" : "−"}{Q_money(Math.abs(amt))}</span></div>
                  <div className="row between" style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}><span>Revised total — once approved</span><span className="tnum" style={{ color: "var(--teal)" }}>{Q_money(revised)}</span></div>
                </div>
                <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                  <button className="btn btn-primary btn-sm" onClick={() => recordChangeOrder("pending")} title="The customer gets an 'Approve this change' card with the same e-signature flow as the quote — the new total takes effect once they sign">
                    <Icon name="send" size={13} />Record &amp; send for approval</button>
                  <button className="btn btn-outline btn-sm" onClick={() => recordChangeOrder("approved")} title="They're right here — record it, then they type their name and sign for it on this device; the total updates once they do">
                    <Icon name="check" size={13} />Approve in person</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setCoReview(null)}>Cancel</button>
                </div>
                <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>
                  "Send for approval" keeps the price at the signed contract until the customer e-signs the change on their hub — that signature is your paper trail if it's ever disputed.
                  {q.qboInvoiceId ? " QuickBooks: re-send the invoice after approval to sync the new total." : ""}
                </p>
              </div>
            </div>,
            document.body
          );
        })()}
        {coSign && (() => {
          // in person: the customer signs for ONE change order on this device
          const cos = Array.isArray(q.changeOrders) ? q.changeOrders : [];
          const c = cos.find((x: any) => x && x.id === coSign.coId);
          if (!c) return null;
          const amt = round2(c.amount);
          const current = round2(quote.customerTotal);
          const after = round2(Math.max(0, quote.baseTotal + coAppliedSum(cos.map((x: any) => x.id === c.id ? { ...x, status: "approved" } : x))));
          const close = () => { if (!coSign.busy) setCoSign(null); };
          return ReactDOM.createPortal(
            <div className="modal-scrim no-print" onClick={close}>
              <div className="modal" style={{ width: "min(520px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
                <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>Approve this change in person</h3>
                <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Hand {(q.customer || "the customer").split(" ")[0]} the device. They type their own name and sign — it's recorded with the same audit trail as their link, and that you presented it.</p>
                <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                  <div className="row between" style={{ fontSize: 13, gap: 10 }}><span style={{ minWidth: 0 }}>{c.label}</span><b className="tnum">{amt >= 0 ? "+" : "−"}{Q_money(Math.abs(amt))}</b></div>
                  <div className="row between" style={{ fontSize: 12.5, marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--line)" }}><span className="muted">Current total</span><span className="tnum">{Q_money(current)}</span></div>
                  <div className="row between" style={{ fontSize: 14, fontWeight: 800 }}><span>New total after approval</span><span className="tnum" style={{ color: "var(--teal)" }}>{Q_money(after)}</span></div>
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  <div>
                    <label className="field-label">Customer's full legal name</label>
                    <input className="input" value={coSign.name || ""} autoComplete="off" placeholder="They type their own name"
                      onChange={(e: any) => setCoSign((s: any) => s && { ...s, name: e.target.value })} />
                  </div>
                  <label className="row gap-8" style={{ fontSize: 12, color: "var(--fg-3)", alignItems: "flex-start", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!coSign.agree} onChange={(e: any) => setCoSign((s: any) => s && { ...s, agree: e.target.checked })} style={{ marginTop: 2 }} />
                    <span>I have reviewed this change order and its effect on my project total, and my signature is a legally binding approval (E-SIGN Act / UETA).</span>
                  </label>
                  <SignaturePad signed={false} onSign={approveCoInPerson} />
                  {coSign.busy && <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Recording the approval…</p>}
                  {coSign.err && <p style={{ color: "var(--red)", fontSize: 12.5, margin: 0 }}>{coSign.err}</p>}
                </div>
                <div className="row gap-8" style={{ marginTop: 12 }}>
                  <button className="btn btn-ghost btn-sm" disabled={!!coSign.busy} onClick={close}>Not now — leave it pending</button>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}
        {editEst && (
          <div className="wo-schedule no-print" style={{ marginBottom: 16 }}>
            <div className="row between" style={{ marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
              <b style={{ fontSize: 13.5 }}>Edit estimate — field-verified measurements</b>
              <span className="muted" style={{ fontSize: 12 }}>Pricing, materials, work-order supplies &amp; the site plan re-flow on apply.</span>
            </div>
            {editEst.sides ? editEst.sides.map(s => (
              <div key={s.i} className="row gap-8" style={{ padding: "7px 0", borderBottom: "1px solid var(--line)", flexWrap: "wrap" }}>
                <button className={"cbox" + (s.selected ? " on" : "")} title={s.selected ? "Remove this run from the fence" : "Include this run"} onClick={() => setSideEdit(s.i, { selected: !s.selected })}>{s.selected && <Icon name="check" size={11} />}</button>
                <span style={{ fontWeight: 600, fontSize: 13, minWidth: 140, opacity: s.selected ? 1 : .45 }}>Side {s.i + 1} ({s.name}{s.suffix ? " — " + s.suffix : ""})</span>
                <div className="row gap-8">
                  <input className="input tnum" type="number" min="0" value={s.lf} disabled={!s.selected} style={{ width: 90 }} onChange={e => setSideEdit(s.i, { lf: e.target.value })} />
                  <span className="muted" style={{ fontSize: 12 }}>ft</span>
                </div>
                <select className="input" value={s.grade || "level"} disabled={!s.selected} style={{ width: 220, maxWidth: "100%", marginLeft: "auto" }} onChange={e => setSideEdit(s.i, { grade: e.target.value, gradeAuto: false })}>
                  {Object.entries(GRADE_LABELS).map(([v, lb]: any) => <option key={v} value={v}>{lb}</option>)}
                </select>
                {s.selected && (() => {
                  const prof = Array.isArray(s.profile) ? s.profile : [];
                  const mg = measuredGrade(prof, s.lf);
                  const open = !!gpEditOpen[s.i];
                  const measuredPricing = !!(settings && settings.quoting && settings.quoting.priceSlopeFromGrade);
                  const col = mg.tier === "steep" ? "var(--red)" : mg.tier === "moderate" ? "var(--amber)" : "var(--green)";
                  const bg = mg.tier === "steep" ? "var(--red-tint)" : mg.tier === "moderate" ? "var(--amber-tint)" : "var(--green-tint)";
                  // same field on the same side object the Estimator writes → they translate over
                  const setProf = (next: any) => setSideEdit(s.i, { profile: normProfile(next) });
                  return (
                    <div className="gp-box" style={{ flexBasis: "100%" }}>
                      <button className="gp-head" onClick={() => setGpEditOpen((o: any) => ({ ...o, [s.i]: !o[s.i] }))}>
                        <Icon name={open ? "chevdown" : "chevright"} size={12} />
                        <span>Grade points{prof.length ? ` · ${prof.length}` : ""}</span>
                        {prof.length >= 2 && <span className="gp-badge" style={{ color: col, background: bg }} title={measuredPricing ? "Measured grade — sets this run's slope surcharge" : "Display only — enable it in Settings → Quoting"}>{+mg.pct.toFixed(1)}% · {mg.label}</span>}
                        {prof.length >= 2 && measuredPricing && <span className="gp-priced">priced</span>}
                      </button>
                      {open && (
                        <div className="gp-body">
                          {prof.length === 0 && <p className="faint" style={{ fontSize: 11, margin: 0, lineHeight: 1.45 }}>Add measured drops/rises by distance — − = drop, + = rise (inches vs the previous point). These sync with the Estimator map and (when enabled in Settings → Quoting) set this run's slope surcharge.</p>}
                          {prof.map((pt: any, idx: number) => (
                            <GradePointRow key={idx + ":" + pt.t + ":" + pt.deltaIn} pt={pt} lf={s.lf}
                              onChange={(patch: any) => setProf(prof.map((p: any, k: number) => k === idx ? { ...p, ...patch } : p))}
                              onRemove={() => setProf(prof.filter((_: any, k: number) => k !== idx))} />
                          ))}
                          <GradePointAddRow lf={s.lf} onAdd={(t: any, deltaIn: any) => setProf([...prof, { t, deltaIn }])} />
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )) : (
              <div className="row gap-8" style={{ padding: "7px 0", flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>Total fence length</span>
                <input className="input tnum" type="number" min="0" value={editEst.linearFeet} style={{ width: 110 }} onChange={e => setEditEst(w => ({ ...w, linearFeet: e.target.value }))} />
                <span className="muted" style={{ fontSize: 12 }}>ft</span>
              </div>
            )}
            <div className="row gap-8" style={{ marginTop: 10, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Walk gates</span>
              <input className="input tnum" type="number" min="0" value={editEst.gates} style={{ width: 70 }} onChange={e => setEditEst(w => ({ ...w, gates: Math.max(0, (+e.target.value || 0) | 0) }))} />
              {!editEst.sides && <>
                <span style={{ fontWeight: 600, fontSize: 13, marginLeft: 10 }}>Corners</span>
                <input className="input tnum" type="number" min="0" value={editEst.corners} style={{ width: 70 }} onChange={e => setEditEst(w => ({ ...w, corners: Math.max(0, (+e.target.value || 0) | 0) }))} />
              </>}
              <span className="tnum" style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "var(--teal)" }}>{editLF} ft total</span>
            </div>
            <div className="row gap-8" style={{ marginTop: 12, flexWrap: "wrap" }}>
              <button className="btn btn-primary btn-sm" onClick={applyEditEst}><Icon name="check" size={13} />{committedQ ? "Apply as change order" : "Apply & reprice"}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setEditEst(null); setPriceEdits({}); }}>Cancel</button>
              {committedQ && <span className="chip amber" style={{ fontSize: 11 }}>Quote is {q.status} — recorded as a change order; the signed total stays on the document</span>}
            </div>
            <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>The drawn site plan keeps its shape ("not to scale") — the lengths labeled on it update to these field-verified numbers. Drawn gate markers stay where they are; the gate count here drives pricing &amp; materials. Need different geometry? Reopen the map in the Estimator.</p>
            {!committedQ && <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>Need to override a price? Every line in the <b>Pricing</b> breakdown below is editable while you're here — type a flat amount over any line (the fence, a gate, removal, the permit…). The subtotal, tax &amp; total re-foot to your numbers, and ↺ resets a line to the computed price. The customer's quote shows only the final figures.</p>}
          </div>
        )}
        {isConcrete ? <><ConcreteScopeTable blocks={quote.concrete && quote.concrete.blocks} showRates /><ConcretePlanBlock concrete={q.inputs && q.inputs.concrete} />{Array.isArray(q.jobTypes) && q.jobTypes.includes("coat") && <CoatingPreviewCard q={q} store={store} canEdit={!committedQ} />}</> : <table className="quote-table runs-table">
          <thead><tr><th>Run</th><th>Fence Type</th><th style={{ textAlign: "right" }}>Length</th><th style={{ textAlign: "right" }}>Grade</th></tr></thead>
          <tbody>
            {runGroups.map((g, gi) => (
              <React.Fragment key={gi}>
                {g.name && <tr className="run-group"><td colSpan={4}>{g.name} · {g.prod.name} · <span className="tnum">{Math.round(g.q.LF)} ft</span></td></tr>}
                {g.rows.map((r, k) => (
                  <tr key={gi + "-" + k}>
                    <td style={{ fontWeight: 600, paddingLeft: g.name ? 14 : 0 }}>{r.name}</td>
                    <td className="muted" style={{ fontSize: 13 }}>{r.type}</td>
                    <td className="tnum" style={{ textAlign: "right" }}>{Math.round(r.length)} ft</td>
                    <td className="tnum muted" style={{ textAlign: "right", fontSize: 12.5 }}>{GRADE_LABELS[r.grade] || "Level"}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={2} style={{ fontWeight: 700 }}>Total fence</td><td className="tnum" colSpan={2} style={{ textAlign: "right", fontWeight: 700, color: "var(--teal)" }}>{Math.round(quote.LF)} ft · {quote.panels} panels · {quote.posts} posts</td></tr>
          </tfoot>
        </table>}
        {!isConcrete && <RendersCard q={q} store={store} canEdit={!committedQ} go={go} />}

        {/* pricing summary */}
        <div className="row between" style={{ margin: "22px 0 10px", flexWrap: "wrap", gap: 8 }}>
          <div className="kicker" style={{ margin: 0 }}>Pricing</div>
          {!detailsLocked && (
            <button className="btn btn-ghost btn-sm no-print" data-describe-lines aria-pressed={!!descEdit} onClick={() => setDescEdit((v: any) => !v)}
              title="A one-line description under each line — what's included, the grade of material. Your price book's product descriptions fill in on their own.">
              <Icon name={descEdit ? "check" : "edit"} size={13} />{descEdit ? "Done describing" : "Describe lines"}</button>
          )}
        </div>
        <table className="quote-table">
          <thead><tr><th>Description</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
          <tbody>
            {lines.map((l: any, k) => {
              // per-line override editor: edit mode, real key, and only on a
              // pre-signature quote — once signed/paid the contract total is
              // frozen, so price changes go through the change-order flow, not
              // a silent line override (which couldn't move the frozen total).
              const editing = !!editEst && !!l.key && !committedQ;
              // input text: the in-progress buffer if the contractor is typing,
              // else the persisted override value, else empty (computed shows as placeholder)
              const buf = (l.key in priceEdits) ? priceEdits[l.key] : (l.overridden ? String(l.amount) : "");
              // the line's one-line description: editable before signing (in
              // "Describe lines" or Edit estimate), the signed words after
              const describing = (!!descEdit || !!editEst) && !!l.key && !detailsLocked;
              const own = q.lineDescs && typeof q.lineDescs === "object" && Object.prototype.hasOwnProperty.call(q.lineDescs, l.key) ? String(q.lineDescs[l.key]) : null;
              return (
                <tr key={l.key || k}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{l.label}
                      {editing && l.overridden && <span className="chip no-print" style={{ marginLeft: 8, fontSize: 10.5, verticalAlign: "middle" }} title={`Price overridden — was ${Q_money(l.computed)}`}>edited</span>}
                    </div>
                    {l.sub && <div className="muted" style={{ fontSize: 12.5 }}>{l.sub}</div>}
                    {describing && (
                      <div className="qline-desc-edit no-print">
                        <input className="input" value={own != null ? own : ""} maxLength={DESC_MAX} aria-label={"Description for " + l.label}
                          placeholder={l.defaultDesc ? l.defaultDesc : "One line: what's included, the grade of material…"}
                          onChange={e => setLineDesc(l.key, e.target.value === "" ? null : e.target.value)} />
                        {l.defaultDesc && own !== "" && <button className="btn btn-ghost btn-sm" onClick={() => setLineDesc(l.key, "")} title="Leave this line without a description on this quote">None</button>}
                        {l.defaultDesc && own != null && <button className="btn btn-ghost btn-sm" onClick={() => setLineDesc(l.key, null)} title="Use the description from your price book">↺ Price book</button>}
                      </div>
                    )}
                    {l.desc && <div className={"quote-line-desc" + (describing ? " print-only" : "")}>{l.desc}</div>}
                    {l.warranty && <div className="quote-line-desc quote-line-warranty"><b>Warranty:</b> {l.warranty}</div>}
                  </td>
                  <td className="tnum" style={{ textAlign: "right", fontWeight: 600 }}>
                    {editing ? (
                      <span className="no-print row gap-8" style={{ justifyContent: "flex-end", alignItems: "center" }}>
                        {l.overridden && <span className="muted" style={{ fontSize: 11, textDecoration: "line-through" }} title="Computed price">{Q_money(l.computed)}</span>}
                        <span className="muted" style={{ fontSize: 13 }}>$</span>
                        <input className="input tnum" type="number" min="0" step="0.01" value={buf}
                          placeholder={String(+(+l.computed).toFixed(2))} style={{ width: 110, textAlign: "right" }}
                          onChange={e => setLineOverride(l.key, e.target.value)} />
                        {l.overridden && <button className="btn btn-ghost btn-sm" title="Reset to computed price" onClick={() => clearLineOverride(l.key)} style={{ padding: "2px 7px" }}>↺</button>}
                      </span>
                    ) : Q_money(l.amount)}
                    {editing && <span className="print-only">{Q_money(l.amount)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {(quote.tax > 0 || quote.discountAmt > 0) && <tr><td className="muted">Subtotal</td>
              <td className="tnum" style={{ textAlign: "right" }}>{Q_money(quote.subtotal)}</td></tr>}
            {quote.discountAmt > 0 && <tr><td style={{ color: "var(--green)", fontWeight: 600 }}>{quote.discountLabel}</td>
              <td className="tnum" style={{ textAlign: "right", color: "var(--green)", fontWeight: 600 }}>−{Q_money(quote.discountAmt)}</td></tr>}
            {quote.tax > 0 && <tr><td className="muted">{quote.taxLabel || "Sales tax"} ({String(+(quote.taxRate * 100).toFixed(4))}%)</td>
              <td className="tnum" style={{ textAlign: "right" }}>{Q_money(quote.tax)}</td></tr>}
            {qChangeOrders.length > 0 && <>
              <tr><td className="muted" style={{ fontWeight: 600 }}>Original contract (as signed)</td>
                <td className="tnum" style={{ textAlign: "right", fontWeight: 600 }}>{Q_money(quote.baseTotal)}</td></tr>
              {qChangeOrders.map((c: any) => (
                <tr key={c.id}><td className="muted" style={{ fontSize: 12.5, paddingLeft: 14 }}>Change order{c.at ? " · " + new Date(c.at).toLocaleDateString() : ""} — {c.label}
                  {c.status === "pending" && <b style={{ color: "var(--amber)" }}> · awaiting customer approval — not in the total</b>}
                  {c.status === "approved" && c.approval && <span className="faint" style={{ fontSize: 11 }}> · approved by {c.approval.name}{(c.approval.method === "in_person" || c.approval.via === "in-person") ? " (in person)" : ""}{c.approval.at ? " " + new Date(c.approval.at).toLocaleDateString() : ""}</span>}
                  {c.photo && <a className="no-print" href={c.photo} target="_blank" rel="noopener" style={{ marginLeft: 6, color: "var(--teal)", fontSize: 11 }}><Icon name="image" size={11} /> photo</a>}</td>
                  <td className="tnum" style={{ textAlign: "right", fontSize: 12.5, opacity: c.status === "pending" ? 0.55 : 1 }}>{(Number(c.amount) || 0) >= 0 ? "+" : "−"}{Q_money(Math.abs(Number(c.amount) || 0))}</td></tr>
              ))}
            </>}
            <tr><td style={{ fontWeight: 800, fontSize: 16 }}>{qChangeOrders.length ? "Revised total" : "Total"}</td>
              <td className="tnum display" style={{ textAlign: "right", fontSize: 20, color: "var(--teal)" }}>{Q_money(quote.customerTotal)}</td></tr>
            {paidTotal > 0 && <tr><td className="muted">Payments received</td>
              <td className="tnum" style={{ textAlign: "right", color: "var(--green)", fontWeight: 600 }}>−{Q_money(paidTotal)}</td></tr>}
            {ledger.map((p: any, i: number) => (
              <tr key={"pl" + i}>
                <td className="muted" style={{ fontSize: 12, paddingLeft: 14 }}>{payMethodLabel(p)}{p.at ? " · " + new Date(p.at).toLocaleDateString() : ""}</td>
                <td className="tnum muted" style={{ textAlign: "right", fontSize: 12 }}>−{Q_money(p.amount)}</td>
              </tr>
            ))}
            {paidTotal > 0 && <tr><td style={{ fontWeight: 800, fontSize: 16, color: fullyPaid ? "var(--green)" : "var(--red)" }}>Amount due</td>
              <td className="tnum display" style={{ textAlign: "right", fontSize: 20, color: fullyPaid ? "var(--green)" : "var(--red)" }}>{Q_money(amountDue)}</td></tr>}
          </tfoot>
        </table>
        {(settings.cardFee && settings.cardFee.enabled && !fullyPaid) ? (
          <p className="muted" style={{ fontSize: 11.5, margin: "6px 0 0" }}>{(() => {
            // the base next to the total with the fee — the only card the fee
            // is ever charged on is a credit card (server/src/cardcapture.js)
            const pct = Math.max(1, Math.min(3, Number(settings.cardFee.pct) || 3));
            return <>A {pct}% fee applies to credit cards. Debit and prepaid cards are not charged the fee.{amountDue > 0 ? <> Amount due {Q_money(amountDue)} — {Q_money(amountDue * (1 + pct / 100))} by credit card.</> : null} Bank transfer (ACH) or check — no fee.</>;
          })()}</p>
        ) : null}

        {/* ---- payment terms: deposit due / balance, or a milestone schedule ----
            Pure presentation of the resolved deposit + optional progress bill;
            a quote with neither override nor schedule shows the classic
            "Deposit due / Balance due" two-up — same numbers as before. */}
        <QuoteOptions q={q} store={store} settings={settings} force={force} fullyPaid={fullyPaid} />

        {!fullyPaid && (
          <div className="pay-terms" style={{ margin: "20px 0 0" }}>
            <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <div className="kicker" style={{ margin: 0 }}>Payment terms</div>
              <button className="qedit-btn no-print" onClick={() => setEditDep(e => !e)}><Icon name={editDep ? "check" : "edit"} size={11} />{editDep ? "Done" : "Edit terms"}</button>
            </div>

            {sched ? (
              /* ---- progress / milestone schedule ---- */
              <div className="wo-schedule" style={{ padding: 0, overflow: "hidden" }}>
                <table className="quote-table" style={{ margin: 0 }}>
                  <thead><tr><th>Stage</th><th style={{ textAlign: "right" }}>Amount</th><th style={{ textAlign: "right" }}>Status</th></tr></thead>
                  <tbody>
                    {sched.stages.map((s: any, i: number) => (
                      <tr key={s.id || i}>
                        <td><div style={{ fontWeight: 600 }}>{s.label}</div><div className="muted" style={{ fontSize: 12 }}>{+(s.pct).toFixed(s.pct % 1 ? 1 : 0)}% of total</div></td>
                        <td className="tnum" style={{ textAlign: "right", fontWeight: 600 }}>{Q_money(s.amount)}</td>
                        <td style={{ textAlign: "right" }}>
                          {s.settled
                            ? <span className="chip green" style={{ fontSize: 11 }}><Icon name="check" size={10} />Paid</span>
                            : s.paid > 0.005
                              ? <span className="chip amber" style={{ fontSize: 11 }}>{Q_money0(s.paid)} of {Q_money0(s.amount)}</span>
                              : <>
                                  <span className="chip" style={{ fontSize: 11 }}>Due</span>
                                  {committedQ && <button className="btn btn-ghost btn-sm no-print" style={{ marginLeft: 6 }} title="Record a cash/check payment toward this stage"
                                    onClick={() => setRecordPay({ stageId: s.id, label: s.label, amount: s.due })}><Icon name="dollar" size={12} />Mark paid</button>}
                                </>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td style={{ fontWeight: 700 }}>Collected</td>
                      <td className="tnum" style={{ textAlign: "right", color: "var(--green)", fontWeight: 700 }}>{Q_money(paidTotal)}</td>
                      <td className="tnum muted" style={{ textAlign: "right", fontSize: 12 }}>{Q_money(amountDue)} remaining</td></tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              /* ---- classic single deposit + balance ---- */
              (() => {
                const depMet = paidTotal >= depInfo.amount - 0.005;
                const pctLb = depInfo.mode === "amount" ? "(flat)" : "(" + +depInfo.pct.toFixed(depInfo.pct % 1 ? 1 : 0) + "%)";
                return (
                  <div className="row gap-10" style={{ flexWrap: "wrap" }}>
                    <div className="deposit-box" style={{ flex: "1 1 180px" }}>
                      <div className="muted" style={{ fontSize: 12 }}>{depMet ? "Deposit" : paidTotal > 0 ? "Deposit remaining" : "Deposit due"} {pctLb}</div>
                      <div className="display tnum" style={{ fontSize: 22, color: depMet ? "var(--green)" : "var(--teal)" }}>{depMet ? "✓ Received" : Q_money(Math.max(0, depInfo.amount - paidTotal))}</div>
                      {depMet
                        ? <div className="faint" style={{ fontSize: 11 }}>{Q_money0(depInfo.amount)} deposit paid in full</div>
                        : paidTotal > 0 && <div className="faint" style={{ fontSize: 11 }}>{Q_money0(paidTotal)} of {Q_money0(depInfo.amount)} received</div>}
                    </div>
                    <div className="deposit-box" style={{ flex: "1 1 180px" }}>
                      <div className="muted" style={{ fontSize: 12 }}>Balance on completion</div>
                      {/* payments beyond the deposit eat into the completion balance */}
                      <div className="display tnum" style={{ fontSize: 22 }}>{Q_money(Math.max(0, Math.min(depInfo.balance, amountDue)))}</div>
                    </div>
                  </div>
                );
              })()
            )}

            {/* ---- contractor-only editor (never printed) ---- */}
            {editDep && (
              <div className="card pad no-print" style={{ background: "var(--surface-2)", marginTop: 12 }}>
                <div className="kicker" style={{ marginBottom: 10 }}>Deposit</div>
                <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 8 }}>
                  {[10, 25, 33, 50, 100].map(p => (
                    <button key={p} className={"filter-chip" + (depInfo.source === "quote" && depInfo.mode === "pct" && Math.round(depInfo.pct) === p ? " on" : "")}
                      onClick={() => setDepositPct(p)}>{p === 100 ? "Full" : p + "%"}</button>
                  ))}
                  <button className={"filter-chip" + (depInfo.source !== "quote" ? " on" : "")} onClick={clearDepositOverride} title="Use the workspace deposit policy from Settings">Default</button>
                </div>
                <div className="row gap-10" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ width: 130 }}><label className="field-label">Percent</label>
                    <div className="row gap-6"><input className="input tnum" type="number" min={0} max={100} placeholder={String(Math.round(depInfo.pct))} value={q.depositPct ?? ""} onChange={e => setDepositPct(e.target.value)} style={{ width: 90 }} /><span className="muted" style={{ fontSize: 13 }}>%</span></div>
                  </div>
                  <div style={{ width: 150 }}><label className="field-label">…or flat amount</label>
                    <div className="row gap-6"><span className="muted" style={{ fontSize: 13 }}>$</span><input className="input tnum" type="number" min={0} step="0.01" placeholder="e.g. 1500" value={q.depositAmt ?? ""} onChange={e => setDepositAmt(e.target.value)} /></div>
                  </div>
                  <span className="faint" style={{ fontSize: 11.5, flex: 1, minWidth: 150 }}>
                    {depInfo.source === "quote" ? "Custom for this quote." : depInfo.source === "policy" ? "From your workspace deposit policy." : "Default 50% — set a policy in Settings."}
                    {" "}Deposit due: <b style={{ color: "var(--fg-2)" }}>{Q_money(depInfo.amount)}</b>.
                  </span>
                </div>

                <div className="kicker" style={{ margin: "16px 0 8px" }}>Progress billing <span className="faint" style={{ fontWeight: 500, textTransform: "none" }}>· optional</span></div>
                {!sched ? (
                  <button className="btn btn-outline btn-sm" onClick={enableSchedule}><Icon name="plus" size={13} />Bill in stages (Deposit → On start → On completion)</button>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {(Array.isArray(q.paymentSchedule) ? q.paymentSchedule : []).map((s: any, i: number) => {
                      const isAmt = s.amt != null && s.amt !== "" && isFinite(+s.amt);
                      const amount = isAmt ? Math.max(0, +s.amt) : quote.customerTotal * ((Number(s.pct) || 0) / 100);
                      return (
                        <div key={s.id || i} className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
                          <input className="input" value={s.label || ""} placeholder={"Stage " + (i + 1)} onChange={e => updateStage(i, { label: e.target.value })} style={{ flex: "1 1 160px", minWidth: 120 }} />
                          {isAmt
                            ? <div className="row gap-6"><span className="muted" style={{ fontSize: 13 }}>$</span><input className="input tnum" type="number" min={0} step="0.01" value={s.amt} onChange={e => updateStage(i, { amt: e.target.value, pct: undefined })} style={{ width: 100 }} /></div>
                            : <div className="row gap-6"><input className="input tnum" type="number" min={0} max={100} value={s.pct ?? 0} onChange={e => updateStage(i, { pct: Math.max(0, Math.min(100, Number(e.target.value) || 0)), amt: undefined })} style={{ width: 80 }} /><span className="muted" style={{ fontSize: 13 }}>%</span></div>}
                          <span className="tnum muted" style={{ fontSize: 12, width: 88, textAlign: "right" }}>{Q_money(amount)}</span>
                          <button className="btn btn-ghost btn-sm" title="Remove stage" onClick={() => removeStage(i)}><Icon name="x" size={13} /></button>
                        </div>
                      );
                    })}
                    {(() => {
                      const pctSum = (Array.isArray(q.paymentSchedule) ? q.paymentSchedule : []).reduce((s: number, x: any) => s + (x.amt != null && x.amt !== "" ? 0 : (Number(x.pct) || 0)), 0);
                      const amtSum = (Array.isArray(q.paymentSchedule) ? q.paymentSchedule : []).reduce((s: number, x: any) => s + (x.amt != null && x.amt !== "" && isFinite(+x.amt) ? +x.amt : 0), 0);
                      const off = Math.abs((sched ? sched.scheduled : 0) - quote.customerTotal) > 0.5;
                      return (
                        <div className="row between" style={{ flexWrap: "wrap", gap: 8, marginTop: 2 }}>
                          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                            <button className="btn btn-ghost btn-sm" onClick={addStage}><Icon name="plus" size={13} />Add stage</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setSchedule(null)}><Icon name="x" size={13} />Remove schedule</button>
                          </div>
                          <span className="faint" style={{ fontSize: 11.5, color: off ? "var(--amber)" : undefined }}>
                            {amtSum > 0 ? `$ stages: ${Q_money0(amtSum)} · ` : ""}{pctSum}% by percent · last stage absorbs the rest ({Q_money(sched ? sched.scheduled : quote.customerTotal)} total)
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ---- finance callout — surface the monthly-payment option up front,
            right under the total (not buried in Sign & Pay). LIVE accounts only. */}
        {finLive && !fullyPaid && finMonthly > 0 && (
          <div className="finance-box no-print" style={{ marginTop: 14 }}>
            <div className="row between" style={{ flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              <div className="row gap-10" style={{ alignItems: "center", minWidth: 0 }}>
                <Icon name="clipboard" size={20} style={{ color: "var(--teal)", flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14.5 }}>Prefer to spread the cost?</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>From <b className="tnum" style={{ color: "var(--fg-2)" }}>{Q_money0(finMonthly)}/mo</b> · {fin.termMonths} mo · {fin.apr}% APR est. · {fin.partner} · $0 down</div>
                </div>
              </div>
              <button className="btn btn-outline btn-sm" style={{ flexShrink: 0 }} onClick={applyFinancing}><Icon name="arrow" size={14} />Check your rate</button>
            </div>
            <p className="faint" style={{ fontSize: 11, margin: "8px 0 0" }}>Soft credit check — won't affect their credit. Funded by {fin.partner}, subject to approval.</p>
          </div>
        )}

        {/* bill of materials — supplies only, no prices */}
        <div className="bom-block">
          <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div className="kicker">Bill of Materials</div>
            <span className="faint" style={{ fontSize: 11.5 }}>Supplies included — quantities only</span>
          </div>
          <table className="quote-table bom-table">
            <thead><tr><th>Item</th><th style={{ textAlign: "right" }}>Qty</th></tr></thead>
            <tbody>
              {bom.map((r, k) => (
                <tr key={k}><td style={{ fontWeight: 600 }}>{r.item}</td><td className="tnum" style={{ textAlign: "right", fontWeight: 700 }}>{r.qty.toLocaleString()}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="faint" style={{ fontSize: 11.5, marginTop: 10 }}>Material list for your reference — exact counts may be field-verified at install. Fasteners, caps &amp; consumables included.</p>
        </div>

        {quote.notes.length > 0 &&
          <div className="quote-notes">
            {quote.notes.map((n, k) => <p key={k} className="row gap-8" style={{ margin: 0, fontSize: 13 }}><Icon name="bolt" size={14} style={{ color: "var(--amber)", flexShrink: 0, marginTop: 2 }} />{n}</p>)}
          </div>}
        {q.inputs && q.inputs.quoteNotes &&
          <div className="quote-notes"><div className="kicker" style={{ marginBottom: 6 }}>Notes</div><p style={{ margin: 0, fontSize: 13.5, color: "var(--fg-2)", whiteSpace: "pre-wrap" }}>{q.inputs.quoteNotes}</p></div>}

        {/* sign & pay */}
        <div className="sign-pay no-print" data-tour="payments">
          <div className="row between" style={{ marginBottom: 14 }}>
            <h3 className="display" style={{ fontSize: 18, margin: 0 }}>{q.status === "lost" ? "Marked lost" : q.status === "paid" ? "Deposit & payments" : "Get it signed & paid"}</h3>
            <Icon name="lock" size={16} className="muted" />
          </div>
          {q.status === "lost"
            ? <div className="pe-callout" style={{ borderColor: "var(--red-tint)", background: "var(--red-tint)" }}>
                <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
                  <Icon name="x" size={15} style={{ color: "var(--red)" }} />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>Lost — {lostReasonLabel(q.lostReason)}{q.lostAt ? " · " + new Date(q.lostAt).toLocaleDateString() : ""}</div>
                    {q.lostNote && <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{q.lostNote}</div>}
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Out of the pipeline, still on the books — it feeds the "Why we lose" report.</div>
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={reopenLost} title="Put it back in the pipeline as pending"><Icon name="arrow" size={13} />Reopen</button>
                </div>
              </div>
            : q.status === "paid"
            ? <div style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }}>
                <div className="paid-banner"><Icon name="check" size={20} /><div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>{fullyPaid ? "Paid in full" : "Paid to date"} — {Q_money0(paidTotal)}</div>
                  <div className="muted" style={{ fontSize: 13 }}>{fullyPaid
                    ? "All payments received. Nothing left to collect on this job."
                    : <>Remaining due: <b style={{ color: "var(--red)" }}>{Q_money(amountDue)}</b> — the customer can pay it from their hub, or record cash / check here.</>}</div></div>
                  <div className="row gap-8 pay-actions" style={{ flexShrink: 0, flexWrap: "wrap" }}>
                    {!fullyPaid && paidTotal > 0 && amountDue > 0.5 && (
                      <button className="btn btn-ghost btn-sm" title={`Email the customer their ${Q_money0(amountDue)} balance with the pay link (logged — the chase cadence tracks it)`}
                        onClick={() => { sendBalanceReminder(store, q); force(); }}>
                        <Icon name="send" size={14} />Send reminder{(q.reminders || []).length ? ` (${q.reminders.length})` : ""}
                      </button>
                    )}
                    {!fullyPaid && <button className="btn btn-outline btn-sm" onClick={() => setRecordPay(true)}><Icon name="dollar" size={14} />Record payment</button>}
                    <button className="btn btn-primary btn-sm" onClick={() => go("workorders")}><Icon name="calendar" size={14} />Schedule the work</button>
                  </div></div>
                {ledger.length > 0 && <div style={{ display: "grid", gap: 6, gridTemplateColumns: "minmax(0, 1fr)" }}>
                  {ledger.map((p: any, i: number) => (
                    <div key={p.id || i} className="row between" role="button" onClick={() => setPayView(p)}
                      style={{ padding: "8px 12px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, fontSize: 13, gap: 10, cursor: "pointer" }}>
                      <span className="row gap-8" style={{ minWidth: 0, flex: "1 1 auto", alignItems: "center" }}>
                        {p.photo && <img src={p.photo} alt="" style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 7, border: "1px solid var(--line-2)", flexShrink: 0 }} />}
                        {/* the whole label is one truncating line, so even a long method
                            ("Other payment · QuickBooks · by …") can never shove the amount off-screen */}
                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <span style={{ fontWeight: 600 }}>{payMethodLabel(p)}</span>
                          {p.by && <span className="muted" style={{ fontSize: 11.5 }}> · by {p.by}</span>}
                          {p.note && <span className="muted" style={{ fontSize: 12 }}> · {p.note}</span>}
                        </span>
                      </span>
                      <span className="row gap-10" style={{ flexShrink: 0, alignItems: "center" }}>
                        {p.at && <span className="muted" style={{ fontSize: 12 }}>{new Date(p.at).toLocaleDateString()}</span>}
                        <b className="tnum" style={{ color: "var(--green)" }}>{Q_money(p.amount)}</b>
                        <Icon name="chevright" size={13} className="muted" />
                      </span>
                    </div>
                  ))}
                </div>}
              </div>
            : <div style={{ display: "grid", gap: 14 }}>
                {/* PRIMARY — the customer e-signs & pays on their own secure link */}
                <div className="deposit-box" style={{ display: "grid", gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>Send it to {q.customer || "your customer"} to sign &amp; pay</div>
                    <p className="muted" style={{ fontSize: 12.5, margin: "3px 0 0" }}>They review the quote, e-sign, and pay the deposit from their own phone — the <b style={{ color: "var(--fg-2)" }}>binding signature</b> and the Stripe receipt happen on their secure link.</p>
                  </div>
                  <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                    <button className="btn btn-primary" onClick={openShareLink}><Icon name="send" size={15} />Send sign &amp; pay link</button>
                    <button className="btn btn-outline btn-sm" onClick={() => setRecordPay(true)}><Icon name="dollar" size={14} />Record cash / check</button>
                  </div>
                </div>

                {/* SECONDARY — close in person on the contractor's own device */}
                {!inPerson
                  ? <button className="btn btn-ghost btn-sm" style={{ justifySelf: "start" }} onClick={() => setInPerson(true)}>
                      <Icon name="edit" size={13} />Customer here in person? Sign &amp; collect now
                    </button>
                  : <div className="card pad" style={{ background: "var(--surface-2)" }}>
                      <div className="row between" style={{ marginBottom: 12 }}>
                        <span className="kicker">In person — they sign on this screen</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => setInPerson(false)}><Icon name="x" size={13} />Hide</button>
                      </div>
                      <div className="sign-grid">
                {(() => {
                  // the same three things their link asks for — their own typed
                  // name, the terms + consent box, a drawn signature — so an
                  // in-person signature is the same record, not a lesser one.
                  // "Signed" means a signature exists: a quote the old pad only
                  // flipped to status "signed" never got one, and can be signed here.
                  const sig = q.signature && q.signature.at ? q.signature : null;
                  // an expired quote can't be signed here either (the server refuses it)
                  if (!sig && ex.expired) return (
                    <div className="pe-callout warn" style={{ margin: 0, alignSelf: "start" }}>
                      <p style={{ margin: "0 0 8px", fontSize: 12.5, color: "var(--fg-2)" }}><b style={{ color: "var(--amber)" }}>Expired {fmtDate(ex.validUntil, ex.today)}.</b> Extend it and they can sign right here.</p>
                      <button className="btn btn-primary btn-sm" onClick={() => extendQuote()}><Icon name="calendar" size={13} />Extend {EXTEND_DAYS} days</button>
                    </div>
                  );
                  return (
                    <div style={{ display: "grid", gap: 10, alignContent: "start" }}>
                      {!sig && <>
                        <div>
                          <label className="field-label">Customer's full legal name</label>
                          <input className="input" value={ipName} onChange={(e: any) => setIpName(e.target.value)} placeholder="They type their own name" autoComplete="off" />
                        </div>
                        <details style={{ fontSize: 12, color: "var(--fg-3)", border: "1px solid var(--line)", borderRadius: 10, padding: "10px 12px" }}>
                          <summary style={{ cursor: "pointer", fontWeight: 700, color: "var(--fg-2)" }}>Terms &amp; Conditions ({isConcrete ? CONTRACT_TERMS_CONCRETE_VERSION : CONTRACT_TERMS_VERSION})</summary>
                          <ol style={{ margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 4 }}>
                            {(isConcrete ? CONTRACT_TERMS_CONCRETE : CONTRACT_TERMS).map((x: string, i: number) => <li key={i}>{x}</li>)}
                          </ol>
                        </details>
                        <label className="row gap-8" style={{ fontSize: 12, color: "var(--fg-3)", alignItems: "flex-start", cursor: "pointer" }}>
                          <input type="checkbox" checked={!!ipAgree} onChange={(e: any) => setIpAgree(e.target.checked)} style={{ marginTop: 2 }} />
                          <span>I have reviewed this estimate, including its total of <b style={{ color: "var(--fg-2)" }}>{Q_money(quote.customerTotal)}</b>, and <b style={{ color: "var(--fg-2)" }}>I agree to the Terms &amp; Conditions above</b>. I consent to do business electronically, and I understand my signature below is a legally binding acceptance of this estimate (E-SIGN Act / UETA).</span>
                        </label>
                      </>}
                      <div>
                        <label className="field-label">Customer signature</label>
                        <SignaturePad signed={!!sig} onSign={signInPerson} />
                      </div>
                      {sig && <p className="muted" style={{ fontSize: 12, margin: 0 }}>Signed by <b>{sig.name}</b>{sig.at ? " · " + new Date(sig.at).toLocaleString() : ""}{sig.method === "in_person" ? " · in person" : ""}</p>}
                      {ipBusy && <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Recording the signature…</p>}
                      {ipErr && <p style={{ color: "var(--red)", fontSize: 12.5, margin: 0 }}>{ipErr}</p>}
                    </div>
                  );
                })()}
                <div>
                  <div className="row gap-8" style={{ marginBottom: 12 }}>
                    <button className={"paymode-tab" + (payMode === "deposit" ? " on" : "")} onClick={() => setPayMode("deposit")}><Icon name="dollar" size={14} />Pay deposit</button>
                    {finLive && <button className={"paymode-tab" + (payMode === "finance" ? " on" : "")} onClick={() => setPayMode("finance")}><Icon name="clipboard" size={14} />Finance monthly</button>}
                  </div>
                  {payMode === "deposit" ? (
                    <>
                      <label className="field-label">Deposit</label>
                      <div className="row gap-8" style={{ marginBottom: 10, flexWrap: "wrap" }}>
                        {(() => {
                          // offer the quote's configured deposit first, then the
                          // familiar shortcuts (de-duplicated) and Pay in full
                          const cfg = quote.customerTotal > 0 ? +(depInfo.amount / quote.customerTotal).toFixed(4) : 0.5;
                          const opts: any[] = [];
                          const push = (v: number, lb: string) => { if (v > 0 && v <= 1 && !opts.some(o => Math.abs(o[0] - v) < 0.005)) opts.push([v, lb]); };
                          push(cfg, "Deposit · " + Q_money0(quote.customerTotal * cfg));
                          push(0.25, "25%"); push(0.5, "50%"); push(1, "Pay in full");
                          return opts.map(([v, lb]) =>
                            <button key={lb} className={"filter-chip" + (Math.abs(depFrac - v) < 0.005 ? " on" : "")} onClick={() => setDeposit(v)}>{lb}</button>);
                        })()}
                      </div>
                      <div className="deposit-box">
                        <div className="row between"><span className="muted" style={{ fontSize: 13 }}>Due now</span><span className="display tnum" style={{ fontSize: 24, color: "var(--teal)" }}>{Q_money(dep)}</span></div>
                        <div className="row between" style={{ marginTop: 4 }}><span className="muted" style={{ fontSize: 12 }}>Balance on completion</span><span className="tnum muted" style={{ fontSize: 13 }}>{Q_money(quote.customerTotal - dep)}</span></div>
                      </div>
                      <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} disabled={q.status !== "signed"} onClick={markPaid}>
                        <Icon name="dollar" size={16} />{q.status === "signed" ? `Pay ${Q_money0(dep)} deposit` : "Sign first to pay"}
                      </button>
                      <p className="faint" style={{ fontSize: 11.5, textAlign: "center", marginTop: 8 }}>Secured by Stripe · cards &amp; ACH{finLive ? ` · ${fin.partner} financing` : ""}</p>
                      <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 4 }} onClick={() => setRecordPay(true)}>
                        <Icon name="edit" size={13} />Paid by cash or check? Record it manually
                      </button>
                    </>
                  ) : (
                    <>
                      <label className="field-label">Monthly financing · {fin.partner}</label>
                      <div className="finance-box">
                        <div className="row between" style={{ alignItems: "flex-end" }}>
                          <span className="muted" style={{ fontSize: 13 }}>As low as</span>
                          <span className="display tnum" style={{ fontSize: 28, color: "var(--teal)" }}>{Q_money0(finMonthly)}<span style={{ fontSize: 14, color: "var(--fg-3)" }}>/mo</span></span>
                        </div>
                        <div className="row between" style={{ marginTop: 6, fontSize: 12 }}>
                          <span className="muted">{fin.termMonths} mo · {fin.apr}% APR est.</span>
                          <span className="muted">$0 down · no prepay penalty</span>
                        </div>
                      </div>
                      <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} onClick={applyFinancing}>
                        <Icon name="arrow" size={16} />Check your rate — won't affect credit
                      </button>
                      <p className="faint" style={{ fontSize: 11.5, textAlign: "center", marginTop: 8 }}>Soft credit check · funded by {fin.partner} · subject to approval</p>
                    </>
                  )}
                </div>
                      </div>
                    </div>}
                <button className="btn btn-ghost btn-sm" style={{ justifySelf: "start", color: "var(--fg-3)" }} onClick={() => setLostModal({ reason: "", note: "" })}
                  title="Take it out of the open pipeline and log why — the reasons roll up in Reports">
                  <Icon name="x" size={12} />Didn't win it? Mark lost
                </button>
              </div>}
        </div>
        {/* every stamped moment on this job — estimate, sends, signature, payments,
            change orders, work order, crew, photos, review. Screen only. */}
        <div className="no-print"><JobTimeline q={q} wo={(store.workOrders || []).find((w: any) => w.quoteId === q.id) || null} store={store} title="Job timeline" limit={12} /></div>

        {lostModal && ReactDOM.createPortal(
          <div className="modal-scrim no-print" onClick={() => setLostModal(null)}>
            <div className="modal" style={{ width: "min(480px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
              <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>Mark this estimate lost</h3>
              <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>One honest reason per lost deal is how you find out whether you're losing on price or on speed.</p>
              <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 12 }}>
                {LOST_REASONS.map(r => (
                  <button key={r.id} className={"filter-chip" + (lostModal.reason === r.id ? " on" : "")} onClick={() => setLostModal((m: any) => ({ ...m, reason: r.id }))}>{r.label}</button>
                ))}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="field-label">Note — optional</label>
                <input className="input" placeholder={lostModal.reason === "competitor" ? "Who got it? At what price?" : "Anything worth remembering next time"} value={lostModal.note || ""}
                  onChange={(e: any) => setLostModal((m: any) => ({ ...m, note: e.target.value }))} />
              </div>
              <div className="row gap-8">
                <button className="btn btn-primary btn-sm" onClick={markLost}><Icon name="check" size={13} />Mark lost</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setLostModal(null)}>Cancel</button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {discModal && ReactDOM.createPortal(
          <div className="modal-scrim no-print" onClick={() => setDiscModal(null)}>
            <div className="modal" style={{ width: "min(460px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
              <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>Discount</h3>
              <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Named, pre-tax, and shown as its own line on the invoice and the customer's hub — so it reads like a promotion, not a price cut.</p>
              <div style={{ marginBottom: 10 }}>
                <label className="field-label">Name it</label>
                <input className="input" autoFocus placeholder="Spring special · Military discount · Referral credit" value={discModal.label}
                  onChange={(e: any) => setDiscModal((m: any) => ({ ...m, label: e.target.value }))} />
              </div>
              <div className="row gap-8" style={{ marginBottom: 10 }}>
                <button className={"filter-chip" + (discModal.mode === "pct" ? " on" : "")} onClick={() => setDiscModal((m: any) => ({ ...m, mode: "pct" }))}>% of subtotal</button>
                <button className={"filter-chip" + (discModal.mode === "amt" ? " on" : "")} onClick={() => setDiscModal((m: any) => ({ ...m, mode: "amt" }))}>Flat $</button>
              </div>
              <div className="row gap-8" style={{ alignItems: "center", marginBottom: 6 }}>
                {discModal.mode === "amt" && <span className="muted" style={{ fontSize: 14 }}>$</span>}
                <input className="input tnum" type="number" min="0" step={discModal.mode === "pct" ? 0.5 : 1} style={{ width: 130 }} placeholder={discModal.mode === "pct" ? "5" : "250"}
                  value={discModal.value} onChange={(e: any) => setDiscModal((m: any) => ({ ...m, value: e.target.value }))} />
                {discModal.mode === "pct" && <span className="muted" style={{ fontSize: 14 }}>%</span>}
              </div>
              {(() => {
                const v = Math.max(0, +discModal.value || 0);
                if (!v) return null;
                const next = discModal.mode === "pct" ? { label: discModal.label, pct: Math.min(100, v), amt: null } : { label: discModal.label, pct: null, amt: v };
                try {
                  const v2 = computeQuoteView({ ...q, discount: next }, settings);
                  return <p className="muted" style={{ fontSize: 12.5, margin: "0 0 4px" }}>New total: <b className="tnum" style={{ color: "var(--teal)" }}>{Q_money(v2.quote.customerTotal)}</b>{v2.quote.discountAmt > 0 ? <span style={{ color: "var(--green)" }}> (−{Q_money(v2.quote.discountAmt)})</span> : null}</p>;
                } catch (e) { return null; }
              })()}
              <div className="row gap-8" style={{ marginTop: 12, flexWrap: "wrap" }}>
                <button className="btn btn-primary btn-sm" disabled={!(+discModal.value > 0)} onClick={() => {
                  const v = Math.max(0, +discModal.value || 0);
                  applyDiscount(discModal.mode === "pct" ? { label: discModal.label, pct: Math.min(100, v), amt: null } : { label: discModal.label, pct: null, amt: v });
                }}><Icon name="check" size={13} />Apply discount</button>
                {q.discount && <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} onClick={() => applyDiscount(null)}><Icon name="trash" size={13} />Remove discount</button>}
                <button className="btn btn-ghost btn-sm" onClick={() => setDiscModal(null)}>Cancel</button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* printed invoice acceptance line (hidden on screen) + the QR back to
            the e-sign/pay portal — every paper doc carries its return path */}
        <div className="print-only accept-line">
          <div className="al-col"><div className="al-rule"></div><div className="al-lbl">Customer signature</div></div>
          <div className="al-col"><div className="al-rule"></div><div className="al-lbl">Date</div></div>
          {(() => {
            const cust = (store.customers || []).find((x: any) => x.id === q.customerId);
            const t = cust && cust.hubToken;
            if (!t) return null;
            const QR: any = (window as any).QrCode, UTM: any = (window as any).utmUrl;
            if (!QR || !UTM) return null;
            return <QR url={UTM(location.origin + "/hub/" + t, "quote-print", "quote")} size={74} label="E-SIGN & PAY ONLINE" sub="scan to skip the paperwork" />;
          })()}
        </div>
        {/* the legal entity behind the trading name — what the signed agreement is with (plan §5.7) */}
        {brand.legalName && <div className="print-only muted" style={{ fontSize: 11, marginTop: 6 }}>Agreement with {brand.legalForm}{brand.license ? ` · License ${brand.license}` : ""}</div>}

        {/* site plan — addendum at the very bottom (fence quotes) */}
        {!isConcrete && (fenceQuotes
          ? <div className="siteplan-block addendum">
              <div className="kicker" style={{ marginBottom: 8 }}>Addendum — Site Plans</div>
              <div style={{ display: "grid", gap: 14 }}>
                {fenceQuotes.map((f, i) => (
                  <div key={i}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 5 }}>{f.name} <span className="faint" style={{ fontWeight: 500 }}>· {f.prod.name} · {Math.round(f.q.LF)} ft</span></div>
                    <SitePlan inputs={f.snap} />
                  </div>
                ))}
              </div>
            </div>
          : (q.inputs && (q.inputs.parcel || (q.inputs.mVerts && q.inputs.mVerts.length > 1))) && (
            <div className="siteplan-block addendum">
              <div className="row between" style={{ marginBottom: 8 }}>
                <div className="kicker">Addendum — Site Plan</div>
                <span className="faint" style={{ fontSize: 11.5 }}>Not to scale · lengths field-verified</span>
              </div>
              <SitePlan inputs={q.inputs} />
            </div>
          ))}

        {/* Free plan: the printed / PDF copy carries "Powered by Fence Works" —
            the service's answer from the plan sync (app.tsx), not settings.plan */}
        {poweredByShown(window.VFP_POWERED_BY, !!(window.VFP_DEMO && window.VFP_DEMO.active), store.settings && store.settings.plan) && (
          <div className="print-only" style={{ marginTop: 18 }}><PoweredBy medium="pdf" /></div>
        )}
      </div>

      {/* fence construction 3D — THIS quote's product, assembled step by step (fence quotes) */}
      {!isConcrete && <div className="card pad no-print" style={{ marginTop: 18 }}>
        <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <span className="kicker">Fence Construction 3D</span>
          <span className="faint" style={{ fontSize: 11.5 }}>{product.name} · interactive</span>
        </div>
        <Fence3DLauncher spec={fenceSpecFrom(product, q.productName)} layout={q.inputs && q.inputs.parcel} quote={q} bom={bom} />
      </div>}

      {/* internal breakdown — cost, margin and the operator's own numbers.
          Sales and crew never see it (roles.ts "money.cost"). */}
      {canCost && !costHidden && <div className="card no-print" style={{ marginTop: 18, overflow: "hidden" }}>
        <button className="cost-toggle" style={{ width: "100%", padding: 18 }} onClick={() => setShowInternal(s => !s)}>
          <span className="row gap-8 kicker"><Icon name="lock" size={13} />Internal cost breakdown — operator only</span>
          <Icon name={showInternal ? "chevdown" : "chevright"} size={15} />
        </button>
        {showInternal &&
          <div style={{ padding: "0 18px 18px" }}>
            <table className="cost-table">
              <tbody>
                {quote.mat.map((m, k) => <tr key={k}><td>{m.label}</td><td className="muted mono" style={{ fontSize: 12 }}>{m.sub}</td><td className="tnum" style={{ textAlign: "right" }}>{Q_money(m.total)}</td></tr>)}
                {/* every internal money line carries its $/linear-ft — the number
                    an owner quotes from on the phone once they know their range */}
                <tr className="sub"><td colSpan={2}>Material total</td><td className="tnum" style={{ textAlign: "right" }}>{Q_money(quote.materialTotal)}{quote.LF > 0 && <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}> · {Q_money(quote.materialTotal / quote.LF)}/ft</span>}</td></tr>
                <tr><td>Labor estimate</td><td className="muted mono" style={{ fontSize: 12 }}>{quote.laborHours.toFixed(1)} crew-hrs × {Q_money(quote.crewRate)}/hr · {quote.crew.name}</td><td className="tnum" style={{ textAlign: "right" }}>{Q_money(quote.laborCost)}{quote.LF > 0 && <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}> · {Q_money(quote.laborCost / quote.LF)}/ft</span>}</td></tr>
                {/* a concrete quote: the rig and the clock, from the rate cards' recipes (lib/engine.concrete.ts) */}
                {isConcrete && Number((quote as any).equipmentCost) > 0 && <tr><td>Equipment</td><td className="muted mono" style={{ fontSize: 12 }}>{((((quote as any).concrete || {}).equipment) || []).map((e: any) => e.label).join(" · ")}</td><td className="tnum" style={{ textAlign: "right" }}>{Q_money((quote as any).equipmentCost)}</td></tr>}
                {isConcrete && (quote as any).concrete && (quote as any).concrete.timeText && <tr><td>Time on site</td><td className="muted mono" colSpan={2} style={{ fontSize: 12 }}>{(quote as any).concrete.timeText}</td></tr>}
                {(() => {
                  // the crew's clock, once the job has hours on it: actual labor next to the estimate, and the adjusted cost + margin below
                  const wo = (store.workOrders || []).find((w: any) => w.quoteId === q.id);
                  const logs = wo ? woTimeLogs(wo) : [];
                  if (!wo || !logs.length) return null;
                  const crew = (settings.crews || []).find((c: any) => c.id === wo.crewId);
                  const rate = crewRate(crew) || quote.crewRate || 0;
                  const hrs = actualHours(wo);
                  const cost = hrs * rate;
                  const dH = hrs - quote.laborHours, dC = cost - quote.laborCost;
                  const over = dH > 0.001;
                  const notes = logs.map((e: any) => e.note).filter(Boolean);
                  return (
                    <tr className={"actual" + (over ? " over" : "")} data-tour="labor-actual">
                      <td>Actual labor <span className="chip" style={{ fontSize: 10, marginLeft: 6, color: over ? "var(--amber)" : "var(--green)", borderColor: "var(--line-2)" }}>{over ? "+" : ""}{dH.toFixed(1)} hr vs estimate</span></td>
                      <td className="muted mono" style={{ fontSize: 12 }}>{hrs.toFixed(1)} crew-hrs × {Q_money(rate)}/hr · from the crew's clock{notes.length ? <div className="faint" style={{ fontSize: 11, fontFamily: "inherit", whiteSpace: "normal", marginTop: 2 }}>{notes.join(" · ")}</div> : null}</td>
                      <td className="tnum" style={{ textAlign: "right", color: over ? "var(--amber)" : "var(--green)" }}>{Q_money(cost)}<span className="muted" style={{ fontWeight: 400, fontSize: 11 }}> · {dC >= 0 ? "+" : "−"}{Q_money(Math.abs(dC))}</span></td>
                    </tr>
                  );
                })()}
                <tr><td>Pass-through (permit / disposal / travel)</td><td></td><td className="tnum" style={{ textAlign: "right" }}>{Q_money(quote.passThrough)}</td></tr>
                <tr className="sub"><td colSpan={2}>Operator cost</td><td className="tnum" style={{ textAlign: "right" }}>{Q_money(quote.operatorCost)}{quote.LF > 0 && <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}> · {Q_money(quote.operatorCost / quote.LF)}/ft</span>}</td></tr>
                <tr><td colSpan={2}>Customer price ({isConcrete ? "from the rate cards — rate × area or cost × markup, minimums applied" : `${quote.markup}× ${quote.markupBase === "material" ? "material" : "material + labor"} + add-ons`})</td><td className="tnum" style={{ textAlign: "right" }}>{Q_money(quote.customerTotal)}{quote.LF > 0 && <b style={{ fontSize: 11.5, color: "var(--teal)" }}> · {Q_money(quote.customerTotal / quote.LF)}/ft</b>}</td></tr>
                {/* after any discount / typed price — the same figure as the margin line up top */}
                <tr className="profit"><td colSpan={2}>Gross profit · {qm.pct != null ? qm.pct.toFixed(0) : "0"}% margin{quote.discountAmt > 0 ? <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}> · after the {Q_money0(quote.discountAmt)} discount</span> : null}</td><td className="tnum display" style={{ textAlign: "right", color: qm.profit < 0 ? "var(--red)" : "var(--green)" }}>{Q_money(qm.profit)}{quote.LF > 0 && <span className="tnum" style={{ fontWeight: 400, fontSize: 11, color: "var(--green)" }}> · {Q_money(qm.profit / quote.LF)}/ft</span>}</td></tr>
                {qComm.amount > 0 && <tr><td>Commission</td><td className="muted mono" style={{ fontSize: 12 }}>{qComm.rep} · {qComm.pct}% of the total</td><td className="tnum" style={{ textAlign: "right" }}>−{Q_money(qComm.amount)}</td></tr>}
                {(() => {
                  const wo = (store.workOrders || []).find((w: any) => w.quoteId === q.id);
                  const logs = wo ? woTimeLogs(wo) : [];
                  if (!wo || !logs.length) return null;
                  const crew = (settings.crews || []).find((c: any) => c.id === wo.crewId);
                  const rate = crewRate(crew) || quote.crewRate || 0;
                  const laborActual = actualHours(wo) * rate;
                  // same revenue base as the quoted row above (change orders are itemized on the ledger, not here)
                  const revenue = qm.revenue;
                  const opActual = quote.operatorCost - quote.laborCost + laborActual;
                  const gpActual = revenue - opActual;
                  const mActual = revenue > 0 ? (gpActual / revenue) * 100 : 0;
                  const d = gpActual - qm.profit;
                  return (<>
                    <tr className="sub"><td colSpan={2}>Operator cost · actual (crew hours as clocked)</td><td className="tnum" style={{ textAlign: "right" }}>{Q_money(opActual)}</td></tr>
                    <tr className="profit actual" data-tour="margin-actual"><td colSpan={2}>Gross profit · actual · {mActual.toFixed(0)}% margin <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>({d >= 0 ? "+" : "−"}{Q_money(Math.abs(d))} vs quoted · this is what Reports counts)</span></td><td className="tnum display" style={{ textAlign: "right", color: d < -0.5 ? "var(--amber)" : "var(--green)" }}>{Q_money(gpActual)}</td></tr>
                  </>);
                })()}
              </tbody>
            </table>
          </div>}
      </div>}
      {/* a signed concrete quote: the job as it ran — the lift log's foam, the crew's clock, a by-the-pound price settled by the log — against the estimate (§13 item 28) */}
      {isConcrete && committedQ && canCost && !costHidden && (() => {
        const wo = (store.workOrders || []).find((w: any) => w.quoteId === q.id && w.kind !== "warranty");
        if (!wo) return null;
        const crew = (settings.crews || []).find((c: any) => c.id === wo.crewId);
        return <JobCostActualCard quote={quote} wo={wo} revenue={qm.revenue} actualCrewRate={crewRate(crew) || quote.crewRate || 0} />;
      })()}

      {/* sign & pay link — proper modal (was a browser alert) */}
      {share && ReactDOM.createPortal(
        <div className="hub-preview-overlay" data-dialog onClick={() => { setShare(null); setSendState(null); }}>
          <div className="hub-quote-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="hub-preview-bar">
              <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="lock" size={14} className="muted" />Sign &amp; pay link</span>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShare(null); setSendState(null); }}><Icon name="x" size={14} /></button>
            </div>
            <div className="hub-quote-body" style={{ display: "grid", gap: 12 }}>
              {/* EST-06: what is about to go out, before it goes. The sender
                  could see the link but not the recipient, the amount, the
                  deposit being asked for, or which of their notes the customer
                  would read. */}
              {(() => {
                const dep = depInfo && depInfo.amount > 0 ? depInfo : null;
                const internal = [q.internalNotes && "internal notes", (q.inputs && q.inputs.costNotes) && "cost notes"].filter(Boolean);
                return (
                  <div className="send-summary">
                    <div className="send-row"><span>To</span><span>{q.customer || <i className="muted">no name</i>}{q.email ? ` · ${q.email}` : ""}{q.phone ? ` · ${q.phone}` : ""}</span></div>
                    <div className="send-row"><span>They see</span><span className="tnum"><b>{Q_money(quote.customerTotal)}</b>{quote.taxRate > 0 ? ` incl. ${Q_money(quote.tax)} tax` : ""}</span></div>
                    <div className="send-row"><span>Asked up front</span><span className="tnum">{dep ? <b>{Q_money(dep.amount)}</b> : <i className="muted">no deposit</i>}</span></div>
                    <div className="send-row"><span>Good until</span><span>{ex.state === "none"
                      ? <i className="muted">no expiry date</i>
                      : ex.expired
                        ? <><b style={{ color: "var(--amber)" }}>Expired {fmtDate(ex.validUntil, ex.today)}</b> <button className="btn btn-ghost btn-sm" style={{ padding: "1px 8px" }} onClick={() => extendQuote()}>Extend {EXTEND_DAYS} days</button></>
                        : <>{fmtDate(ex.validUntil, ex.today)} <span className="muted">· {expiryPhrase(ex)}</span></>}</span></div>
                    {noteShown && <div className="send-row"><span>Your note</span><span className="send-note">{noteShown.length > 120 ? noteShown.slice(0, 118) + "…" : noteShown}</span></div>}
                    <div className="send-row"><span>Delivery</span><span>{q.email ? "Email + link" : q.phone ? "Text + link" : "Link only — copy it yourself"}</span></div>
                    {internal.length > 0 && <div className="send-row kept"><span>Kept back</span><span>Your {internal.join(" and ")} and every cost figure stay on your side.</span></div>}
                    <button className="btn btn-ghost btn-sm send-preview-btn" onClick={() => window.open(share.url, "_blank", "noopener")}>
                      <Icon name="search" size={13} />Preview exactly what they'll see
                    </button>
                  </div>
                );
              })()}
              <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                {q.customer || "Your customer"} can review the full quote, <b style={{ color: "var(--fg-2)" }}>e-sign</b>, and <b style={{ color: "var(--fg-2)" }}>pay the deposit</b> from any device with this private link{share.copied ? " (already copied to your clipboard)" : ""}:
              </p>
              <div className="link-row">
                <input className="input mono" readOnly value={share.url} style={{ fontSize: 12 }} onFocus={e => (e.target as HTMLInputElement).select()} />
                <button className="link-copy" title="Copy the link" aria-label="Copy the link"
                  onClick={(e) => { if (navigator.clipboard) navigator.clipboard.writeText(share.url).catch(() => {}); const b = e.currentTarget; b.classList.add("ok"); setTimeout(() => b.classList.remove("ok"), 1400); }}>
                  <Icon name="clipboard" size={15} />
                </button>
              </div>
              {share.preview && (
                <p style={{ fontSize: 12.5, margin: 0, color: "var(--amber)" }}>
                  ⚠ This quote has no customer name or email, so there's no private hub to link to — this placeholder only works on your own screen. Add the customer's name or email to the estimate and reopen this link.
                </p>
              )}
              {share.live === "checking" && <p className="muted" style={{ fontSize: 12, margin: 0 }}><span className="auth-spinner" style={{ display: "inline-block", width: 11, height: 11, verticalAlign: -1, marginRight: 6 }} />Confirming the link is live on the server…</p>}
              {share.live === "fixing" && <p className="muted" style={{ fontSize: 12, margin: 0 }}><span className="auth-spinner" style={{ display: "inline-block", width: 11, height: 11, verticalAlign: -1, marginRight: 6 }} />Writing the customer and link to the cloud…</p>}
              {share.live === "live" && <p style={{ fontSize: 12, margin: 0, color: "var(--green)", fontWeight: 600 }}>✓ Link verified live — safe to send.{share.reissued ? " This is a new link — any older one you sent no longer works." : ""}</p>}
              {share.live === "dead" && (
                <div className="pe-callout warn" style={{ padding: "10px 14px" }}>
                  <p style={{ fontSize: 12.5, margin: "0 0 8px", color: "var(--fg-2)" }}>
                    <b style={{ color: "var(--amber)" }}>This link isn't live</b> — the server has no customer with this link, so it opens as "Link not found" and <b>signing &amp; paying would fail</b>. {share.error ? <span>Last attempt: {share.error}</span> : <span><b>Fix link</b> writes this customer and the link straight to the cloud, no sync wait.</span>}
                  </p>
                  <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                    <button className="btn btn-primary btn-sm" onClick={() => repairShareLink(false)}><Icon name="check" size={13} />Fix link</button>
                    <button className="btn btn-outline btn-sm" onClick={() => repairShareLink(true)}><Icon name="undo" size={13} />Regenerate link</button>
                  </div>
                </div>
              )}
              <div className="row gap-8 share-actions" style={{ flexWrap: "wrap" }}>
                {!share.preview && share.live !== "dead" && share.live !== "fixing" && (
                  <button className="btn btn-ghost btn-sm" title="Issue a fresh link — the old one stops working; the new one is written to the cloud and checked live"
                    onClick={async () => { if (await confirmDialog({ title: "Regenerate this link?", body: "Any link you already sent for this customer will stop working. The new one is copied and checked live on the server.", confirmLabel: "Regenerate" })) repairShareLink(true); }}>
                    <Icon name="undo" size={13} />Regenerate link
                  </button>
                )}
                {q.phone && <a className="btn btn-ghost btn-sm" href={"sms:" + q.phone + "?&body=" + encodeURIComponent("Your " + thing + " estimate is ready — review, sign & pay your deposit here: " + share.url)}><Icon name="phone" size={13} />Text it</a>}
                {q.email && <a className="btn btn-ghost btn-sm" href={"mailto:" + q.email + "?subject=" + encodeURIComponent("Your " + thing + " estimate") + "&body=" + encodeURIComponent("Hi " + (q.customer || "") + ",\n\nYour estimate is ready — review, e-sign and pay the deposit here:\n" + share.url + "\n\nThanks!")}><Icon name="send" size={13} />Email app</a>}
                {q.email && window.VFP_DB && window.VFP_DB.apiBase && window.VFP_DB.apiBase() && (
                  <button className="btn btn-primary btn-sm" disabled={sendState === "sending" || sendState === "sent"} onClick={() => {
                    setSendState("sending");
                    (window.VFP_DB as any).sendQuoteEmail(q.id)
                      .then(() => { setSendState("sent"); store.logQuoteSend(q.id); })
                      .catch((e) => setSendState(String((e && e.message) || "Send failed")));
                  }}>
                    <Icon name="send" size={13} />{sendState === "sending" ? "Sending…" : sendState === "sent" ? "Sent ✓" : "Send from Fence Works"}
                  </button>
                )}
              </div>
              {typeof sendState === "string" && sendState !== "sending" && sendState !== "sent" && (
                <p style={{ color: "var(--red)", fontSize: 12, margin: 0 }}>{sendState}</p>
              )}
              {window.VFP_PLAN && !window.VFP_PLAN.allows(store, "payments") && (
                <p className="faint" style={{ fontSize: 12, margin: 0 }}>⚡ E-signature, online deposit collection and in-app sending need the <b>Starter</b> plan — <a href="#" onClick={(e) => { e.preventDefault(); setShare(null); go("account"); }}>upgrade</a>. The link still works for review &amp; e-sign.</p>
              )}
              {window.VFP_PLAN && window.VFP_PLAN.allows(store, "payments") && !(store.settings.integrations && store.settings.integrations.stripe && store.settings.integrations.stripe.connected) && (
                <p className="faint" style={{ fontSize: 12, margin: 0 }}>💳 <a href="#" onClick={(e) => { e.preventDefault(); setShare(null); go("integrations"); }}>Connect Stripe</a> so the deposit can be paid online — until then the link is review &amp; e-sign only.</p>
              )}
            </div>
          </div>
        </div>, document.body)}

      {/* share 3D — public /build3d/<token> viewer link (mirrors the sign & pay modal) */}
      {share3d && ReactDOM.createPortal(
        <div className="hub-preview-overlay" data-dialog onClick={() => setShare3d(null)}>
          <div className="hub-quote-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="hub-preview-bar">
              <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="box" size={14} className="muted" />Share their fence in 3D</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShare3d(null)}><Icon name="x" size={14} /></button>
            </div>
            <div className="hub-quote-body" style={{ display: "grid", gap: 12 }}>
              <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                {q.customer || "Your customer"} can <b style={{ color: "var(--fg-2)" }}>watch their real fence go together in 3D</b> — their layout, their style — no sign-in needed{share3d.copied ? " (already copied to your clipboard)" : ""}:
              </p>
              <div className="row gap-8" style={{ alignItems: "center" }}>
                <input className="input mono" readOnly value={share3d.url} style={{ fontSize: 12 }} onFocus={e => (e.target as HTMLInputElement).select()} />
                <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(share3d.url).catch(() => {}); }}>Copy</button>
              </div>
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                {q.phone && <a className="btn btn-ghost btn-sm" href={"sms:" + q.phone + "?&body=" + encodeURIComponent("Want to see your new fence before we build it? Here it is in 3D: " + share3d.url)}><Icon name="phone" size={13} />Text it</a>}
                {q.email && <a className="btn btn-ghost btn-sm" href={"mailto:" + q.email + "?subject=" + encodeURIComponent("Your new fence — in 3D") + "&body=" + encodeURIComponent("Hi " + (q.customer || "") + ",\n\nWant to see your new fence before we build it? Here it is in 3D — spin it around and watch it go together piece by piece:\n" + share3d.url + "\n\nThanks!")}><Icon name="send" size={13} />Email app</a>}
              </div>
            </div>
          </div>
        </div>, document.body)}

      {/* printable crew build sheet — specs, runs+grade, BOM & build order */}
      {crewSheet && <CrewBuildSheet quote={q} store={store} settings={settings} onClose={() => setCrewSheet(false)} />}
      {planSheet && <FencePlanSheet quote={q} store={store} settings={settings} onClose={() => setPlanSheet(false)} />}

      {/* record a manual payment (cash / check / outside card) — optionally
          seeded with a milestone stage's amount when "Mark paid" is tapped */}
      {recordPay && <RecordPaymentModal due={amountDue} total={quote.customerTotal}
        seedAmount={recordPay && recordPay.amount} seedLabel={recordPay && recordPay.label}
        onClose={() => setRecordPay(false)} onSave={recordPayment} />}

      {/* one payment's full detail (click a ledger row) */}
      {payView && <PaymentDetailModal p={payView} onClose={() => setPayView(null)} onDelete={deletePayment} onPhoto={(src: string) => setPhotoView(src)} />}

      {/* payment photo lightbox (check / receipt shots) */}
      {photoView && ReactDOM.createPortal(
        <div className="hub-preview-overlay" data-dialog style={{ cursor: "zoom-out" }} onClick={() => setPhotoView("")}>
          <img src={photoView} alt="" style={{ maxWidth: "92vw", maxHeight: "88vh", borderRadius: 12 }} />
        </div>, document.body)}
    </div>
  );
}

Object.assign(window, { Quote });

export { SitePlan, SignaturePad, Quote, Q_money, computeQuoteView, quoteMarginOf, marginFloorOf, marginBelowFloor, MARGIN_FLOOR_DEFAULT, MarginFloorSetting, QuoteDefaultsSetting, quoteTiers, recommendedTierId, customerDocOf, signedDocOf, docBaseTotal, hubQuoteView, GRADE_LABELS, quotePayments, quoteDeposit, quoteSchedule, payMethodLabel, financingLive, fenceSpecFrom, Fence3DLauncher, quoteBom, customerHubUrl, HubLinkButton, HubRegenButton, pushHubLink, sendBalanceReminder, LOST_REASONS, lostReasonLabel };
